import React, { useMemo, useRef, useState, useCallback, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  BusyIndicator,
  Card,
  DatePicker,
  Dialog,
  FlexBox,
  FlexBoxDirection,
  FlexBoxJustifyContent,
  Icon,
  Input,
  Label,
  MessageStrip,
  Option,
  Select,
  Text,
  Title
} from '@ui5/webcomponents-react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import axiosInstance from '../utils/axiosInstance';
import useAutoTranslate from '../hooks/useAutoTranslate';
import BlockingLoadingDialog from './BlockingLoadingDialog';
import {
  uploadSalesOrdersWholesaleFile,
  extractWholesaleImportCreatedCount,
  extractWholesaleImportSuccessDetail,
  pollWholesaleImportJob
} from '../services/salesOrdersWholesaleImport';
import { fetchAllSaleChannels } from '../services/saleChannels';
import { fetchAllBrandings } from '../services/brandings';
import { fetchAllShippingMethods } from '../services/shippingMethods';
import {
  fetchSapB1BusinessPartnerByFederalTaxId,
  normalizeFederalTaxIdForQuery,
  resolveBulkBrandDiscount,
  resolvePartnerPriceListNum,
  isBulkPeruWholesaleContext,
  isBulkPeruDockersSapItemsFetchReady,
  isDockersBrandName,
  isPeruFederalTaxIdForWholesale
} from '../services/sapB1BusinessPartners';
import {
  fetchSalesOrdersPage,
  fetchSalesOrdersSearch,
  SALES_ORDER_STATUS_UI_TO_API,
  SALES_ORDER_CHANNEL_UI_TO_API,
  getSalesOrderListStatusKind,
  getSalesOrderSapDocNumForDisplay,
  canCancelSalesOrderStatus,
  cancelSalesOrdersByIds,
  updateSalesOrderModifiedBy,
  fetchSalesOrderById,
  fetchSalesOrderByOrderNumber,
  isSalesOrderUuidKey,
  isSalesOrderStatusPending,
  isSalesOrderStatusSelectableInList,
  pickSalesOrderStatusFromApiPayload,
  formatSalesOrderListDestination
} from '../services/salesOrdersList';
import {
  parseSpreadsheetFileFull,
  buildValidatedRowsFromGrid,
  countByValidation,
  groupBulkPreviewRowsByClient,
  pickBestGridForBulkPreview,
  analyzeGridForBulkPreview,
  getBulkFormatLabelKey,
  getBulkFormatCurrency,
  formatBulkMoneyAmount,
  enrichBulkPreviewRowsWithSapItems,
  parseEocPriceFile,
  buildPriceMapFromEocGrids,
  validateEocDiscountConsistency,
  BULK_FORMAT_DEFAULT_ID,
  IX_COMERCIO_PROFILE_ID,
  isIxComercioProfileId,
  IX_COMERCIO_PERU_PROFILE_ID,
  MULTIMARCA_PROFILE_ID,
  isMultimarcaProfileId,
  FALABELLA_CHILE_PROFILE_ID,
  RIPLEY_PERU_PROFILE_ID,
  OECHSLE_PERU_PROFILE_ID,
  usesCustomerCatalogSubstitute,
  applyCustomerCatalogResolution,
  extractMultimarcaRutClienteFromGrid,
  showsPeruCentralWarehousePreview,
  getBulkGroupingColumnLabel,
  getBulkLineDestinationLabel
} from '../utils/salesOrderBulkPreview';
import { fetchSapB1PreviewItemsMap, normalizeSapItemCodeKey } from '../services/sapB1Items';
import { bulkPreviewRowHasStockShortage } from '../utils/bulkPreviewExportEligibility';
import {
  buildCatalogItemCodeMap,
  buildCatalogResolution,
  fetchSapB1AlternateCatalogInWaves
} from '../services/sapB1AlternateCatalog';
import { sendSalesOrdersToSapAndWait } from '../services/sapB1SalesOrdersSend';
import {
  applyBrandDiscountFallbackToSapProcessRows,
  buildSapProcessPreviewRowsFromOrderRaw,
  groupSapProcessPreviewRowsByOrder,
  pickOrderCustomerRutForSap
} from '../utils/salesOrderSapProcessPreview';
import {
  buildSapProcessPreviewMarkedXlsxBuffer,
  buildSapProcessPreviewProblemRowsCsvBlob,
  buildSapProcessPreviewXlsxWithoutErrorRowsBuffer,
  filterSapProcessPreviewProblemRows
} from '../utils/sapProcessPreviewExport';
import { patchBulkGridLongIdentifierColumns } from '../utils/bulkExcelLongIdentifiers';
import { getLoggedInUserId, getUserWhsCode, getUserWhsName, getWholesaleCountryFromSession } from '../utils/userSession';
import { getStatusStyle } from '../utils/validator';
import { formatSalesOrderDisplayDateEsCl } from '../utils/salesOrderListDateDisplay';

/** Cantidad de **grupos** (sucursales destino) por página en el modal “Vista previa” de carga mayorista. */
const BULK_PREVIEW_GROUPS_PAGE_SIZE = 10;

/** Cantidad de **órdenes** por página en el modal «Procesar a SAP» (vista previa validada). */
const SAP_PROCESS_PREVIEW_GROUPS_PAGE_SIZE = 10;

function patchBulkGridsLongIdentifierRows(grids) {
  if (!Array.isArray(grids)) return;
  for (const g of grids) {
    if (g.rowsRaw?.length) {
      const { headerIndex } = analyzeGridForBulkPreview(g);
      g.rows = patchBulkGridLongIdentifierColumns(g.rows, g.rowsRaw, headerIndex);
      delete g.rowsRaw;
    }
  }
}

function parsePickerDateToLocalDate(value) {
  if (!value || !String(value).trim()) return null;
  const s = String(value).trim();
  const parts = s.split(/[/.-]/).map((p) => p.trim());
  if (parts.length >= 3) {
    const p0 = parseInt(parts[0], 10);
    const p1 = parseInt(parts[1], 10);
    const p2 = parseInt(parts[2], 10);
    if (String(parts[0]).length === 4) {
      return new Date(p0, p1 - 1, p2);
    }
    return new Date(p2, p1 - 1, p0);
  }
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : new Date(t);
}

/** Rango ISO para `startDate` / `endDate` en búsqueda (inicio del primer día, fin del último). */
function buildSearchDateRangeIso(createdAt, createdAtEnd) {
  const d0 = parsePickerDateToLocalDate(createdAt);
  const d1 = parsePickerDateToLocalDate(createdAtEnd);
  const startDay = d0 || d1;
  if (!startDay) return { startDate: '', endDate: '' };
  const start = new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate());
  const endBase = d1 || d0;
  const end = new Date(endBase.getFullYear(), endBase.getMonth(), endBase.getDate(), 23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString()
  };
}

/** Días inclusivos por preset (referencia: “hoy” local al pulsar Buscar). */
const SALES_LIST_DATE_PRESET_DAY_COUNT = {
  last7: 7,
  last15: 15,
  last1m: 30,
  last3m: 90,
  last6m: 180
};

/**
 * Rango ISO para presets rápidos (misma semántica local que `buildSearchDateRangeIso`).
 * @param {keyof typeof SALES_LIST_DATE_PRESET_DAY_COUNT} preset
 * @param {Date} [now]
 */
function buildPresetDateRangeIso(preset, now = new Date()) {
  const n = SALES_LIST_DATE_PRESET_DAY_COUNT[preset];
  if (!n) return { startDate: '', endDate: '' };
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const startDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  startDay.setDate(startDay.getDate() - (n - 1));
  const start = new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate());
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString()
  };
}

/** Fecha corta es-CL para columnas del listado OV; vacío o inválido → — */
function formatSalesOrderListDate(value) {
  return formatSalesOrderDisplayDateEsCl(value, '—');
}

/** Líneas o suma de unidades en listado OV; sin dato → — */
function formatSalesOrderListCount(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  return Number.isInteger(value) && Math.abs(value - Math.round(value)) < 1e-9
    ? String(Math.round(value))
    : value.toLocaleString('es-CL', { maximumFractionDigits: 2 });
}

/** Soles (Perú): ISO suele ser PEN en APIs; en columnas se muestra el símbolo S/. */
function isSalesOrderPeruSolCurrency(currencyCode) {
  const raw = String(currencyCode ?? '').trim();
  if (!raw) return false;
  const upper = raw.toUpperCase();
  /** `SOL` = código que guardan las OV peruanas creadas por carga mayorista. */
  return upper === 'PEN' || upper === 'SOL' || raw === 'S/' || upper === 'S/';
}

/**
 * Total en listado OV según moneda de la fila (CLP vs soles Perú por marketplace en el mapper).
 */
function formatSalesOrderListMoney(value, currencyCode = 'CLP') {
  if (value == null || value === '') return '—';
  let num;
  if (typeof value === 'number' && Number.isFinite(value)) {
    num = value;
  } else {
    const s = String(value).trim();
    if (!s) return '—';
    const n = parseFloat(s.replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(n)) return s;
    num = n;
  }
  if (isSalesOrderPeruSolCurrency(currencyCode)) {
    const rounded = Math.round(num * 100) / 100;
    try {
      return new Intl.NumberFormat('es-PE', {
        style: 'currency',
        currency: 'PEN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(rounded);
    } catch {
      return `S/ ${rounded.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }
  return `$${Math.round(num).toLocaleString('es-CL')}`;
}

/** Texto plano de la columna Estado (misma lógica que `statusPill` en la tabla). */
function getSalesOrderStatusExportLabel(row, autoT) {
  const kind = getSalesOrderListStatusKind(row);
  if (kind === 'canceled') return autoT('Cancelada');
  if (kind === 'shipped') return autoT('Despachada');
  if (kind === 'partially_shipped') return autoT('Despacho Parcial');
  if (kind === 'closed') return autoT('Cerrado');
  if (kind === 'returned') return autoT('Devuelta');
  if (kind === 'sent_sap') {
    const doc = getSalesOrderSapDocNumForDisplay(row);
    return autoT('Enviada a SAP: DocNum {{doc}}', { doc });
  }
  return autoT('creada');
}

/** DocNum SAP (`doc_number_reference` en API); guion si vacío o null. */
function formatSalesOrderListDocNum(row) {
  const v = row?.docNumberReference ?? row?.doc_number_reference;
  if (v == null || String(v).trim() === '') return '-';
  return String(v).trim();
}

const EMPTY_APPLIED_SEARCH = {
  orderNumber: '',
  status: '',
  channel: '',
  startDate: '',
  endDate: ''
};

/** Tamaños de página permitidos en el listado de OV (GET `size`). */
const SALES_LIST_PAGE_SIZES = [15, 30, 50, 100];

/**
 * Vista **Órdenes de Venta** (embebida en `BuyOrderList` cuando `orderView === 'sales'`).
 *
 * **Datos:** `../services/salesOrdersList.js` — `fetchSalesOrdersPage` (listado paginado `GET sales-orders/headers`) y
 * `fetchSalesOrdersSearch` (tras «Buscar» con filtros). Las filas pasan por `mapSalesOrderApiToRow`.
 *
 * **Detalle:** clic en fila → `/sales-order-detail/:key` — si `row.id` es UUID (`GET sales-orders/:id`);
 * si no, número de orden (`GET sales-orders/order-number/:orderNumber`).
 *
 * **Estado visible (columna):** según `status` del API: `CANCELLED` → Cancelada; `SHIPPED` → Despachada; `PARTIALLY_SHIPPED` → Despacho Parcial;
 * `CLOSED` → Cerrado; `RETURNED` → Devuelta; `ACCEPTED` → «Enviada a SAP» con DocNum (`getSalesOrderSapDocNumForDisplay`); `PENDING` → creada. Filtro Estado envía códigos en mayúsculas vía `SALES_ORDER_STATUS_UI_TO_API`.
 *
 * **Selección masiva:** checkboxes solo `PENDING` y `ACCEPTED`; cancelación masiva solo esas mismas (`canCancelSalesOrderStatus`).
 * **SAP lote:** envío solo con `status` PENDING (el botón queda deshabilitado si hay `ACCEPTED` en la selección visible);
 * vista previa con precio/stock; luego `POST integration/sapb1/orders` con `orderIds` validados al abrir el modal.
 *
 * **Carga mayorista:** `../services/salesOrdersWholesaleImport.js` + utilidades `../utils/salesOrderBulkPreview.js`
 * (parse Excel, vista previa, enriquecimiento SAP opcional). Endpoint típico: `sales-orders-import/file-wholesales`.
 */
const SalesOrderList = () => {
  const { autoT } = useAutoTranslate();
  /** `autoT` no está memoizado en el hook; si va en deps de un efecto provoca re-ejecución en cada render y bucles de API. */
  const autoTRef = useRef(autoT);
  autoTRef.current = autoT;
  const navigate = useNavigate();

  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  const bulkPriceFileInputRef = useRef(null);
  const bulkSapPreviewFetchAbortRef = useRef(null);
  /** Evita repetir GET SAP al cerrar y reabrir la misma vista previa (mismo archivo + hoja + PC13). */
  const bulkSapItemsFetchCacheRef = useRef({ key: '', map: {} });
  /** Última consulta de socio SAP exitosa (RUT+marca+archivo); evita repetir GET si el efecto se dispara de nuevo sin cambiar datos. */
  const bulkSapPartnerFetchSuccessKeyRef = useRef('');
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isBulkPreviewOpen, setIsBulkPreviewOpen] = useState(false);
  const [filters, setFilters] = useState({
    orderNumber: '',
    status: '',
    /** Query API `channel` (ej. mayorista); no confundir con marketplace de línea. */
    channel: '',
    /** `all` | `last7` | `last15` | `last1m` | `last3m` | `last6m` | `custom` */
    datePreset: 'all',
    createdAt: '',
    createdAtEnd: ''
  });

  const [orders, setOrders] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(null);
  const [totalElements, setTotalElements] = useState(null);
  const [lastPageFromApi, setLastPageFromApi] = useState(null);
  /** `true` mientras haya criterios de búsqueda aplicados con el botón Buscar (GET search en servidor). */
  const [searchModeActive, setSearchModeActive] = useState(false);
  /** Snapshot de filtros ya enviados al API (evita refetch en cada tecla; solo cambia al Buscar/Limpiar). */
  const [appliedSearch, setAppliedSearch] = useState(() => ({ ...EMPTY_APPLIED_SEARCH }));
  /** Nombres de `GET sale-channels` para el filtro «Canal» (mismo literal que espera `sales-orders/search`). */
  const [filterSaleChannels, setFilterSaleChannels] = useState([]);
  const [filterSaleChannelsLoading, setFilterSaleChannelsLoading] = useState(false);
  /** IDs de OV seleccionadas (`String(row.id)` o `orderNumber`) para procesar a SAP. */
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const sapProcessPreviewAbortRef = useRef(null);
  /** IDs validados PENDING al abrir la vista previa SAP; el POST final usa esta lista (no `selectedOrderIds` crudo). */
  const sapProcessEligibleOrderIdsRef = useRef(null);
  const [isSapChileProcessPreviewOpen, setIsSapChileProcessPreviewOpen] = useState(false);
  const [sapChileProcessSelectionLoading, setSapChileProcessSelectionLoading] = useState(false);
  const [sapChilePreviewSapItemsLoading, setSapChilePreviewSapItemsLoading] = useState(false);
  const [sapChilePreviewSapItemsProgress, setSapChilePreviewSapItemsProgress] = useState({ done: 0, total: 0 });
  const [sapChilePreviewSapItemsError, setSapChilePreviewSapItemsError] = useState('');
  const [sapChilePreviewFlatRows, setSapChilePreviewFlatRows] = useState([]);
  const [sapChilePreviewGrouped, setSapChilePreviewGrouped] = useState([]);
  const [sapChilePreviewGroupPage, setSapChilePreviewGroupPage] = useState(1);
  const [expandedSapChileGroupId, setExpandedSapChileGroupId] = useState(null);
  /**
   * Perú + DOCKERS (envío a SAP): línea Hombre/Mujer elegida para resolver el descuento
   * DOCKERS-MEN / DOCKERS-WOMEN del partner. Vacío hasta que el usuario seleccione.
   */
  const [sapProcessDockersGender, setSapProcessDockersGender] = useState('');
  const [sapProcessPartnerLoading, setSapProcessPartnerLoading] = useState(false);
  /** true si alguna OV de la vista previa es DOCKERS + Perú (muestra el selector Hombre/Mujer y bloquea envío). */
  const [sapProcessDockersPeruActive, setSapProcessDockersPeruActive] = useState(false);
  const [sapProcessPartnerError, setSapProcessPartnerError] = useState('');
  /** OV construidas (con customerRut/isDockers) y map de ítems SAP cacheado para re-enriquecer al cambiar el género. */
  const sapProcessBuiltOrdersRef = useRef([]);
  const sapProcessItemMapRef = useRef({});
  const sapProcessFlatRowsRef = useRef([]);
  const sapProcessWhsOptsRef = useRef({});
  /** true tras quitar una OV del modal: re-enriquecer para recalcular la demanda total por SKU. */
  const sapProcessReenrichPendingRef = useRef(false);
  /** Cache RUT → partner SAP (con DiscountBrand) para no reconsultar el mismo cliente. */
  const sapProcessPartnerByRutRef = useRef(new Map());
  const [sapProcessLoading, setSapProcessLoading] = useState(false);
  /** Avance del envío a SAP («Enviando OV 2 de 3…»); el backend procesa una por una. */
  const [sapSendProgressMsg, setSapSendProgressMsg] = useState('');
  const [showSapResultDialog, setShowSapResultDialog] = useState(false);
  const [sapResult, setSapResult] = useState(null);
  const [showSapErrorDialog, setShowSapErrorDialog] = useState(false);
  const [sapErrorMessage, setSapErrorMessage] = useState('');
  const [isCancelOrderDialogOpen, setIsCancelOrderDialogOpen] = useState(false);
  const [cancelOrderLoading, setCancelOrderLoading] = useState(false);
  const [showCancelOrderResultDialog, setShowCancelOrderResultDialog] = useState(false);
  const [cancelOrderResultSummary, setCancelOrderResultSummary] = useState(null);
  const salesListSelectAllHeaderRef = useRef(null);

  /** Recarga la tabla: en modo búsqueda activo usa GET `sales-orders/search`; si no, GET `sales-orders/headers` paginado. */
  const loadSalesOrders = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      let result;
      if (searchModeActive) {
        result = await fetchSalesOrdersSearch({
          orderNumber: appliedSearch.orderNumber,
          status: appliedSearch.status,
          channel: appliedSearch.channel,
          startDate: appliedSearch.startDate,
          endDate: appliedSearch.endDate,
          page: currentPage,
          size: pageSize
        });
      } else {
        result = await fetchSalesOrdersPage({
          page: currentPage,
          size: pageSize,
          sortBy: 'orderNumber'
        });
      }
      setOrders(result.rows);
      setTotalPages(result.totalPages ?? null);
      setTotalElements(
        result.totalElements != null && Number.isFinite(Number(result.totalElements))
          ? Number(result.totalElements)
          : null
      );
      setLastPageFromApi(result.last ?? null);
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      const msg =
        (typeof data === 'string' && data) ||
        data?.message ||
        err?.message ||
        '';
      setListError(String(msg || 'Error al cargar órdenes de venta.'));
      setOrders([]);
      setTotalPages(null);
      setTotalElements(null);
      setLastPageFromApi(null);
    } finally {
      setListLoading(false);
    }
    // No incluir `autoT`: en useAutoTranslate se recrea cada render y provocaría fetch en bucle.
  }, [
    appliedSearch.orderNumber,
    appliedSearch.status,
    appliedSearch.channel,
    appliedSearch.startDate,
    appliedSearch.endDate,
    currentPage,
    pageSize,
    searchModeActive
  ]);

  useEffect(() => {
    loadSalesOrders();
  }, [loadSalesOrders]);

  useEffect(() => {
    const ac = new AbortController();
    setFilterSaleChannelsLoading(true);
    (async () => {
      try {
        const list = await fetchAllSaleChannels({ signal: ac.signal });
        if (!ac.signal.aborted) setFilterSaleChannels(Array.isArray(list) ? list : []);
      } catch {
        if (!ac.signal.aborted) setFilterSaleChannels([]);
      } finally {
        if (!ac.signal.aborted) setFilterSaleChannelsLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  /** Si el usuario eligió un slug legacy y ya cargaron los canales del API, sustituir por el nombre exacto del catálogo. */
  useEffect(() => {
    if (!filterSaleChannels.length) return;
    setFilters((prev) => {
      const v = (prev.channel || '').trim();
      if (!v) return prev;
      if (filterSaleChannels.some((c) => String(c.name || '').trim() === v)) return prev;
      const mappedName = SALES_ORDER_CHANNEL_UI_TO_API[v];
      if (mappedName && filterSaleChannels.some((c) => String(c.name || '').trim() === mappedName)) {
        return { ...prev, channel: mappedName };
      }
      return prev;
    });
  }, [filterSaleChannels]);

  const handleSalesListPageSizeChange = useCallback((e) => {
    const target = e.currentTarget;
    let raw = e.detail?.selectedOption?.value;
    if (raw == null || raw === '') {
      raw = target?.value ?? target?.selectedOption?.value;
    }
    const n = parseInt(String(raw ?? ''), 10);
    if (!Number.isFinite(n) || !SALES_LIST_PAGE_SIZES.includes(n)) return;
    setPageSize(n);
    setCurrentPage(1);
  }, []);

  const handleExportSalesOrdersExcel = useCallback(() => {
    if (!searchModeActive) return;
    if (!orders.length) {
      window.alert(autoT('No hay órdenes en esta página para exportar.'));
      return;
    }
    const headers = [
      autoT('N° Orden'),
      autoT('DocNum'),
      autoT('Fecha OC', {}),
      autoT('Canal'),
      autoT('Marketplace'),
      autoT('Sucursal Destino'),
      autoT('RUT', {}),
      autoT('Marca'),
      autoT('Total de líneas', {}),
      autoT('Cant. Unidades', {}),
      autoT('Total'),
      autoT('Moneda'),
      autoT('Estado')
    ];
    const metaRow1 = [
      autoT('Exportado'),
      new Date().toLocaleString('es-CL'),
      autoT('Página'),
      String(currentPage),
      autoT('Registros por página'),
      String(pageSize)
    ];
    const dataRows = orders.map((row) => [
      row.orderNumber ?? '',
      formatSalesOrderListDocNum(row),
      formatSalesOrderListDate(row.purchaseOrderDate),
      row.channel ?? '',
      row.marketplaceLabel ?? '',
      formatSalesOrderListDestination(row),
      row.clientRut ?? '',
      row.brand != null && String(row.brand).trim() ? String(row.brand) : '—',
      formatSalesOrderListCount(row.productLineCount),
      formatSalesOrderListCount(row.productUnitsTotal),
      formatSalesOrderListMoney(row.total, row.currency),
      row.currency ?? '',
      getSalesOrderStatusExportLabel(row, autoT)
    ]);
    const aoa = [metaRow1, [], headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OV');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }),
      `ordenes-venta-${Date.now()}.xlsx`
    );
  }, [autoT, currentPage, orders, pageSize, searchModeActive]);

  const handleApplySearch = useCallback(() => {
    const preset = filters.datePreset || 'all';
    const hasDateFromPickers = !!(filters.createdAt || '').trim() || !!(filters.createdAtEnd || '').trim();
    const hasQuickDatePreset = preset !== 'all' && preset !== 'custom';
    const hasCriteria =
      !!(filters.orderNumber || '').trim() ||
      !!filters.status ||
      !!(filters.channel || '').trim() ||
      hasQuickDatePreset ||
      (preset === 'custom' && hasDateFromPickers);

    if (!hasCriteria) {
      setSearchModeActive(false);
      setAppliedSearch({ ...EMPTY_APPLIED_SEARCH });
      setCurrentPage(1);
      setSelectedOrderIds([]);
      return;
    }

    let startDate = '';
    let endDate = '';
    if (preset === 'custom') {
      ({ startDate, endDate } = buildSearchDateRangeIso(filters.createdAt, filters.createdAtEnd));
    } else if (preset !== 'all') {
      ({ startDate, endDate } = buildPresetDateRangeIso(preset));
    }
    const apiStatus = filters.status
      ? SALES_ORDER_STATUS_UI_TO_API[filters.status] || String(filters.status).toUpperCase()
      : '';
    const rawCh = (filters.channel || '').trim();
    /** Con catálogo cargado, el `<Select>` usa el `name` del API; sin catálogo (error de red) se mantiene el mapa legacy. */
    const apiChannel = rawCh
      ? filterSaleChannels.length > 0
        ? rawCh
        : SALES_ORDER_CHANNEL_UI_TO_API[rawCh] ?? rawCh
      : '';

    setAppliedSearch({
      orderNumber: (filters.orderNumber || '').trim(),
      status: apiStatus,
      channel: apiChannel,
      startDate,
      endDate
    });
    setSearchModeActive(true);
    setCurrentPage(1);
    setSelectedOrderIds([]);
  }, [filters, filterSaleChannels.length]);

  const handleClearListFilters = useCallback(() => {
    setFilters({
      orderNumber: '',
      status: '',
      channel: '',
      datePreset: 'all',
      createdAt: '',
      createdAtEnd: ''
    });
    setSearchModeActive(false);
    setAppliedSearch({ ...EMPTY_APPLIED_SEARCH });
    setCurrentPage(1);
    setSelectedOrderIds([]);
  }, []);

  /** Claves de la página actual elegibles para selección masiva (`PENDING` o `ACCEPTED`; no canceladas). */
  const pageOrderKeysEligible = useMemo(
    () =>
      orders
        .map((o) => {
          const k = String(o.id ?? o.orderNumber ?? '').trim();
          return { k, o };
        })
        .filter(
          ({ k, o }) => k && !k.startsWith('ov-') && isSalesOrderStatusSelectableInList(o.status)
        )
        .map(({ k }) => k),
    [orders]
  );

  const allVisibleOrdersSelected = useMemo(
    () =>
      pageOrderKeysEligible.length > 0 &&
      pageOrderKeysEligible.every((k) => selectedOrderIds.includes(k)),
    [pageOrderKeysEligible, selectedOrderIds]
  );

  const someVisibleOrdersSelected = useMemo(
    () => pageOrderKeysEligible.some((k) => selectedOrderIds.includes(k)),
    [pageOrderKeysEligible, selectedOrderIds]
  );

  /** Si en la página actual un id seleccionado se ve con estado no PENDING, no abrir / enviar SAP. */
  const canProcessSapByVisibleRows = useMemo(() => {
    for (const row of orders) {
      const k = String(row.id ?? row.orderNumber ?? '').trim();
      if (!k || !selectedOrderIds.includes(k)) continue;
      if (!isSalesOrderStatusPending(row.status)) return false;
    }
    return true;
  }, [orders, selectedOrderIds]);

  /** Solo órdenes cancelables (`PENDING`/`ACCEPTED`) pueden incluirse en cancelación masiva. */
  const canCancelByVisibleRows = useMemo(() => {
    for (const row of orders) {
      const k = String(row.id ?? row.orderNumber ?? '').trim();
      if (!k || !selectedOrderIds.includes(k)) continue;
      if (!canCancelSalesOrderStatus(row.status)) return false;
    }
    return true;
  }, [orders, selectedOrderIds]);

  /** Si una fila visible deja de ser seleccionable (solo PENDING/ACCEPTED), la quitamos de la selección. */
  useEffect(() => {
    if (!orders.length) return;
    setSelectedOrderIds((prev) => {
      if (!prev.length) return prev;
      const toDrop = new Set();
      for (const row of orders) {
        const k = String(row.id ?? row.orderNumber ?? '').trim();
        if (!k) continue;
        if (!isSalesOrderStatusSelectableInList(row.status)) toDrop.add(k);
      }
      if (!toDrop.size) return prev;
      return prev.filter((id) => !toDrop.has(id));
    });
  }, [orders]);

  useEffect(() => {
    const el = salesListSelectAllHeaderRef.current;
    if (el && 'indeterminate' in el) {
      el.indeterminate = someVisibleOrdersSelected && !allVisibleOrdersSelected;
    }
  }, [someVisibleOrdersSelected, allVisibleOrdersSelected]);

  const toggleOrderRowSelected = useCallback((row) => {
    const k = String(row.id ?? row.orderNumber ?? '').trim();
    if (!k || !isSalesOrderStatusSelectableInList(row.status)) return;
    setSelectedOrderIds((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  }, []);

  const toggleSelectAllVisibleOrders = useCallback(() => {
    setSelectedOrderIds((prev) => {
      if (!pageOrderKeysEligible.length) return prev;
      const prevSet = new Set(prev);
      const allOnPage = pageOrderKeysEligible.every((k) => prevSet.has(k));
      const next = new Set(prevSet);
      if (allOnPage) {
        pageOrderKeysEligible.forEach((k) => next.delete(k));
      } else {
        pageOrderKeysEligible.forEach((k) => next.add(k));
      }
      return [...next];
    });
  }, [pageOrderKeysEligible]);

  const closeSapChileProcessPreview = useCallback(() => {
    sapProcessPreviewAbortRef.current?.abort();
    sapProcessEligibleOrderIdsRef.current = null;
    setIsSapChileProcessPreviewOpen(false);
    setSapChilePreviewFlatRows([]);
    setSapChilePreviewGrouped([]);
    setSapChilePreviewSapItemsError('');
    setSapChilePreviewSapItemsLoading(false);
    setSapChilePreviewSapItemsProgress({ done: 0, total: 0 });
    setSapChilePreviewGroupPage(1);
    setExpandedSapChileGroupId(null);
    setSapExportDialogOpen(false);
    setSapProcessDockersGender('');
    setSapProcessDockersPeruActive(false);
    setSapProcessPartnerError('');
    setSapProcessPartnerLoading(false);
  }, []);

  /** Quita una OV del modal «Procesar a SAP» sin cerrar; actualiza filas, grupos y ref de IDs para el POST. */
  const removeSapChilePreviewGroup = useCallback((grp) => {
    if (!grp || sapProcessLoading || sapChilePreviewSapItemsLoading) return;
    const orderIdRemove = String(
      (grp.detailRows || []).find((r) => r && r._orderId)?._orderId || ''
    ).trim();
    const nOc = String(grp.nOc || '').trim();

    /** La demanda total por SKU cambia al quitar la OV: re-validar stock tras aplicar el filtro (ver efecto). */
    sapProcessReenrichPendingRef.current = true;
    setExpandedSapChileGroupId((id) => (id === grp.id ? null : id));
    setSapChilePreviewGrouped((prev) => prev.filter((g) => g.id !== grp.id));
    setSapChilePreviewFlatRows((prev) => {
      const filtered = prev.filter((r) => {
        const rid = String(r?._orderId || '').trim();
        if (orderIdRemove && rid) return rid !== orderIdRemove;
        const rOn = String(r?._orderNumber ?? r?.nOc ?? '').trim();
        return rOn !== nOc;
      });
      const cur = sapProcessEligibleOrderIdsRef.current;
      if (Array.isArray(cur)) {
        if (orderIdRemove) {
          sapProcessEligibleOrderIdsRef.current = cur.filter((id) => String(id).trim() !== orderIdRemove);
        } else {
          const keep = new Set(
            filtered.map((r) => String(r?._orderId || '').trim()).filter(Boolean)
          );
          sapProcessEligibleOrderIdsRef.current = cur.filter((id) => keep.has(String(id).trim()));
        }
      }
      // Mantener refs (re-enriquecido DOCKERS) y flag Perú+DOCKERS sincronizados con la OV removida.
      sapProcessFlatRowsRef.current = (sapProcessFlatRowsRef.current || []).filter((r) => {
        const rid = String(r?._orderId || '').trim();
        if (orderIdRemove && rid) return rid !== orderIdRemove;
        const rOn = String(r?._orderNumber ?? r?.nOc ?? '').trim();
        return rOn !== nOc;
      });
      sapProcessBuiltOrdersRef.current = (sapProcessBuiltOrdersRef.current || []).filter(
        (b) => String(b.orderNumber || '').trim() !== nOc
      );
      const sessionIsPeru = getWholesaleCountryFromSession() === 'PE';
      setSapProcessDockersPeruActive(
        (sapProcessBuiltOrdersRef.current || []).some(
          (b) => b.isDockers && (sessionIsPeru || isPeruFederalTaxIdForWholesale(b.customerRut))
        )
      );
      return filtered;
    });
  }, [sapProcessLoading, sapChilePreviewSapItemsLoading]);

  const runChileSapProcessPreviewFromRaws = useCallback(
    async (rawApis, signal, priceListByOrderNumber = {}, partnersByRut = null) => {
      setSapChilePreviewSapItemsError('');
      setSapChilePreviewGroupPage(1);
      setExpandedSapChileGroupId(null);
      setIsSapChileProcessPreviewOpen(true);
      // Nueva vista previa: reiniciar selección DOCKERS; la cache de partners parte con los ya consultados
      // al resolver la lista de precios (así DOCKERS no vuelve a consultar SAP).
      setSapProcessDockersGender('');
      setSapProcessPartnerError('');
      sapProcessPartnerByRutRef.current = partnersByRut instanceof Map ? new Map(partnersByRut) : new Map();

      const whs = getUserWhsCode();
      const sapWhsOpts = { stockShortageIsBlocking: true, sapWarehouseCode: whs };

      const builtOrders = rawApis.map((api) => buildSapProcessPreviewRowsFromOrderRaw(api));
      /**
       * Lista de precios SAP por OV (`PriceListNum` del cliente en SAP, resuelta al procesar la selección).
       * Se inyecta por fila como `_sapPriceListId`; el enrich la usa con precedencia estricta.
       */
      const priceListMap = priceListByOrderNumber && typeof priceListByOrderNumber === 'object'
        ? priceListByOrderNumber
        : {};
      /**
       * Cabecera 0 (Chile no persiste el % salvo Falabella): usar el descuento de marca del socio,
       * igual que la carga. Perú + DOCKERS no se estampa acá; lo resuelve el selector Hombre/Mujer.
       */
      const withBrandDiscount = applyBrandDiscountFallbackToSapProcessRows(
        builtOrders,
        partnersByRut,
        { sessionCountry: getWholesaleCountryFromSession() }
      );
      const flat = withBrandDiscount.map((row) => {
        const listId = priceListMap[row._orderNumber];
        return listId != null ? { ...row, _sapPriceListId: listId } : row;
      });
      // Cache para re-enriquecer (recalcular validación) al cambiar la línea DOCKERS sin reconsultar SAP.
      sapProcessBuiltOrdersRef.current = builtOrders;
      sapProcessFlatRowsRef.current = flat;
      sapProcessWhsOptsRef.current = sapWhsOpts;
      // Perú + DOCKERS: requiere elegir línea Hombre/Mujer para resolver el descuento de marca.
      const sessionIsPeru = getWholesaleCountryFromSession() === 'PE';
      const dockersPeruActive = builtOrders.some(
        (b) =>
          b.isDockers &&
          (sessionIsPeru || isPeruFederalTaxIdForWholesale(b.customerRut))
      );
      setSapProcessDockersPeruActive(dockersPeruActive);
      const uniqueCodes = [
        ...new Set(flat.map((r) => normalizeSapItemCodeKey(r.skuIac)).filter(Boolean))
      ].sort();

      if (uniqueCodes.length === 0) {
        sapProcessItemMapRef.current = {};
        const enriched = enrichBulkPreviewRowsWithSapItems(
          flat,
          {},
          autoT,
          BULK_FORMAT_DEFAULT_ID,
          sapWhsOpts
        );
        const groupsInput = builtOrders.map((b) => ({
          orderNumber: b.orderNumber,
          sucursalLabel: b.sucursalLabel,
          detailRows: enriched.filter((r) => r._orderNumber === b.orderNumber)
        }));
        setSapChilePreviewFlatRows(enriched);
        setSapChilePreviewGrouped(groupSapProcessPreviewRowsByOrder(groupsInput));
        return;
      }

      setSapChilePreviewSapItemsProgress({ done: 0, total: uniqueCodes.length });
      setSapChilePreviewSapItemsLoading(true);
      try {
        const { map } = await fetchSapB1PreviewItemsMap(uniqueCodes, {
          signal,
          debugSampleCap: 40,
          onProgress: ({ done, total }) => {
            if (!signal.aborted) setSapChilePreviewSapItemsProgress({ done, total });
          }
        });
        if (signal.aborted) return;
        sapProcessItemMapRef.current = map;
        const enriched = enrichBulkPreviewRowsWithSapItems(
          flat,
          map,
          autoT,
          BULK_FORMAT_DEFAULT_ID,
          sapWhsOpts
        );
        const groupsInput = builtOrders.map((b) => ({
          orderNumber: b.orderNumber,
          sucursalLabel: b.sucursalLabel,
          detailRows: enriched.filter((r) => r._orderNumber === b.orderNumber)
        }));
        setSapChilePreviewFlatRows(enriched);
        setSapChilePreviewGrouped(groupSapProcessPreviewRowsByOrder(groupsInput));
      } catch (e) {
        const canceled =
          signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
        if (canceled) return;
        setSapChilePreviewSapItemsError(String(e?.message || 'Error al consultar ítems SAP.'));
        sapProcessItemMapRef.current = Object.fromEntries(uniqueCodes.map((c) => [c, null]));
        const enriched = enrichBulkPreviewRowsWithSapItems(
          flat,
          sapProcessItemMapRef.current,
          autoT,
          BULK_FORMAT_DEFAULT_ID,
          sapWhsOpts
        );
        const groupsInput = builtOrders.map((b) => ({
          orderNumber: b.orderNumber,
          sucursalLabel: b.sucursalLabel,
          detailRows: enriched.filter((r) => r._orderNumber === b.orderNumber)
        }));
        setSapChilePreviewFlatRows(enriched);
        setSapChilePreviewGrouped(groupSapProcessPreviewRowsByOrder(groupsInput));
      } finally {
        if (!signal.aborted) {
          setSapChilePreviewSapItemsLoading(false);
          setSapChilePreviewSapItemsProgress({ done: 0, total: 0 });
        }
      }
    },
    [autoT]
  );

  /** Re-agrupa filas enriquecidas usando las OV ya construidas (etiquetas de sucursal). */
  const rebuildSapProcessGroupsFromEnriched = useCallback((enriched) => {
    const builtOrders = sapProcessBuiltOrdersRef.current || [];
    const groupsInput = builtOrders.map((b) => ({
      orderNumber: b.orderNumber,
      sucursalLabel: b.sucursalLabel,
      detailRows: enriched.filter((r) => r._orderNumber === b.orderNumber)
    }));
    setSapChilePreviewFlatRows(enriched);
    setSapChilePreviewGrouped(groupSapProcessPreviewRowsByOrder(groupsInput));
  }, []);

  /**
   * Tras quitar una OV del modal «Procesar a SAP»: la demanda total por SKU bajó, así que se re-enriquece
   * con el map de ítems SAP cacheado (sin reconsultar SAP). Las filas enriquecidas conservan
   * `_headerDiscountPercent` / `_sapPriceListId`, por lo que precio y descuento se recalculan igual.
   */
  useEffect(() => {
    if (!sapProcessReenrichPendingRef.current) return;
    sapProcessReenrichPendingRef.current = false;
    if (!sapChilePreviewFlatRows.length) return;
    const enriched = enrichBulkPreviewRowsWithSapItems(
      sapChilePreviewFlatRows,
      sapProcessItemMapRef.current || {},
      autoT,
      BULK_FORMAT_DEFAULT_ID,
      sapProcessWhsOptsRef.current || {}
    );
    rebuildSapProcessGroupsFromEnriched(enriched);
  }, [sapChilePreviewFlatRows, autoT, rebuildSapProcessGroupsFromEnriched]);

  /**
   * Perú + DOCKERS: al elegir línea Hombre/Mujer, resuelve el descuento de marca del partner SAP
   * (por RUT de cada OV) y re-enriquece la vista previa con el % aplicado para recalcular la validación.
   * Reutiliza el map de ítems SAP cacheado (no reconsulta SAP).
   */
  const applySapProcessDockersGender = useCallback(
    async (gender) => {
      setSapProcessDockersGender(gender);
      setSapProcessPartnerError('');

      const flat = sapProcessFlatRowsRef.current || [];
      const itemMap = sapProcessItemMapRef.current || {};
      const whsOpts = sapProcessWhsOptsRef.current || {};

      // Sin línea seleccionada: volver al descuento de cabecera original (sin marca DOCKERS).
      if (gender !== 'men' && gender !== 'women') {
        const enriched = enrichBulkPreviewRowsWithSapItems(
          flat,
          itemMap,
          autoT,
          BULK_FORMAT_DEFAULT_ID,
          whsOpts
        );
        rebuildSapProcessGroupsFromEnriched(enriched);
        return;
      }

      const builtOrders = sapProcessBuiltOrdersRef.current || [];
      const sessionIsPeru = getWholesaleCountryFromSession() === 'PE';
      const dockersPeruOrders = builtOrders.filter(
        (b) =>
          b.isDockers &&
          (sessionIsPeru || isPeruFederalTaxIdForWholesale(b.customerRut))
      );
      const uniqueRuts = [
        ...new Set(dockersPeruOrders.map((b) => String(b.customerRut || '').trim()).filter(Boolean))
      ];

      setSapProcessPartnerLoading(true);
      try {
        for (const rut of uniqueRuts) {
          if (sapProcessPartnerByRutRef.current.has(rut)) continue;
          try {
            const partner = await fetchSapB1BusinessPartnerByFederalTaxId(rut);
            sapProcessPartnerByRutRef.current.set(rut, partner ?? null);
          } catch (e) {
            sapProcessPartnerByRutRef.current.set(rut, null);
          }
        }

        // RUT → % descuento DOCKERS-MEN/WOMEN según la línea elegida.
        const rutToPct = new Map();
        for (const rut of uniqueRuts) {
          const partner = sapProcessPartnerByRutRef.current.get(rut);
          const pct = resolveBulkBrandDiscount(partner, 'DOCKERS', {
            dockersGender: gender,
            isPeruContext: true
          });
          rutToPct.set(rut, Number.isFinite(Number(pct)) ? Number(pct) : 0);
        }

        // OV DOCKERS Perú → % a aplicar; el resto conserva su descuento de cabecera.
        const orderNumberToPct = new Map();
        for (const b of dockersPeruOrders) {
          orderNumberToPct.set(b.orderNumber, rutToPct.get(String(b.customerRut || '').trim()) ?? 0);
        }

        const adjustedFlat = flat.map((row) =>
          orderNumberToPct.has(row._orderNumber)
            ? { ...row, _headerDiscountPercent: orderNumberToPct.get(row._orderNumber) }
            : row
        );

        const enriched = enrichBulkPreviewRowsWithSapItems(
          adjustedFlat,
          itemMap,
          autoT,
          BULK_FORMAT_DEFAULT_ID,
          whsOpts
        );
        rebuildSapProcessGroupsFromEnriched(enriched);

        // IX Comercio Perú no tiene descuento de negocio: un 0 % en esas OV no es error.
        const anyZero = dockersPeruOrders.some(
          (b) => !b.isIxComercioPeru && !(Number(orderNumberToPct.get(b.orderNumber)) > 0)
        );
        if (anyZero) {
          setSapProcessPartnerError(
            autoT('No se encontró un descuento DOCKERS válido para una o más OV con la línea seleccionada.')
          );
        }
      } finally {
        setSapProcessPartnerLoading(false);
      }
    },
    [autoT, rebuildSapProcessGroupsFromEnriched]
  );

  const openProcessSapDialog = useCallback(async () => {
    if (selectedOrderIds.length === 0) return;

    const detailFailedMessage = autoT(
      'No se pudo obtener el detalle de una o más órdenes. Revise la selección e intente de nuevo.'
    );

    sapProcessPreviewAbortRef.current?.abort();
    const ac = new AbortController();
    sapProcessPreviewAbortRef.current = ac;

    setSapChileProcessSelectionLoading(true);
    try {
      const settled = await Promise.allSettled(
        selectedOrderIds.map((selId) =>
          isSalesOrderUuidKey(selId)
            ? fetchSalesOrderById(selId)
            : fetchSalesOrderByOrderNumber(selId)
        )
      );
      if (ac.signal.aborted) return;

      const rawList = settled.map((r, i) => ({
        selId: selectedOrderIds[i],
        raw: r.status === 'fulfilled' ? r.value : null,
        ok: r.status === 'fulfilled'
      }));

      if (rawList.some((x) => !x.ok)) {
        window.alert(detailFailedMessage);
        return;
      }

      const notPending = rawList.filter(
        (x) => !isSalesOrderStatusPending(pickSalesOrderStatusFromApiPayload(x.raw))
      );
      if (notPending.length > 0) {
        window.alert(
          autoT(
            'Solo se pueden enviar a SAP las órdenes en estado PENDING (creadas). Quite de la selección las enviadas a SAP o canceladas, o vuelva a cargar el listado.'
          )
        );
        return;
      }

      const raws = rawList.map((x) => x.raw);

      /**
       * Lista de precios SAP por OV: `PriceListNum` del cliente en SAP (socio de negocio buscado por RUT,
       * una consulta por RUT). OV sin RUT, cliente no encontrado o sin lista asignada ⇒ bloqueo total.
       */
      const partnersByRut = new Map();
      const uniqueRuts = [
        ...new Set(raws.map((raw) => String(pickOrderCustomerRutForSap(raw) || '').trim()).filter(Boolean))
      ];
      for (const rut of uniqueRuts) {
        try {
          const partner = await fetchSapB1BusinessPartnerByFederalTaxId(rut);
          partnersByRut.set(rut, partner ?? null);
        } catch (e) {
          const canceled =
            ac.signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
          if (canceled) return;
          partnersByRut.set(rut, null);
        }
        if (ac.signal.aborted) return;
      }

      const priceListByOrderNumber = {};
      const ordersMissingPriceList = [];
      for (const raw of raws) {
        const orderNumber = String(raw?.orderNumber ?? raw?.order_number ?? raw?.id ?? '').trim();
        const rut = String(pickOrderCustomerRutForSap(raw) || '').trim();
        const partner = rut ? partnersByRut.get(rut) : null;
        const listId = resolvePartnerPriceListNum(partner);
        if (!rut) {
          ordersMissingPriceList.push(`${orderNumber || '—'} (${autoT('sin RUT de cliente')})`);
        } else if (!partner) {
          ordersMissingPriceList.push(`${orderNumber || '—'} (${rut} — ${autoT('cliente no encontrado en SAP')})`);
        } else if (listId == null) {
          ordersMissingPriceList.push(
            `${orderNumber || '—'} (${rut} — ${autoT('sin lista de precios asignada en SAP')})`
          );
        } else if (orderNumber) {
          priceListByOrderNumber[orderNumber] = listId;
        }
      }

      if (ordersMissingPriceList.length > 0) {
        window.alert(
          autoT(
            'Una o más OV no tienen lista de precios SAP: el cliente debe existir en SAP y tener una lista de precios asignada (PriceListNum).'
          ) +
            '\n\n' +
            ordersMissingPriceList.join('\n')
        );
        return;
      }

      sapProcessEligibleOrderIdsRef.current = rawList.map((x) => {
        const raw = x.raw;
        if (raw && typeof raw === 'object') {
          const id = String(raw.id ?? raw.salesOrderId ?? raw.orderId ?? raw.order_id ?? '').trim();
          if (id) return id;
        }
        return String(x.selId ?? '').trim();
      });
      await runChileSapProcessPreviewFromRaws(raws, ac.signal, priceListByOrderNumber, partnersByRut);
    } finally {
      if (!ac.signal.aborted) setSapChileProcessSelectionLoading(false);
    }
  }, [selectedOrderIds, autoT, runChileSapProcessPreviewFromRaws]);

  const openCancelOrderDialog = useCallback(() => {
    if (selectedOrderIds.length === 0) return;
    setIsCancelOrderDialogOpen(true);
  }, [selectedOrderIds]);

  const closeCancelOrderDialog = useCallback(() => {
    if (cancelOrderLoading) return;
    setIsCancelOrderDialogOpen(false);
  }, [cancelOrderLoading]);

  /** Cancela por PUT cada OV seleccionada; refresca listado y muestra resumen si hubo errores parciales. */
  const handleConfirmCancelSalesOrders = useCallback(async () => {
    if (selectedOrderIds.length === 0) return;
    setCancelOrderLoading(true);
    try {
      const mb = getLoggedInUserId();
      const results = await cancelSalesOrdersByIds(selectedOrderIds, mb ? { modifiedBy: mb } : {});
      const failures = results.filter((r) => r.outcome === 'error');
      const successCount = results.filter((r) => r.outcome === 'ok').length;
      setIsCancelOrderDialogOpen(false);
      setSelectedOrderIds([]);
      await loadSalesOrders();
      if (failures.length > 0) {
        setCancelOrderResultSummary({ successCount, failures });
        setShowCancelOrderResultDialog(true);
      }
    } catch (e) {
      setCancelOrderResultSummary({
        successCount: 0,
        failures: [{ id: '—', errorMessage: String(e?.message || e) }]
      });
      setShowCancelOrderResultDialog(true);
      setIsCancelOrderDialogOpen(false);
    } finally {
      setCancelOrderLoading(false);
    }
  }, [selectedOrderIds, loadSalesOrders]);

  /** Envío masivo de OV a SAP B1; `orderIds` + `itemAdjustments` (cantidad a SAP y quiebre por línea). */
  const handleSendSalesOrdersToSap = async () => {
    const orderIds = sapProcessEligibleOrderIdsRef.current;
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      window.alert(
        autoT(
          'No hay órdenes elegibles para enviar. Cierre el diálogo y vuelva a preparar la vista previa con órdenes PENDING.'
        )
      );
      return;
    }
    const itemAdjustments = sapChilePreviewFlatRows.map((row) => {
      const stock = Number(row.stockDispSap);
      const cantPedida = Number(row.cantOc);
      const stockFinite = Number.isFinite(stock);
      const cantFinite = Number.isFinite(cantPedida) && cantPedida > 0;
      const quantityToSend =
        stockFinite && cantFinite ? Math.min(Math.max(stock, 0), cantPedida) : cantFinite ? 0 : 0;
      const brokeQuantity = cantFinite ? cantPedida - quantityToSend : 0;
      return {
        orderId: row._orderId,
        itemId: row._itemId,
        quantityToSend,
        brokeQuantity
      };
    });
    const orderIdsSent = [...orderIds];
    setSapProcessLoading(true);
    setSapSendProgressMsg('');
    try {
      // El backend responde al instante y envía las OV una por una; acá se sigue el avance.
      const result = await sendSalesOrdersToSapAndWait(orderIds, itemAdjustments, {
        onProgress: ({ processed, total }) =>
          setSapSendProgressMsg(
            total > 0
              ? `${autoT('Enviando OV')} ${Math.min(processed + 1, total)} ${autoT('de')} ${total}…`
              : ''
          )
      });
      const mbSap = getLoggedInUserId();
      if (mbSap) {
        await Promise.allSettled(
          orderIdsSent.map((ovId) => updateSalesOrderModifiedBy(ovId, mbSap))
        );
      }
      setSapResult(result);
      sapProcessEligibleOrderIdsRef.current = null;
      closeSapChileProcessPreview();
      setShowSapResultDialog(true);
      setSelectedOrderIds([]);
      loadSalesOrders();
    } catch (err) {
      setSapErrorMessage(
        autoT('Error al enviar órdenes a SAP B1: ') + (err.response?.data?.message || err.message)
      );
      setShowSapErrorDialog(true);
      closeSapChileProcessPreview();
      // El envío sigue en el servidor aunque acá se haya perdido el hilo: refrescar muestra el estado real.
      loadSalesOrders();
    } finally {
      setSapProcessLoading(false);
      setSapSendProgressMsg('');
    }
  };

  const sapChileValidationCounts = useMemo(
    () => countByValidation(sapChilePreviewFlatRows),
    [sapChilePreviewFlatRows]
  );

  /** Código bodega sesión al mostrar la vista previa «Procesar a SAP» (vuelve a leer al abrir el modal). */
  const sapChileSessionWhs = useMemo(
    () => (isSapChileProcessPreviewOpen ? getUserWhsCode() : ''),
    [isSapChileProcessPreviewOpen]
  );

  /**
   * Moneda de la vista previa «Procesar a SAP»: las OV del modal son del tenant de la sesión, así que en
   * Perú se muestran en soles (2 decimales) y en Chile en CLP. El perfil se usa solo por su moneda.
   */
  const sapPreviewCurrencyProfileId =
    getWholesaleCountryFromSession() === 'PE' ? RIPLEY_PERU_PROFILE_ID : BULK_FORMAT_DEFAULT_ID;
  const sapChilePreviewCurrencyLabel = useMemo(
    () => getBulkFormatCurrency(sapPreviewCurrencyProfileId),
    [sapPreviewCurrencyProfileId]
  );

  const formatSapChilePreviewMoney = useCallback(
    (n) => formatBulkMoneyAmount(n, sapPreviewCurrencyProfileId),
    [sapPreviewCurrencyProfileId]
  );

  const sapChilePreviewGroupTotalPages = Math.max(
    1,
    Math.ceil(sapChilePreviewGrouped.length / SAP_PROCESS_PREVIEW_GROUPS_PAGE_SIZE)
  );

  const sapChilePreviewGroupedPageSlice = useMemo(() => {
    const start = (sapChilePreviewGroupPage - 1) * SAP_PROCESS_PREVIEW_GROUPS_PAGE_SIZE;
    return sapChilePreviewGrouped.slice(start, start + SAP_PROCESS_PREVIEW_GROUPS_PAGE_SIZE);
  }, [sapChilePreviewGrouped, sapChilePreviewGroupPage]);

  useEffect(() => {
    setSapChilePreviewGroupPage(1);
  }, [sapChilePreviewGrouped.length]);

  useEffect(() => {
    setExpandedSapChileGroupId(null);
  }, [sapChilePreviewGroupPage]);

  const canSubmitSapChileProcessPreview = useMemo(() => {
    if (!sapChileSessionWhs) return false;
    if (sapChilePreviewFlatRows.length === 0) return false;
    if (sapChileValidationCounts.error > 0) return false;
    if (sapChileProcessSelectionLoading || sapChilePreviewSapItemsLoading) return false;
    if (sapChilePreviewSapItemsError) return false;
    // Perú + DOCKERS: exige línea Hombre/Mujer y que el partner esté resuelto sin errores de descuento.
    if (sapProcessDockersPeruActive) {
      if (sapProcessPartnerLoading) return false;
      if (sapProcessDockersGender !== 'men' && sapProcessDockersGender !== 'women') return false;
      if (sapProcessPartnerError) return false;
    }
    return true;
  }, [
    sapChileSessionWhs,
    sapChilePreviewFlatRows.length,
    sapChileValidationCounts.error,
    sapChileProcessSelectionLoading,
    sapChilePreviewSapItemsLoading,
    sapChilePreviewSapItemsError,
    sapProcessDockersPeruActive,
    sapProcessPartnerLoading,
    sapProcessDockersGender,
    sapProcessPartnerError
  ]);

  const hasSapValidationExportRows = useMemo(
    () =>
      sapChilePreviewFlatRows.some(
        (r) => r && String(r.validationStatus || '').trim() && r.validationStatus !== 'ok'
      ),
    [sapChilePreviewFlatRows]
  );

  const sapExportColumnLabels = useMemo(
    () => [
      '#',
      autoT('Estado'),
      autoT('N° OC'),
      autoT('Sucursal destino'),
      autoT('Fecha OC'),
      autoT('PC13'),
      autoT('Descripción'),
      autoT('Cant. OC'),
      autoT('Precio acordado'),
      autoT('Total línea'),
      autoT('Precio lista SAP'),
      autoT('Descuento'),
      `${autoT('Precio SAP')} ${autoT('con Descuento')}`,
      autoT('Stock disp. SAP'),
      autoT('Pedido total SKU'),
      autoT('Observación validación (FAROS)')
    ],
    [autoT]
  );

  /** Encabezados de la hoja «Resumen stock» (Excel marcados de carga mayorista y Procesar a SAP). */
  const stockSummaryLabels = useMemo(
    () => ({
      sku: autoT('SKU'),
      descripcion: autoT('Descripción'),
      pedidoTotal: autoT('Pedido total'),
      disponible: autoT('Disponible SAP'),
      faltante: autoT('Faltante'),
      sucursales: autoT('N° sucursales')
    }),
    [autoT]
  );

  const exportSapProblemRowsCsv = useCallback(() => {
    const rows = filterSapProcessPreviewProblemRows(sapChilePreviewFlatRows);
    if (!rows.length) return;
    const blob = buildSapProcessPreviewProblemRowsCsvBlob(rows, sapExportColumnLabels);
    saveAs(blob, `validacion-procesar-sap-${Date.now()}.csv`);
  }, [sapChilePreviewFlatRows, sapExportColumnLabels]);

  const exportSapFullMarkedXlsx = useCallback(async () => {
    try {
      const buf = await buildSapProcessPreviewMarkedXlsxBuffer(
        sapChilePreviewFlatRows,
        sapExportColumnLabels,
        { stockSummaryLabels }
      );
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `validacion-procesar-sap-${Date.now()}.xlsx`);
    } catch (e) {
      console.error(e);
    }
  }, [sapChilePreviewFlatRows, sapExportColumnLabels, stockSummaryLabels]);

  const exportSapXlsxWithoutErrorRows = useCallback(async () => {
    try {
      const buf = await buildSapProcessPreviewXlsxWithoutErrorRowsBuffer(
        sapChilePreviewFlatRows,
        sapExportColumnLabels
      );
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `validacion-procesar-sap-sin-error-${Date.now()}.xlsx`);
    } catch (e) {
      console.error(e);
    }
  }, [sapChilePreviewFlatRows, sapExportColumnLabels]);

  /** Total de páginas coherente con el API o inferido desde `totalElements` y tamaño de página. */
  const effectiveTotalPages = useMemo(() => {
    if (totalPages != null && totalPages >= 1 && Number.isFinite(Number(totalPages))) {
      return Number(totalPages);
    }
    const te = totalElements != null ? Number(totalElements) : null;
    if (te != null && Number.isFinite(te) && pageSize > 0) {
      return Math.max(1, Math.ceil(te / pageSize));
    }
    return null;
  }, [totalPages, totalElements, pageSize]);

  const isNextPageDisabled = useMemo(() => {
    if (listLoading) return true;
    if (lastPageFromApi === true) return true;
    if (effectiveTotalPages != null && currentPage >= effectiveTotalPages) return true;
    return false;
  }, [listLoading, lastPageFromApi, effectiveTotalPages, currentPage]);

  /** Rango global y total para texto «Mostrando a–b de n» (paginación servidor). */
  const salesListRange = useMemo(() => {
    const nRows = orders.length;
    if (nRows === 0) {
      return {
        start: 0,
        end: 0,
        total: totalElements != null && Number.isFinite(Number(totalElements)) ? Number(totalElements) : null
      };
    }
    const start = (currentPage - 1) * pageSize + 1;
    const end = (currentPage - 1) * pageSize + nRows;
    return {
      start,
      end,
      total: totalElements != null && Number.isFinite(Number(totalElements)) ? Number(totalElements) : null
    };
  }, [orders.length, currentPage, pageSize, totalElements]);

  /** Total de páginas para etiqueta «Página x / y» (coherente con `isNextPageDisabled`). */
  const salesListTotalPagesLabel = useMemo(() => {
    if (effectiveTotalPages != null) return effectiveTotalPages;
    if (lastPageFromApi === true) return Math.max(1, currentPage);
    return null;
  }, [effectiveTotalPages, lastPageFromApi, currentPage]);

  // --- Carga mayorista (Excel → API) + vista previa agrupada ---
  // Parámetros de cabecera van en query del POST; el archivo es el cuerpo multipart.
  const [bulkParams, setBulkParams] = useState({
    customerRut: '',
    salesChannel: '',
    customerOrderNumber: '',
    brand: '',
    orderType: '',
    businessDiscount: '',
    /** `shipCode` del catálogo `GET shipping-methods`; opcional (auto-set a `URBANO` con Excel Ix Comercio o Multimarca). */
    shippingMethod: '',
    /** Solo Perú + marca DOCKERS: `men` | `women` → `DOCKERS-MEN` / `DOCKERS-WOMEN` en DiscountBrand. */
    dockersGender: ''
  });
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  /** Parseo local del Excel/CSV (puede tardar en archivos grandes). */
  const [bulkFileParsing, setBulkFileParsing] = useState(false);
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [bulkUploadSuccessMessage, setBulkUploadSuccessMessage] = useState('');
  /** Aviso tras «Excel solo líneas OK (reimportar)» cuando se repartió stock entre sucursales. */
  const [bulkStockAllocationNotice, setBulkStockAllocationNotice] = useState('');
  const [bulkSapPartnerForConfirm, setBulkSapPartnerForConfirm] = useState(null);
  /** Consulta automática del socio SAP (tras ítems SAP); ya no hay modal «Confirmar carga». */
  const [bulkSapPartnerLoading, setBulkSapPartnerLoading] = useState(false);
  /** Error de lookup del socio (visible encima del campo RUT). */
  const [bulkSapPartnerLookupError, setBulkSapPartnerLookupError] = useState('');
  /** Mensaje tras import OK (modal ya cerrado): "Carga exitosa, se crearon: N OV" */
  const [wholesaleImportBanner, setWholesaleImportBanner] = useState('');
  /** Mensaje de progreso mientras el job async está procesando en el servidor */
  const [wholesaleImportPollingMsg, setWholesaleImportPollingMsg] = useState('');
  const [bulkSelectedFileName, setBulkSelectedFileName] = useState('');
  /** Rejilla completa por hoja (cualquier estructura de Excel) */
  const [bulkSheetGrids, setBulkSheetGrids] = useState([]);
  const [bulkActiveSheetIndex, setBulkActiveSheetIndex] = useState(0);
  const [bulkParseError, setBulkParseError] = useState('');
  const [isBulkDragging, setIsBulkDragging] = useState(false);
  /** UI5 Select no sincroniza bien value="" tras opción elegida; al resetear se fuerza remount */
  const [bulkSelectRemountKey, setBulkSelectRemountKey] = useState(0);
  const [bulkSaleChannels, setBulkSaleChannels] = useState([]);
  const [bulkSaleChannelsLoading, setBulkSaleChannelsLoading] = useState(false);
  const [bulkSaleChannelsError, setBulkSaleChannelsError] = useState('');
  const [bulkBrandings, setBulkBrandings] = useState([]);
  const [bulkBrandingsLoading, setBulkBrandingsLoading] = useState(false);
  const [bulkBrandingsError, setBulkBrandingsError] = useState('');
  const [bulkShippingMethods, setBulkShippingMethods] = useState([]);
  const [bulkShippingMethodsLoading, setBulkShippingMethodsLoading] = useState(false);
  const [bulkShippingMethodsError, setBulkShippingMethodsError] = useState('');
  /** Mapa ítemCode → DTO SAP o `null` si falló; se llena al abrir Vista previa. */
  const [bulkSapItemsMap, setBulkSapItemsMap] = useState(() => ({}));
  /**
   * Perú (Falabella, Oechsle, Ripley): Substitute → ItemCode SAP.
   * `null` en el valor = el código no existe para el cliente.
   */
  const [bulkCatalogResolution, setBulkCatalogResolution] = useState(null);
  const [bulkSapItemsLoading, setBulkSapItemsLoading] = useState(false);
  const [bulkSapItemsProgress, setBulkSapItemsProgress] = useState({ done: 0, total: 0 });
  const [bulkSapItemsError, setBulkSapItemsError] = useState('');
  const [bulkExportDialogOpen, setBulkExportDialogOpen] = useState(false);
  const [sapExportDialogOpen, setSapExportDialogOpen] = useState(false);

  /** Falabella Chile entrega 2 archivos: el principal (eOD) y un eOC con `SKU + COSTO_UNI`. */
  const [bulkPriceFile, setBulkPriceFile] = useState(null);
  const [bulkPriceFileName, setBulkPriceFileName] = useState('');
  /** Mapa SKU → COSTO_UNI armado desde el eOC; `null` mientras no esté cargado/válido. */
  const [bulkPriceMap, setBulkPriceMap] = useState(null);
  /**
   * Líneas crudas del eOC (`{ sku, costo, cantidad, totDescent }`) usadas para validar
   * consistencia con el % de descuento del partner. Vacío cuando el eOC no aporta
   * `CANTIDAD_PROD` ni `TOT_DESCUENT` o no hay archivo cargado.
   */
  const [bulkPriceEocLines, setBulkPriceEocLines] = useState([]);
  /** Hojas parseadas del eOC para reescribirlo en exportar (marcado / filtrado por SKU). */
  const [bulkPriceSheetGrids, setBulkPriceSheetGrids] = useState([]);
  /** Índices del eOC: hoja con cabecera detectada, fila cabecera y columnas SKU / COSTO_UNI. */
  const [bulkPriceActiveSheetIndex, setBulkPriceActiveSheetIndex] = useState(-1);
  const [bulkPriceHeaderIndex, setBulkPriceHeaderIndex] = useState(-1);
  const [bulkPriceSkuColIndex, setBulkPriceSkuColIndex] = useState(-1);
  const [bulkPriceCostoColIndex, setBulkPriceCostoColIndex] = useState(-1);
  /** N° OC detectado en el eOC, para advertencia cruzada contra el eOD. */
  const [bulkPriceFileNroOc, setBulkPriceFileNroOc] = useState('');
  const [bulkPriceFileError, setBulkPriceFileError] = useState('');
  const [bulkPriceFileParsing, setBulkPriceFileParsing] = useState(false);
  const [isBulkPriceDragging, setIsBulkPriceDragging] = useState(false);

  const activeBulkGrid = useMemo(() => {
    const g = bulkSheetGrids[bulkActiveSheetIndex];
    return g || { name: '', rows: [], rowCount: 0, colCount: 0 };
  }, [bulkSheetGrids, bulkActiveSheetIndex]);

  /** Perfil + fila cabecera para la hoja activa (multi-formato: Falabella, Ripley, etc.). */
  const bulkPreviewMeta = useMemo(() => {
    if (!activeBulkGrid?.rows?.length) {
      return { profileId: BULK_FORMAT_DEFAULT_ID, headerIndex: 0 };
    }
    return analyzeGridForBulkPreview(activeBulkGrid);
  }, [activeBulkGrid]);

  /** Hay al menos una hoja con filas parseadas del archivo principal (evita tratar el perfil por defecto como detección real). */
  const hasBulkGridData =
    bulkSheetGrids.length > 0 && bulkSheetGrids.some((g) => g.rows && g.rows.length > 0);

  /** El Excel principal tiene formato Falabella Chile (eOD / líneas de orden). */
  const hasFalabellaChileOrderGrid =
    hasBulkGridData && bulkPreviewMeta.profileId === FALABELLA_CHILE_PROFILE_ID;
  const hasEocPriceMapReady = !!(bulkPriceMap && bulkPriceMap.size > 0);
  /**
   * Dos columnas eOD+eOC: cuando ya hay orden Falabella Chile en rejilla, o bien solo se cargó el eOC
   * (mapa de precios) y aún no hay rejilla de orden.
   */
  const showBulkFalabellaChileDualFileUi =
    hasFalabellaChileOrderGrid || (hasEocPriceMapReady && !hasBulkGridData);

  /** Excel IX Comercio o Multimarca: sin N° OC en archivo; cabecera no exige «Orden de compra cliente». */
  const isBulkIxComercioExcelFormat = isIxComercioProfileId(bulkPreviewMeta.profileId);
  const isBulkMultimarcaExcelFormat = isMultimarcaProfileId(bulkPreviewMeta.profileId);
  const isBulkWholesaleNoClientOcFormat =
    isBulkIxComercioExcelFormat || isBulkMultimarcaExcelFormat;

  /** Perú Ripley / Oechsle: 1 OV a bodega central; destino tienda por línea. */
  const bulkPeruCentralWarehousePreview = useMemo(
    () => showsPeruCentralWarehousePreview(bulkPreviewMeta.profileId),
    [bulkPreviewMeta.profileId]
  );

  const bulkLineDestinationColumnLabel = useMemo(
    () => getBulkLineDestinationLabel(bulkPreviewMeta.profileId, autoT),
    [bulkPreviewMeta.profileId, autoT]
  );

  const isBulkPeruWholesaleContextActive = useMemo(
    () =>
      isBulkPeruWholesaleContext({
        profileId: bulkPreviewMeta.profileId,
        customerRut: bulkParams.customerRut
      }),
    [bulkPreviewMeta.profileId, bulkParams.customerRut]
  );

  const showBulkDockersGenderSelect =
    isBulkPeruWholesaleContextActive && isDockersBrandName(bulkParams.brand);

  /** IX Comercio Perú no tiene descuento de negocio: sin descuento DOCKERS no se bloquea (se valida contra la lista sin descuento). */
  const isBulkIxComercioPeru =
    bulkPreviewMeta.profileId === IX_COMERCIO_PERU_PROFILE_ID ||
    (isIxComercioProfileId(bulkPreviewMeta.profileId) && isBulkPeruWholesaleContextActive);

  const bulkBrandDiscountResolveOptions = useMemo(
    () => ({
      dockersGender: bulkParams.dockersGender,
      isPeruContext: isBulkPeruWholesaleContextActive
    }),
    [bulkParams.dockersGender, isBulkPeruWholesaleContextActive]
  );

  /**
   * Lista de precios SAP del cliente: `PriceListNum` del socio de negocio SAP (no depende del canal de venta).
   * `null` ⇒ cliente aún no resuelto o sin lista asignada: bloquea validación y carga (sin lista por defecto).
   */
  const bulkSapPriceListId = useMemo(
    () => resolvePartnerPriceListNum(bulkSapPartnerForConfirm),
    [bulkSapPartnerForConfirm]
  );

  /** Aviso cuando el cliente ya está resuelto en SAP pero no tiene lista de precios asignada. */
  const bulkSapPriceListError = useMemo(() => {
    if (!bulkSapPartnerForConfirm) return '';
    if (bulkSapPriceListId != null) return '';
    return autoT(
      'El cliente no tiene lista de precios asignada en SAP (PriceListNum). No se puede validar ni cargar.'
    );
  }, [bulkSapPartnerForConfirm, bulkSapPriceListId, autoT]);

  /**
   * Descuento de marca del partner SAP que se aplica al `COSTO_UNI` del eOC en Falabella Chile
   * (sin efecto en otros perfiles, donde `priceMap` es `null`). Solo tras canal válido y partner resuelto.
   */
  const bulkBrandDiscountPercent = useMemo(() => {
    if (!bulkParams.salesChannel || bulkSapPriceListId == null) return 0;
    return resolveBulkBrandDiscount(
      bulkSapPartnerForConfirm,
      bulkParams.brand,
      bulkBrandDiscountResolveOptions
    );
  }, [
    bulkParams.salesChannel,
    bulkSapPriceListId,
    bulkSapPartnerForConfirm,
    bulkParams.brand,
    bulkBrandDiscountResolveOptions
  ]);

  /** Perú + DOCKERS: ítems SAP solo tras línea Hombre/Mujer y descuento > 0. */
  const bulkPeruDockersSapItemsReady = useMemo(
    () =>
      isBulkPeruDockersSapItemsFetchReady({
        isPeruContext: isBulkPeruWholesaleContextActive,
        brand: bulkParams.brand,
        dockersGender: bulkParams.dockersGender,
        brandDiscountPercent: bulkBrandDiscountPercent,
        discountOptional: isBulkIxComercioPeru
      }),
    [
      isBulkPeruWholesaleContextActive,
      bulkParams.brand,
      bulkParams.dockersGender,
      bulkBrandDiscountPercent,
      isBulkIxComercioPeru
    ]
  );

  const bulkPeruDockersHeaderLocked = showBulkDockersGenderSelect && bulkSapItemsLoading;

  /** Cabecera lista para validación SAP en vista previa (canal + partner). */
  const bulkSapValidationReady =
    bulkSapPriceListId != null && Boolean(bulkSapPartnerForConfirm);

  /** Validación + mapeo según cabecera detectada en la hoja activa */
  const bulkInterpreted = useMemo(
    () =>
      buildValidatedRowsFromGrid(activeBulkGrid, autoT, {
        profileId: bulkPreviewMeta.profileId,
        headerIndex: bulkPreviewMeta.headerIndex,
        priceMap: bulkPriceMap,
        brandDiscountPercent: bulkBrandDiscountPercent
      }),
    [activeBulkGrid, autoT, bulkPreviewMeta, bulkPriceMap, bulkBrandDiscountPercent]
  );
  const bulkPreviewRowsFromExcel = bulkInterpreted.rows;

  /** RUT leído del Excel Multimarca (columna RutCliente) para la cabecera de vista previa. */
  const bulkMultimarcaExcelRut = useMemo(() => {
    if (!isBulkMultimarcaExcelFormat) return '';
    return extractMultimarcaRutClienteFromGrid(activeBulkGrid, bulkPreviewMeta.headerIndex);
  }, [isBulkMultimarcaExcelFormat, activeBulkGrid, bulkPreviewMeta.headerIndex]);

  /** N° OC del eOD (primer valor no vacío) para validación cruzada con `bulkPriceFileNroOc` del eOC. */
  const bulkEodNroOc = useMemo(() => {
    for (const r of bulkPreviewRowsFromExcel) {
      const v = String(r?.nOc || '').trim();
      if (v) return v;
    }
    return '';
  }, [bulkPreviewRowsFromExcel]);

  /** Advertencia (no bloquea): el N° OC del eOC no coincide con el del eOD. */
  const bulkPriceFileNroOcMismatch =
    hasFalabellaChileOrderGrid &&
    !!bulkPriceFileNroOc &&
    !!bulkEodNroOc &&
    bulkPriceFileNroOc !== bulkEodNroOc;

  /**
   * Falabella Chile: validación cruzada del % de descuento del partner SAP contra `TOT_DESCUENT`
   * del eOC. Solo se ejecuta cuando hay descuento positivo, eOC con líneas válidas y perfil Falabella.
   * No bloquea la carga; se muestra como advertencia listando los primeros mismatches.
   */
  const bulkEocDiscountConsistency = useMemo(() => {
    if (!hasFalabellaChileOrderGrid || !hasEocPriceMapReady) {
      return { valid: true, mismatches: [], checked: 0 };
    }
    if (!bulkBrandDiscountPercent || bulkBrandDiscountPercent <= 0) {
      return { valid: true, mismatches: [], checked: 0 };
    }
    return validateEocDiscountConsistency(bulkPriceEocLines, bulkBrandDiscountPercent);
  }, [
    hasFalabellaChileOrderGrid,
    hasEocPriceMapReady,
    bulkBrandDiscountPercent,
    bulkPriceEocLines
  ]);

  /** Falabella Chile: faltan eOD o eOC para cerrar la pareja (orden independiente del orden de carga). */
  const isBulkFalabellaChileDualIncomplete =
    showBulkFalabellaChileDualFileUi &&
    (!hasFalabellaChileOrderGrid || !hasEocPriceMapReady);

  /** Bodega del usuario en sesión (localStorage); al abrir carga mayorista o vista previa se relee. */
  const bulkSessionWarehouse = useMemo(
    () => ({ code: getUserWhsCode(), name: getUserWhsName() }),
    [isBulkDialogOpen, isBulkPreviewOpen]
  );

  /** Id de usuario en sesión (UUID); misma ventana de refresco que bodega. */
  const bulkSessionUserId = useMemo(
    () => getLoggedInUserId(),
    [isBulkDialogOpen, isBulkPreviewOpen]
  );

  /**
   * Filas para firma de códigos SAP: `autoT` no afecta `skuIac`; deps estables evitan re-ejecutar
   * el efecto de fetch SAP en cada render (carga perpetua).
   */
  const bulkRowsForSapFetch = useMemo(() => {
    if (!activeBulkGrid?.rows?.length) return [];
    return buildValidatedRowsFromGrid(activeBulkGrid, (s) => s, {
      profileId: bulkPreviewMeta.profileId,
      headerIndex: bulkPreviewMeta.headerIndex,
      priceMap: bulkPriceMap,
      brandDiscountPercent: bulkBrandDiscountPercent
    }).rows;
  }, [
    activeBulkGrid,
    bulkPreviewMeta.profileId,
    bulkPreviewMeta.headerIndex,
    bulkPriceMap,
    bulkBrandDiscountPercent
  ]);

  const bulkPreviewRows = useMemo(() => {
    const rowsForSap = usesCustomerCatalogSubstitute(bulkPreviewMeta.profileId)
      ? applyCustomerCatalogResolution(bulkPreviewRowsFromExcel, bulkCatalogResolution)
      : bulkPreviewRowsFromExcel;
    if (isBulkPreviewOpen && bulkSapItemsLoading) return bulkPreviewRowsFromExcel;
    if (isBulkPreviewOpen && !bulkSapItemsLoading && bulkSapValidationReady) {
      return enrichBulkPreviewRowsWithSapItems(
        rowsForSap,
        bulkSapItemsMap || {},
        autoT,
        bulkPreviewMeta.profileId,
        {
          sapWarehouseCode: bulkSessionWarehouse.code,
          brandDiscountPercent: bulkBrandDiscountPercent,
          sapPriceListId: bulkSapPriceListId ?? undefined
        }
      );
    }
    return bulkPreviewRowsFromExcel;
  }, [
    isBulkPreviewOpen,
    bulkSapItemsLoading,
    bulkSapValidationReady,
    bulkSapItemsMap,
    bulkCatalogResolution,
    bulkPreviewRowsFromExcel,
    autoT,
    bulkPreviewMeta.profileId,
    bulkSessionWarehouse.code,
    bulkBrandDiscountPercent,
    bulkSapPriceListId
  ]);

  /** Filas de detalle del Excel agrupadas por tienda/cliente (columna homónima a la lista OV). */
  const bulkPreviewGroupedByClient = useMemo(
    () => groupBulkPreviewRowsByClient(bulkPreviewRows),
    [bulkPreviewRows]
  );

  const [bulkPreviewGroupPage, setBulkPreviewGroupPage] = useState(1);

  // Vuelve a la primera página de grupos si cambia el archivo, la hoja activa o el número de grupos.
  useEffect(() => {
    setBulkPreviewGroupPage(1);
  }, [bulkPreviewGroupedByClient.length, bulkSelectedFileName, bulkActiveSheetIndex]);

  /** Al abrir Carga Mayorista: lista completa de canales (`sale-channels`, paginado por totalCount). */
  useEffect(() => {
    if (!isBulkDialogOpen) return undefined;

    const ac = new AbortController();
    setBulkSaleChannelsLoading(true);
    setBulkSaleChannelsError('');
    setBulkSaleChannels([]);

    (async () => {
      try {
        const list = await fetchAllSaleChannels({ signal: ac.signal });
        if (!ac.signal.aborted) setBulkSaleChannels(list);
      } catch (e) {
        const canceled =
          ac.signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
        if (!canceled) {
          setBulkSaleChannels([]);
          setBulkSaleChannelsError(String(e?.message || 'Error al cargar canales de venta.'));
        }
      } finally {
        if (!ac.signal.aborted) setBulkSaleChannelsLoading(false);
      }
    })();

    return () => ac.abort();
  }, [isBulkDialogOpen]);

  /** Al abrir Carga Mayorista: lista completa de marcas (`brandings`, paginado por totalCount). */
  useEffect(() => {
    if (!isBulkDialogOpen) return undefined;

    const ac = new AbortController();
    setBulkBrandingsLoading(true);
    setBulkBrandingsError('');
    setBulkBrandings([]);

    (async () => {
      try {
        const list = await fetchAllBrandings({ signal: ac.signal });
        if (!ac.signal.aborted) setBulkBrandings(list);
      } catch (e) {
        const canceled =
          ac.signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
        if (!canceled) {
          setBulkBrandings([]);
          setBulkBrandingsError(String(e?.message || 'Error al cargar marcas.'));
        }
      } finally {
        if (!ac.signal.aborted) setBulkBrandingsLoading(false);
      }
    })();

    return () => ac.abort();
  }, [isBulkDialogOpen]);

  /** Al abrir Carga Mayorista: catálogo de métodos de envío (`shipping-methods`, paginado por totalCount). */
  useEffect(() => {
    if (!isBulkDialogOpen) return undefined;

    const ac = new AbortController();
    setBulkShippingMethodsLoading(true);
    setBulkShippingMethodsError('');
    setBulkShippingMethods([]);

    (async () => {
      try {
        const list = await fetchAllShippingMethods({ signal: ac.signal });
        if (!ac.signal.aborted) setBulkShippingMethods(list);
      } catch (e) {
        const canceled =
          ac.signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
        if (!canceled) {
          setBulkShippingMethods([]);
          setBulkShippingMethodsError(String(e?.message || 'Error al cargar métodos de envío.'));
        }
      } finally {
        if (!ac.signal.aborted) setBulkShippingMethodsLoading(false);
      }
    })();

    return () => ac.abort();
  }, [isBulkDialogOpen]);

  /**
   * Con archivo parseado y **cliente SAP ya validado** en Carga Mayorista: consulta ítems SAP
   * vía `fetchSapB1PreviewItemsMap` (POST `items/query` en tandas si
   * `REACT_APP_SAP_B1_ITEMS_BATCH_ENABLED=true`; si no, GET unitarios en tandas de 4).
   *
   * Orden del flujo: primero se consulta el BusinessPartner (RUT + marca). Solo cuando el cliente
   * fue encontrado correctamente (`bulkSapPartnerForConfirm` truthy y sin error) recién acá se
   * disparan los ítems. Si el cliente todavía no fue validado, o cambia RUT/marca, se limpia el
   * mapa para que la vista previa no muestre datos viejos.
   *
   * Misma clave archivo+hoja+códigos → reutiliza caché (no vuelve a llamar la API).
   * La vista previa reutiliza el mismo mapa (`bulkSapItemsMap`).
   */
  useEffect(() => {
    if (!isBulkDialogOpen || !hasBulkGridData) return undefined;

    /**
     * Gate: la consulta de ítems SAP solo arranca cuando el cliente fue resuelto correctamente.
     * Si todavía está cargando, hubo error, o aún no se buscó, se limpia el mapa de ítems para
     * que `enrichBulkPreviewRowsWithSapItems` no reuse datos del cliente anterior.
     */
    if (bulkSapPartnerLoading || bulkSapPartnerLookupError || !bulkSapPartnerForConfirm) {
      bulkSapPreviewFetchAbortRef.current?.abort();
      bulkSapItemsFetchCacheRef.current = { key: '', map: {}, resolution: null };
      setBulkSapItemsMap({});
      setBulkCatalogResolution(null);
      setBulkSapItemsLoading(false);
      setBulkSapItemsProgress({ done: 0, total: 0 });
      setBulkSapItemsError('');
      return undefined;
    }

    /**
     * Perú + DOCKERS: no consultar ítems hasta elegir línea y tener descuento SAP > 0.
     */
    if (!bulkPeruDockersSapItemsReady) {
      bulkSapPreviewFetchAbortRef.current?.abort();
      bulkSapItemsFetchCacheRef.current = { key: '', map: {}, resolution: null };
      setBulkSapItemsMap({});
      setBulkCatalogResolution(null);
      setBulkSapItemsLoading(false);
      setBulkSapItemsProgress({ done: 0, total: 0 });
      setBulkSapItemsError('');
      return undefined;
    }

    /**
     * Canal sin lista de precios SAP válida (nombre sin número al final): no se consultan ítems;
     * la validación de precio quedaría sin base. El usuario debe configurar el canal primero.
     */
    if (bulkSapPriceListId == null) {
      bulkSapPreviewFetchAbortRef.current?.abort();
      bulkSapItemsFetchCacheRef.current = { key: '', map: {}, resolution: null };
      setBulkSapItemsMap({});
      setBulkCatalogResolution(null);
      setBulkSapItemsLoading(false);
      setBulkSapItemsProgress({ done: 0, total: 0 });
      setBulkSapItemsError('');
      return undefined;
    }

    const rows = bulkRowsForSapFetch;
    const useCatalog = usesCustomerCatalogSubstitute(bulkPreviewMeta.profileId);
    const cardCode = String(
      bulkSapPartnerForConfirm?.CardCode || bulkSapPartnerForConfirm?.cardCode || ''
    ).trim();
    const uniqueSorted = [
      ...new Set(
        rows
          .map((r) => normalizeSapItemCodeKey(useCatalog ? r.catalogSubstitute : r.skuIac))
          .filter(Boolean)
      )
    ].sort();

    if (useCatalog && uniqueSorted.length > 0 && !cardCode) {
      bulkSapPreviewFetchAbortRef.current?.abort();
      setBulkSapItemsMap({});
      setBulkCatalogResolution(null);
      setBulkSapItemsLoading(false);
      setBulkSapItemsProgress({ done: 0, total: 0 });
      setBulkSapItemsError(autoTRef.current('El cliente no tiene CardCode en SAP.'));
      return undefined;
    }

    const fileFingerprint = bulkUploadFile
      ? `${bulkUploadFile.name}\x00${bulkUploadFile.lastModified}\x00${bulkUploadFile.size}`
      : `${bulkSelectedFileName}\x000\x000`;
    /** Falabella Chile: cambiar el eOC reescribe `precioAcordado` y, por tanto, las validaciones; la caché debe invalidarse. */
    const priceFingerprint = bulkPriceFile
      ? `${bulkPriceFile.name}\x00${bulkPriceFile.lastModified}\x00${bulkPriceFile.size}`
      : '\x000\x000';
    const peruDockersCacheFragment = showBulkDockersGenderSelect
      ? `${bulkParams.dockersGender}\x00${bulkBrandDiscountPercent}`
      : '';
    const sapCacheKey = `${useCatalog ? 'cat' : 'item'}\x00${cardCode}\x00${fileFingerprint}\x00${priceFingerprint}\x00${bulkActiveSheetIndex}\x00${peruDockersCacheFragment}\x00${uniqueSorted.join('\x1e')}`;

    const cache = bulkSapItemsFetchCacheRef.current;
    const cacheHit = cache.key === sapCacheKey && uniqueSorted.length > 0 && cache.map;

    bulkSapPreviewFetchAbortRef.current?.abort();
    const ac = new AbortController();
    bulkSapPreviewFetchAbortRef.current = ac;

    if (uniqueSorted.length === 0) {
      setBulkSapItemsMap({});
      setBulkCatalogResolution(null);
      setBulkSapItemsLoading(false);
      setBulkSapItemsProgress({ done: 0, total: 0 });
      setBulkSapItemsError('');
      return () => {
        ac.abort();
      };
    }

    if (cacheHit) {
      setBulkSapItemsLoading(false);
      setBulkSapItemsProgress({ done: 0, total: 0 });
      setBulkSapItemsError('');
      setBulkSapItemsMap({ ...cache.map });
      setBulkCatalogResolution(cache.resolution ?? null);
      return () => {
        ac.abort();
      };
    }

    setBulkSapItemsLoading(true);
    setBulkSapItemsProgress({ done: 0, total: uniqueSorted.length });
    setBulkSapItemsError('');
    setBulkSapItemsMap({});
    setBulkCatalogResolution(null);

    (async () => {
      try {
        let resolution = null;
        let codesForItems = uniqueSorted;
        if (useCatalog) {
          const entries = await fetchSapB1AlternateCatalogInWaves(cardCode, uniqueSorted, {
            signal: ac.signal,
            onProgress: ({ done, total }) => {
              if (!ac.signal.aborted) setBulkSapItemsProgress({ done, total });
            }
          });
          if (ac.signal.aborted) return;
          resolution = buildCatalogResolution(uniqueSorted, entries);
          codesForItems = [
            ...new Set(
              Object.values(resolution)
                .map((hit) => (hit && hit.itemCode ? hit.itemCode : ''))
                .filter(Boolean)
            )
          ];
        }
        const { map } = codesForItems.length
          ? await fetchSapB1PreviewItemsMap(codesForItems, {
              signal: ac.signal,
              debugSampleCap: 40,
              onProgress: ({ done, total }) => {
                if (!ac.signal.aborted) setBulkSapItemsProgress({ done, total });
              }
            })
          : { map: {} };
        if (resolution) {
          for (const [sub, hit] of Object.entries(resolution)) {
            if (!hit || hit.ambiguous) map[sub] = null;
          }
        }
        if (!ac.signal.aborted) {
          setBulkCatalogResolution(resolution);
          setBulkSapItemsMap({ ...map });
          bulkSapItemsFetchCacheRef.current = {
            key: sapCacheKey,
            map: { ...map },
            resolution
          };
        }
      } catch (e) {
        const canceled =
          ac.signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
        if (!canceled) {
          setBulkSapItemsError(String(e?.message || 'Error al consultar ítems SAP.'));
          setBulkCatalogResolution(null);
          setBulkSapItemsMap(
            useCatalog ? {} : Object.fromEntries(uniqueSorted.map((c) => [c, null]))
          );
        }
      } finally {
        /** Siempre quitar loading (p. ej. Strict Mode aborta el controller y antes quedaba colgado “Consultando SAP…”). */
        setBulkSapItemsLoading(false);
        setBulkSapItemsProgress({ done: 0, total: 0 });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [
    isBulkDialogOpen,
    hasBulkGridData,
    bulkRowsForSapFetch,
    bulkSelectedFileName,
    bulkActiveSheetIndex,
    bulkUploadFile,
    bulkPriceFile,
    bulkSapPartnerForConfirm,
    bulkSapPartnerLookupError,
    bulkSapPartnerLoading,
    bulkPeruDockersSapItemsReady,
    bulkSapPriceListId,
    bulkPreviewMeta.profileId,
    showBulkDockersGenderSelect,
    bulkParams.dockersGender,
    bulkBrandDiscountPercent
  ]);

  /**
   * Socio SAP: tras canal de venta válido (lista SAP resoluble), RUT + marca y Excel cargado.
   * Dispara GET BusinessPartner; la consulta de ítems SAP recién corre después (ver efecto de ítems).
   * Nueva GET solo si cambia RUT, marca, canal o el/los Excel.
   * Debounce breve para no martillar la API mientras se edita el RUT.
   * No incluir `autoT` en deps (nueva función cada render → bucle infinito de GET).
   */
  useEffect(() => {
    if (!isBulkDialogOpen || !bulkUploadFile || !hasBulkGridData) return undefined;

    /** Falabella: hasta tener eOD + mapa eOC no se consulta el socio (evita GET por cada archivo suelto). */
    if (isBulkFalabellaChileDualIncomplete) {
      setBulkSapPartnerForConfirm(null);
      setBulkSapPartnerLookupError('');
      setBulkSapPartnerLoading(false);
      bulkSapPartnerFetchSuccessKeyRef.current = '';
      return undefined;
    }

    const rutRaw = String(bulkParams.customerRut || '').trim();
    const brand = String(bulkParams.brand || '').trim();
    if (!rutRaw || !brand) {
      setBulkSapPartnerForConfirm(null);
      setBulkSapPartnerLookupError('');
      setBulkSapPartnerLoading(false);
      bulkSapPartnerFetchSuccessKeyRef.current = '';
      return undefined;
    }

    /**
     * Sin canal elegido: no consultar partner hasta que el usuario elija canal. La lista de precios ya no
     * condiciona la consulta: sale del propio partner (`PriceListNum`).
     */
    if (!bulkParams.salesChannel || bulkSaleChannelsLoading) {
      setBulkSapPartnerForConfirm(null);
      setBulkSapPartnerLookupError('');
      setBulkSapPartnerLoading(false);
      bulkSapPartnerFetchSuccessKeyRef.current = '';
      return undefined;
    }

    const orderFileFingerprint = `${bulkUploadFile.name}\x00${bulkUploadFile.lastModified}\x00${bulkUploadFile.size}`;
    const priceFileFingerprint =
      bulkPriceFile != null
        ? `${bulkPriceFile.name}\x00${bulkPriceFile.lastModified}\x00${bulkPriceFile.size}`
        : '';
    /** Ripley/uno solo: huella del orden; Falabella pareja completa: eOD + eOC. */
    const bulkPartnerFilesKey =
      hasFalabellaChileOrderGrid && hasEocPriceMapReady
        ? `${orderFileFingerprint}\x00${priceFileFingerprint}`
        : orderFileFingerprint;

    const normalizedPreview = normalizeFederalTaxIdForQuery(rutRaw);
    const fetchKey =
      normalizedPreview && brand ? `${normalizedPreview}\x00${brand}\x00${bulkPartnerFilesKey}` : '';

    if (fetchKey && bulkSapPartnerFetchSuccessKeyRef.current === fetchKey) {
      return undefined;
    }

    let canceled = false;
    const tid = window.setTimeout(async () => {
      const normalizedRut = normalizeFederalTaxIdForQuery(rutRaw);
      if (!normalizedRut) {
        if (!canceled) {
          setBulkSapPartnerForConfirm(null);
          setBulkSapPartnerLookupError(autoTRef.current('RUT inválido'));
          setBulkSapPartnerLoading(false);
          bulkSapPartnerFetchSuccessKeyRef.current = '';
        }
        return;
      }
      const dedupeKey = `${normalizedRut}\x00${brand}\x00${bulkPartnerFilesKey}`;
      if (bulkSapPartnerFetchSuccessKeyRef.current === dedupeKey) {
        if (!canceled) setBulkSapPartnerLoading(false);
        return;
      }
      if (!canceled) {
        setBulkSapPartnerLoading(true);
        setBulkSapPartnerLookupError('');
      }
      try {
        const partner = await fetchSapB1BusinessPartnerByFederalTaxId(rutRaw);
        if (canceled) return;
        if (partner == null) {
          setBulkSapPartnerForConfirm(null);
          bulkSapPartnerFetchSuccessKeyRef.current = '';
          setBulkSapPartnerLookupError(autoTRef.current('RUT inválido'));
        } else {
          setBulkSapPartnerForConfirm(partner);
          bulkSapPartnerFetchSuccessKeyRef.current = dedupeKey;
          setBulkSapPartnerLookupError('');
        }
      } catch (err) {
        if (canceled) return;
        bulkSapPartnerFetchSuccessKeyRef.current = '';
        const data = err?.response?.data;
        let msg =
          (typeof data === 'string' && data) ||
          data?.message ||
          (Array.isArray(data?.errors) && data.errors.join(', ')) ||
          err?.message ||
          '';
        if (msg && typeof msg === 'object') {
          try {
            msg = JSON.stringify(msg);
          } catch {
            msg = '';
          }
        }
        setBulkSapPartnerForConfirm(null);
        setBulkSapPartnerLookupError(String(msg || autoTRef.current('Error al validar el cliente en SAP.')));
      } finally {
        if (!canceled) setBulkSapPartnerLoading(false);
      }
    }, 400);

    return () => {
      canceled = true;
      window.clearTimeout(tid);
    };
  }, [
    isBulkDialogOpen,
    bulkUploadFile,
    bulkPriceFile,
    hasBulkGridData,
    hasFalabellaChileOrderGrid,
    hasEocPriceMapReady,
    isBulkFalabellaChileDualIncomplete,
    bulkParams.customerRut,
    bulkParams.brand,
    bulkParams.salesChannel,
    bulkSaleChannelsLoading
  ]);

  /**
   * Sincroniza el campo informativo "Desc. negocio (%)" con el porcentaje
   * resuelto desde `DiscountBrand` del socio SAP y la marca elegida en la cabecera.
   * Sin canal, lista inválida o sin socio el valor queda en "0".
   */
  useEffect(() => {
    if (!bulkParams.salesChannel || bulkSapPriceListId == null || !bulkSapPartnerForConfirm) {
      setBulkParams((p) => (p.businessDiscount === '0' ? p : { ...p, businessDiscount: '0' }));
      return;
    }
    const pct = resolveBulkBrandDiscount(
      bulkSapPartnerForConfirm,
      bulkParams.brand,
      bulkBrandDiscountResolveOptions
    );
    const next = String(pct);
    setBulkParams((p) => (p.businessDiscount === next ? p : { ...p, businessDiscount: next }));
  }, [
    bulkSapPartnerForConfirm,
    bulkParams.brand,
    bulkParams.salesChannel,
    bulkSapPriceListId,
    bulkBrandDiscountResolveOptions
  ]);

  const bulkPreviewGroupTotalPages = Math.max(
    1,
    Math.ceil(bulkPreviewGroupedByClient.length / BULK_PREVIEW_GROUPS_PAGE_SIZE)
  );

  /** Subconjunto de grupos mostrados en la tabla resumida de la vista previa. */
  const bulkPreviewGroupedPageSlice = useMemo(() => {
    const start = (bulkPreviewGroupPage - 1) * BULK_PREVIEW_GROUPS_PAGE_SIZE;
    return bulkPreviewGroupedByClient.slice(start, start + BULK_PREVIEW_GROUPS_PAGE_SIZE);
  }, [bulkPreviewGroupedByClient, bulkPreviewGroupPage]);

  const bulkCurrencyInfo = useMemo(
    () => getBulkFormatCurrency(bulkPreviewMeta.profileId),
    [bulkPreviewMeta.profileId]
  );

  const formatBulkMoney = useCallback(
    (n) => formatBulkMoneyAmount(n, bulkPreviewMeta.profileId),
    [bulkPreviewMeta.profileId]
  );

  /** Grupo de tienda con fila de detalle expandida (solo uno a la vez). */
  const [expandedBulkGroupId, setExpandedBulkGroupId] = useState(null);

  useEffect(() => {
    setExpandedBulkGroupId(null);
  }, [bulkPreviewGroupPage]);

  const pill = (text, bg, color, compact, pillExtra = {}) => (
    <span
      style={{
        display: 'inline-block',
        background: bg,
        color,
        padding: compact ? '1px 6px' : '2px 10px',
        borderRadius: compact ? 6 : 12,
        fontSize: compact ? '0.72rem' : '0.8rem',
        fontWeight: 600,
        lineHeight: compact ? 1.25 : 1.35,
        maxWidth: compact ? 'min(100%, 28rem)' : undefined,
        wordBreak: compact ? 'break-word' : undefined,
        verticalAlign: compact ? 'middle' : undefined,
        boxSizing: 'border-box',
        ...pillExtra
      }}
    >
      {text}
    </span>
  );

  /**
   * Columna Estado: primero cancelada (`status` API); luego DocNum SAP vs «creada».
   */
  const statusPill = (row) => {
    const kind = getSalesOrderListStatusKind(row);
    const stCanceled = getStatusStyle('Canceled');
    const stPending = getStatusStyle('Pending');
    const stShipped = getStatusStyle('Shipped');
    const stDelivered = getStatusStyle('Delivered');
    const stPartial = getStatusStyle('PartiallyShipped');
    const stClosed = getStatusStyle('Closed');
    const stReturned = getStatusStyle('Returned');
    if (kind === 'canceled') {
      return pill(autoT('Cancelada'), stCanceled.backgroundColor, stCanceled.color, false, {
        border: stCanceled.border
      });
    }
    if (kind === 'shipped') {
      return pill(autoT('Despachada'), stDelivered.backgroundColor, stDelivered.color, false, {
        border: stDelivered.border
      });
    }
    if (kind === 'partially_shipped') {
      return pill(autoT('Despacho Parcial'), stPartial.backgroundColor, stPartial.color, false, {
        border: stPartial.border
      });
    }
    if (kind === 'closed') {
      return pill(autoT('Cerrado'), stClosed.backgroundColor, stClosed.color, false, {
        border: stClosed.border
      });
    }
    if (kind === 'returned') {
      return pill(autoT('Devuelta'), stReturned.backgroundColor, stReturned.color, false, {
        border: stReturned.border
      });
    }
    const docNum = getSalesOrderSapDocNumForDisplay(row);
    const label =
      kind === 'sent_sap'
        ? autoT('Enviada a SAP: DocNum {{doc}}', { doc: docNum })
        : autoT('creada');
    const st = kind === 'sent_sap' ? stShipped : stPending;
    return pill(label, st.backgroundColor, st.color, false, { border: st.border });
  };

  const navigateToSalesOrderDetail = useCallback(
    (row) => {
      const idStr = String(row.id ?? '').trim();
      const onStr = String(row.orderNumber ?? '').trim();
      const key = isSalesOrderUuidKey(idStr) ? idStr : onStr || idStr;
      if (key === '') return;
      const path = `/sales-order-detail/${encodeURIComponent(String(key))}`;
      const ocUnits = row.totalUnitsByOcReference;
      if (ocUnits != null && Number.isFinite(Number(ocUnits))) {
        navigate(path, { state: { totalUnitsByOcReference: Number(ocUnits) } });
      } else {
        navigate(path);
      }
    },
    [navigate]
  );

  const openBulkDialog = () => {
    setBulkUploadError('');
    setBulkUploadSuccessMessage('');
    // Oculta el aviso verde de la última importación al iniciar una carga nueva.
    setWholesaleImportBanner('');
    setIsBulkDialogOpen(true);
  };
  const closeBulkDialog = () => setIsBulkDialogOpen(false);

  /** Limpia cabecera, archivo en memoria y el input file al salir de Carga Mayorista */
  const resetBulkMayoristaState = useCallback(() => {
    setBulkParams({
      customerRut: '',
      salesChannel: '',
      customerOrderNumber: '',
      brand: '',
      orderType: '',
      businessDiscount: '',
      shippingMethod: '',
      dockersGender: ''
    });
    setBulkUploadFile(null);
    setBulkUploading(false);
    setBulkFileParsing(false);
    setBulkStockAllocationNotice('');
    setBulkUploadError('');
    setBulkUploadSuccessMessage('');
    setBulkSelectedFileName('');
    setBulkSheetGrids([]);
    setBulkActiveSheetIndex(0);
    setBulkParseError('');
    setIsBulkDragging(false);
    setBulkSelectRemountKey((k) => k + 1);
    setBulkSapPartnerForConfirm(null);
    bulkSapPartnerFetchSuccessKeyRef.current = '';
    setBulkSapPartnerLoading(false);
    setBulkSapPartnerLookupError('');
    bulkSapPreviewFetchAbortRef.current?.abort();
    bulkSapItemsFetchCacheRef.current = { key: '', map: {} };
    setBulkSapItemsMap({});
    setBulkSapItemsLoading(false);
    setBulkSapItemsProgress({ done: 0, total: 0 });
    setBulkSapItemsError('');
    setBulkPriceFile(null);
    setBulkPriceFileName('');
    setBulkPriceMap(null);
    setBulkPriceEocLines([]);
    setBulkPriceSheetGrids([]);
    setBulkPriceActiveSheetIndex(-1);
    setBulkPriceHeaderIndex(-1);
    setBulkPriceSkuColIndex(-1);
    setBulkPriceCostoColIndex(-1);
    setBulkPriceFileNroOc('');
    setBulkPriceFileError('');
    setBulkPriceFileParsing(false);
    setIsBulkPriceDragging(false);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
    if (bulkPriceFileInputRef.current) bulkPriceFileInputRef.current.value = '';
  }, []);

  const handleBulkDialogAfterClose = useCallback(() => {
    resetBulkMayoristaState();
    setIsBulkDialogOpen(false);
  }, [resetBulkMayoristaState]);

  const openBulkPreview = () => setIsBulkPreviewOpen(true);
  const closeBulkPreview = () => {
    setExpandedBulkGroupId(null);
    setBulkStockAllocationNotice('');
    setIsBulkPreviewOpen(false);
  };

  const bulkPreviewNestedThStyle = {
    padding: '6px 8px',
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'left',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  };
  const bulkPreviewNestedTdStyle = {
    padding: '6px 8px',
    borderBottom: '1px solid #e5e5e5',
    verticalAlign: 'top'
  };

  /** Misma altura y padding en las dos columnas eOD / eOC (Falabella Chile). */
  const bulkFalabellaDualCardStyle = {
    flex: '1 1 320px',
    minWidth: 260,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  };
  const bulkFalabellaDualCardBodyStyle = {
    padding: '1rem',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 360
  };
  const bulkFalabellaDualDropBase = {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    boxSizing: 'border-box',
    borderRadius: 10,
    padding: '2.25rem 1rem',
    textAlign: 'center',
    cursor: 'pointer'
  };

  const finalizeBulkOrderFromGrids = useCallback((file, grids) => {
    const best = pickBestGridForBulkPreview(grids);
    bulkSapItemsFetchCacheRef.current = { key: '', map: {} };
    setBulkSheetGrids(grids);
    setBulkActiveSheetIndex(best.gridIndex);
    setBulkUploadFile(file);
    setBulkSelectedFileName(file.name);
    /**
     * Excel nuevo: limpiar la marca para obligar al usuario a re-seleccionarla.
     * Evita que la consulta del partner se dispare al instante con datos del Excel anterior.
     * Al quedar `brand` vacío, el efecto del partner limpia resumen / error / partner;
     * y el efecto de items, al perder el partner validado, limpia `bulkSapItemsMap`.
     */
    setBulkParams((p) => (p.brand ? { ...p, brand: '' } : p));
  }, []);

  /**
   * @param {File} file
   * @param {object[]} grids
   * @param {{ priceMap: Map<string, number>, gridIndex?: number, headerIndex?: number, skuCol?: number, costoCol?: number, nroOcDetected?: string, totalSkus?: number, eocLines?: { sku: string, costo: number, cantidad: number|null, totDescent: number|null }[] }|undefined} prebuilt - resultado de `buildPriceMapFromEocGrids` para evitar duplicar trabajo.
   */
  const finalizeBulkPriceFromGrids = useCallback((file, grids, prebuilt) => {
    setBulkPriceFileName(file.name);
    const built = prebuilt || buildPriceMapFromEocGrids(grids);
    const { priceMap, gridIndex, headerIndex, skuCol, costoCol, nroOcDetected, totalSkus, eocLines } = built;
    if (!priceMap || priceMap.size === 0) {
      setBulkPriceFile(null);
      setBulkPriceMap(null);
      setBulkPriceEocLines([]);
      setBulkPriceSheetGrids([]);
      setBulkPriceActiveSheetIndex(-1);
      setBulkPriceHeaderIndex(-1);
      setBulkPriceSkuColIndex(-1);
      setBulkPriceCostoColIndex(-1);
      setBulkPriceFileNroOc('');
      setBulkPriceFileError(
        autoT('El archivo de precios no contiene columnas válidas (SKU + COSTO_UNI).')
      );
      return;
    }
    bulkSapItemsFetchCacheRef.current = { key: '', map: {} };
    setBulkPriceFile(file);
    setBulkPriceMap(priceMap);
    setBulkPriceEocLines(Array.isArray(eocLines) ? eocLines : []);
    setBulkPriceSheetGrids(Array.isArray(grids) ? grids : []);
    setBulkPriceActiveSheetIndex(typeof gridIndex === 'number' ? gridIndex : -1);
    setBulkPriceHeaderIndex(typeof headerIndex === 'number' ? headerIndex : -1);
    setBulkPriceSkuColIndex(typeof skuCol === 'number' ? skuCol : -1);
    setBulkPriceCostoColIndex(typeof costoCol === 'number' ? costoCol : -1);
    setBulkPriceFileNroOc(nroOcDetected || '');
    setBulkPriceFileError(
      totalSkus > 0 ? '' : autoT('El archivo de precios se leyó pero no aportó SKUs.')
    );
  }, [autoT]);

  /** Procesa Excel/CSV: parseo + filas mapeadas y validadas (reglas de ejemplo en util). Si el archivo es eOC Falabella, actualiza solo precios. */
  const processBulkFile = useCallback(
    async (file) => {
      if (!file) return;
      setBulkUploadError('');
      setBulkUploadSuccessMessage('');
      const allowed = /\.(xlsx|xls|csv)$/i.test(file.name);
      if (!allowed) {
        setBulkParseError(autoT('Formato no soportado. Use .xlsx, .xls o .csv'));
        setBulkSelectedFileName(file.name);
        setBulkSheetGrids([]);
        setBulkActiveSheetIndex(0);
        setBulkUploadFile(null);
        return;
      }
      setBulkParseError('');
      setBulkFileParsing(true);
      try {
        const { grids } = await parseSpreadsheetFileFull(file);
        patchBulkGridsLongIdentifierRows(grids);
        const eocProbe = buildPriceMapFromEocGrids(grids);
        if (eocProbe.priceMap.size > 0) {
          setBulkParseError('');
          setBulkPriceFileError('');
          finalizeBulkPriceFromGrids(file, grids, eocProbe);
          return;
        }
        finalizeBulkOrderFromGrids(file, grids);
      } catch (e) {
        console.error(e);
        setBulkParseError(autoT('No se pudo leer el archivo. Verifique el formato.'));
        setBulkSheetGrids([]);
        setBulkActiveSheetIndex(0);
        setBulkUploadFile(null);
        setBulkSelectedFileName('');
      } finally {
        setBulkFileParsing(false);
      }
    },
    [autoT, finalizeBulkOrderFromGrids, finalizeBulkPriceFromGrids]
  );

  const handleBulkFilePicked = (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) event.target.value = '';
    processBulkFile(file);
  };

  const handleBulkDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBulkDragging(true);
  };

  const handleBulkDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBulkDragging(false);
  };

  const handleBulkDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBulkDragging(false);
    const file = e.dataTransfer?.files?.[0];
    processBulkFile(file);
  };

  /**
   * Columna secundaria Falabella: por defecto eOC (SKU+COSTO_UNI); si el archivo es el eOD,
   * aplica el mismo pipeline que la columna principal.
   */
  const processBulkPriceFile = useCallback(
    async (file) => {
      if (!file) return;
      setBulkPriceFileError('');
      const allowed = /\.(xlsx|xls|csv)$/i.test(file.name);
      if (!allowed) {
        setBulkPriceFileError(autoT('Formato no soportado. Use .xlsx, .xls o .csv'));
        setBulkPriceFile(null);
        setBulkPriceFileName(file.name);
        setBulkPriceMap(null);
        setBulkPriceEocLines([]);
        setBulkPriceFileNroOc('');
        return;
      }
      setBulkPriceFileParsing(true);
      try {
        const { grids } = await parseEocPriceFile(file);
        patchBulkGridsLongIdentifierRows(grids);
        const eocProbe = buildPriceMapFromEocGrids(grids);
        if (eocProbe.priceMap.size === 0) {
          setBulkParseError('');
          setBulkFileParsing(true);
          try {
            finalizeBulkOrderFromGrids(file, grids);
          } catch (orderErr) {
            console.error(orderErr);
            setBulkParseError(autoT('No se pudo leer el archivo. Verifique el formato.'));
            setBulkSheetGrids([]);
            setBulkActiveSheetIndex(0);
            setBulkUploadFile(null);
            setBulkSelectedFileName('');
          } finally {
            setBulkFileParsing(false);
          }
          return;
        }
        finalizeBulkPriceFromGrids(file, grids, eocProbe);
      } catch (e) {
        console.error(e);
        setBulkPriceFile(null);
        setBulkPriceMap(null);
        setBulkPriceEocLines([]);
        setBulkPriceFileNroOc('');
        setBulkPriceFileError(autoT('No se pudo leer el archivo. Verifique el formato.'));
      } finally {
        setBulkPriceFileParsing(false);
      }
    },
    [autoT, finalizeBulkOrderFromGrids, finalizeBulkPriceFromGrids]
  );

  const handleBulkPriceFilePicked = (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) event.target.value = '';
    processBulkPriceFile(file);
  };

  const handleBulkPriceDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBulkPriceDragging(true);
  };

  const handleBulkPriceDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBulkPriceDragging(false);
  };

  const handleBulkPriceDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBulkPriceDragging(false);
    const file = e.dataTransfer?.files?.[0];
    processBulkPriceFile(file);
  };

  /**
   * Si ya no aplica la pareja eOD+eOC Falabella (sin datos en rejilla, otro retailer u otra hoja), descarta el eOC.
   */
  useEffect(() => {
    if (showBulkFalabellaChileDualFileUi) return;
    if (!bulkPriceFile && !bulkPriceMap && !bulkPriceFileError && !bulkPriceFileName) return;
    setBulkPriceFile(null);
    setBulkPriceFileName('');
    setBulkPriceMap(null);
    setBulkPriceEocLines([]);
    setBulkPriceSheetGrids([]);
    setBulkPriceActiveSheetIndex(-1);
    setBulkPriceHeaderIndex(-1);
    setBulkPriceSkuColIndex(-1);
    setBulkPriceCostoColIndex(-1);
    setBulkPriceFileNroOc('');
    setBulkPriceFileError('');
    if (bulkPriceFileInputRef.current) bulkPriceFileInputRef.current.value = '';
  }, [showBulkFalabellaChileDualFileUi, bulkPriceFile, bulkPriceMap, bulkPriceFileError, bulkPriceFileName]);

  const validationCounts = useMemo(() => countByValidation(bulkPreviewRows), [bulkPreviewRows]);

  const hasBulkValidationExportRows =
    validationCounts.error > 0 || validationCounts.warning > 0;

  const exportProblemRowsCsv = useCallback(() => {
    const bad = bulkPreviewRows.filter((r) => r.validationStatus !== 'ok');
    if (!bad.length) return;
    const headers = [
      '#',
      autoT('Estado'),
      'N° OC',
      'Fecha OC',
      'Tienda Destino',
      'pc13',
      'Descripción',
      'Cant. OC',
      'Precio Acordado',
      'Total línea',
      'Precio Lista SAP',
      'Stock Disp. SAP',
      autoT('Pedido total SKU'),
      autoT('Sucursales SKU'),
      autoT('Validación')
    ];
    const lines = [
      headers.join(';'),
      ...bad.map((r) =>
        [
          r.rowIndex,
          r.validationStatus,
          r.nOc,
          r.fechaOc ?? '',
          r.tiendaDestino,
          r.skuIac,
          r.descripcion,
          r.cantOc ?? '',
          r.precioAcordado ?? '',
          r.totalLinea ?? '',
          r.precioListaSap ?? '',
          r.stockDispSap ?? '',
          r.skuTotalDemand ?? '',
          r.skuBranchCount ?? '',
          r.validationMessage
        ]
          .map((c) => `"${String(c).replace(/"/g, '""')}"`)
          .join(';')
      )
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `validacion-carga-mayorista-${Date.now()}.csv`);
  }, [autoT, bulkPreviewRows]);

  /**
   * Consolida `bulkPreviewRows` por SKU para marcar el eOC: cuenta líneas con precio fuera de tolerancia
   * y con stock insuficiente; si hay errores prevalece sobre advertencias.
   */
  const buildEodIssuesBySku = useCallback((rows) => {
    const issues = new Map();
    if (!Array.isArray(rows)) return issues;
    for (const r of rows) {
      const sku = String(r?.skuIac || '').trim();
      if (!sku) continue;
      const hasPriceMismatch = !!r?.sapPriceMismatch;
      /** Faltante por demanda total del SKU (todas las sucursales), no solo por la línea. */
      const hasStockShortage = bulkPreviewRowHasStockShortage(r);
      const isError = r?.validationStatus === 'error';
      if (!hasPriceMismatch && !hasStockShortage && !isError) continue;
      const acc = issues.get(sku) || {
        priceMismatchLines: 0,
        stockShortageLines: 0,
        errorLines: 0,
        skuTotalDemand: null,
        stockDispSap: null
      };
      if (hasStockShortage) {
        if (r?.skuTotalDemand != null) acc.skuTotalDemand = r.skuTotalDemand;
        if (r?.stockDispSap != null) acc.stockDispSap = r.stockDispSap;
      }
      if (hasPriceMismatch) acc.priceMismatchLines += 1;
      if (hasStockShortage) acc.stockShortageLines += 1;
      if (isError) acc.errorLines += 1;
      issues.set(sku, acc);
    }
    const out = new Map();
    for (const [sku, acc] of issues.entries()) {
      const status = acc.errorLines > 0 ? 'error' : 'warning';
      const parts = [];
      if (acc.priceMismatchLines > 0) {
        parts.push(
          `${acc.priceMismatchLines} ${autoT('línea(s) con precio distinto a SAP')}`
        );
      }
      if (acc.stockShortageLines > 0) {
        parts.push(
          `${acc.stockShortageLines} ${autoT('línea(s) con stock insuficiente')}` +
            (acc.skuTotalDemand != null && acc.stockDispSap != null
              ? ` (${autoT('pedido total')} ${acc.skuTotalDemand}, disp. ${acc.stockDispSap})`
              : '')
        );
      }
      if (acc.errorLines > 0 && parts.length === 0) {
        parts.push(`${acc.errorLines} ${autoT('línea(s) con error')}`);
      }
      out.set(sku, { status, summary: parts.join(' · ') });
    }
    return out;
  }, [autoT]);

  const exportFullMarkedXlsx = useCallback(async () => {
    try {
      const exportMod = await import('../utils/bulkPreviewMarkedExport');
      const buf = await exportMod.buildBulkMarkedXlsxBuffer(
        activeBulkGrid,
        bulkPreviewMeta.headerIndex,
        bulkPreviewRows,
        autoT('Observación validación (FAROS)'),
        { profileId: bulkPreviewMeta.profileId, stockSummaryLabels }
      );
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const base = (bulkSelectedFileName || 'carga-mayorista').replace(/\.[^.]+$/i, '');
      saveAs(blob, `${base}-con-validacion.xlsx`);

      const eocGrid = bulkPriceSheetGrids[bulkPriceActiveSheetIndex];
      if (
        showBulkFalabellaChileDualFileUi &&
        eocGrid &&
        bulkPriceHeaderIndex >= 0 &&
        bulkPriceSkuColIndex >= 0
      ) {
        const issuesBySku = buildEodIssuesBySku(bulkPreviewRows);
        const eocBuf = await exportMod.buildEocMarkedXlsxBuffer(
          eocGrid,
          bulkPriceHeaderIndex,
          bulkPriceSkuColIndex,
          issuesBySku,
          autoT('Observación validación (FAROS)')
        );
        const eocBlob = new Blob([eocBuf], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const eocBase = (bulkPriceFileName || 'carga-mayorista-eoc').replace(/\.[^.]+$/i, '');
        saveAs(eocBlob, `${eocBase}-con-validacion.xlsx`);
      }
    } catch (e) {
      console.error(e);
      const isChunkErr =
        e?.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(String(e?.message || ''));
      setBulkUploadError(
        isChunkErr
          ? autoT(
              'No se pudo cargar el componente de exportación (el servidor de desarrollo no respondió). Recargue la página (Ctrl+Shift+R) e intente de nuevo.'
            )
          : autoT('No se pudo generar el archivo Excel: ') + String(e?.message || 'Error desconocido')
      );
    }
  }, [
    activeBulkGrid,
    bulkPreviewMeta.headerIndex,
    bulkPreviewRows,
    bulkPreviewMeta.profileId,
    autoT,
    bulkSelectedFileName,
    showBulkFalabellaChileDualFileUi,
    bulkPriceSheetGrids,
    bulkPriceActiveSheetIndex,
    bulkPriceHeaderIndex,
    bulkPriceSkuColIndex,
    bulkPriceFileName,
    buildEodIssuesBySku,
    stockSummaryLabels
  ]);

  /**
   * Excel de la hoja activa: solo líneas validadas OK (verde), sin errores ni advertencias (reimportar).
   * Falabella Chile dual: además re-descarga el File del eOC tal cual (es diccionario de precios; el eOD manda
   * qué líneas se importan, así que el eOC no se filtra para que la reimportación tenga todos los SKUs disponibles).
   */
  const exportXlsxWithoutErrorRows = useCallback(async () => {
    try {
      const exportMod = await import('../utils/bulkPreviewMarkedExport');
      const { buffer: buf, allocationSummary } = await exportMod.buildBulkReimportXlsxWithStockAllocation(
        activeBulkGrid,
        bulkPreviewMeta.headerIndex,
        bulkPreviewRows,
        { profileId: bulkPreviewMeta.profileId }
      );
      const adjustedLines =
        (allocationSummary?.reducedLines || 0) + (allocationSummary?.excludedLines || 0);
      setBulkStockAllocationNotice(
        adjustedLines > 0
          ? autoT(
              'Excel para reimportar: se repartió el stock disponible en el orden del archivo. {{skus}} SKU afectados · {{reduced}} líneas con cantidad reducida · {{excluded}} líneas excluidas por falta de stock ({{units}} unidades menos).',
              {
                skus: allocationSummary.skuCount,
                reduced: allocationSummary.reducedLines,
                excluded: allocationSummary.excludedLines,
                units: allocationSummary.unitsRemoved
              }
            )
          : ''
      );
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const base = (bulkSelectedFileName || 'carga-mayorista').replace(/\.[^.]+$/i, '');
      saveAs(blob, `${base}-precio-stock-ok.xlsx`);

      if (showBulkFalabellaChileDualFileUi) {
        if (bulkPriceFile) {
          const eocBaseName = (bulkPriceFileName || 'carga-mayorista-eoc').replace(/\.[^.]+$/i, '');
          const eocExtMatch = (bulkPriceFileName || '').match(/\.[^.]+$/i);
          const eocExt = eocExtMatch ? eocExtMatch[0].toLowerCase() : '.xlsx';
          saveAs(bulkPriceFile, `${eocBaseName}-precio-stock-ok${eocExt}`);
        } else {
          console.warn(
            'Falabella Chile dual: no hay File del eOC en estado para re-descargar (solo se entrega el eOD).'
          );
        }
      }
    } catch (e) {
      console.error(e);
      const isChunkErr =
        e?.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(String(e?.message || ''));
      setBulkUploadError(
        isChunkErr
          ? autoT(
              'No se pudo cargar el componente de exportación (el servidor de desarrollo no respondió). Recargue la página (Ctrl+Shift+R) e intente de nuevo.'
            )
          : autoT('No se pudo generar el archivo Excel: ') + String(e?.message || 'Error desconocido')
      );
    }
  }, [
    activeBulkGrid,
    bulkPreviewMeta.headerIndex,
    bulkPreviewMeta.profileId,
    bulkPreviewRows,
    bulkSelectedFileName,
    showBulkFalabellaChileDualFileUi,
    bulkPriceFile,
    bulkPriceFileName,
    autoT
  ]);

  const cancelBulkFlow = () => {
    resetBulkMayoristaState();
    setIsBulkPreviewOpen(false);
    setIsBulkDialogOpen(false);
  };

  useEffect(() => {
    if (!isBulkWholesaleNoClientOcFormat) return undefined;
    setBulkParams((p) => (p.customerOrderNumber ? { ...p, customerOrderNumber: '' } : p));
    return undefined;
  }, [isBulkWholesaleNoClientOcFormat]);

  /**
   * Auto-selección del método de envío `URBANO` al detectar Excel Ix Comercio **Chile** o Multimarca
   * (URBANO es transportista chileno; no aplica a IX Comercio Perú).
   * Solo aplica si: el catálogo lo contiene y el usuario aún no eligió otro valor (no pisamos su elección).
   */
  const isBulkUrbanoAutoSelectFormat =
    bulkPreviewMeta.profileId === IX_COMERCIO_PROFILE_ID || isBulkMultimarcaExcelFormat;
  useEffect(() => {
    if (!isBulkUrbanoAutoSelectFormat) return;
    if (bulkParams.shippingMethod) return;
    if (!bulkShippingMethods.some((m) => m.shipCode === 'URBANO')) return;
    setBulkParams((p) => (p.shippingMethod ? p : { ...p, shippingMethod: 'URBANO' }));
  }, [isBulkUrbanoAutoSelectFormat, bulkShippingMethods, bulkParams.shippingMethod]);

  // Habilita Iniciar Carga / Crear Pedidos: archivo OK, sin errores de línea (precio/stock SAP van como advertencias) y cabecera completa.
  const canStartBulkUpload = useMemo(() => {
    if (!bulkUploadFile || bulkUploading || bulkSapPartnerLoading || bulkParseError || !hasBulkGridData)
      return false;
    if (bulkSapItemsLoading) return false;
    if (validationCounts.error > 0) return false;
    if (!bulkSessionWarehouse.code) return false;
    if (!bulkSessionUserId) return false;
    if (isBulkFalabellaChileDualIncomplete) return false;
    const p = bulkParams;
    if (!String(p.customerRut || '').trim()) return false;
    if (!p.salesChannel) return false;
    if (bulkSapPriceListId == null) return false;
    if (!isBulkWholesaleNoClientOcFormat && !String(p.customerOrderNumber || '').trim()) return false;
    if (!p.brand) return false;
    if (!p.orderType) return false;
    if (!bulkSapPartnerForConfirm) return false;
    if (bulkSapPartnerLookupError) return false;
    if (showBulkDockersGenderSelect && !String(p.dockersGender || '').trim()) return false;
    if (showBulkDockersGenderSelect && bulkBrandDiscountPercent <= 0 && !isBulkIxComercioPeru) return false;
    return true;
  }, [
    isBulkIxComercioPeru,
    bulkUploadFile,
    bulkUploading,
    bulkSapPartnerLoading,
    bulkParseError,
    hasBulkGridData,
    validationCounts.error,
    bulkParams,
    isBulkWholesaleNoClientOcFormat,
    bulkSapItemsLoading,
    bulkSessionWarehouse.code,
    bulkSessionUserId,
    isBulkDialogOpen,
    isBulkFalabellaChileDualIncomplete,
    bulkSapPartnerForConfirm,
    bulkSapPartnerLookupError,
    showBulkDockersGenderSelect,
    bulkBrandDiscountPercent,
    bulkSapPriceListId
  ]);

  /** POST multipart al backend; el socio SAP ya quedó resuelto en cabecera (`bulkSapPartnerForConfirm`). */
  const performWholesaleFileUpload = useCallback(async (discountAmountFromSap) => {
    if (!bulkUploadFile) return;
    const warehouse = getUserWhsCode();
    if (!warehouse) {
      setBulkUploadError(
        autoT('No tiene código de bodega asignado en su usuario. Vuelva a iniciar sesión o contacte al administrador.')
      );
      return;
    }
    const userId = getLoggedInUserId();
    if (!userId) {
      setBulkUploadError(
        autoT('No se identificó el usuario de sesión. Vuelva a iniciar sesión o contacte al administrador.')
      );
      return;
    }
    setBulkUploadError('');
    setBulkUploadSuccessMessage('');
    setBulkUploading(true);
    try {
      /**
       * Archivo eOC (precios) en multipart: debe enviarse siempre que exista mapa válido y referencia File.
       * No usar `showBulkFalabellaChileDualFileUi`: solo gobierna columnas del modal; si la hoja activa
       * cambia el perfil detectado, esa bandera puede quedar false y el POST omitía priceFile → OV en $0.
       */
      const priceFileForUpload =
        bulkPriceFile && bulkPriceMap && bulkPriceMap.size > 0 ? bulkPriceFile : null;
      if (
        bulkPreviewMeta.profileId === FALABELLA_CHILE_PROFILE_ID &&
        bulkPriceMap &&
        bulkPriceMap.size > 0 &&
        !bulkPriceFile
      ) {
        console.warn(
          '[Carga mayorista Falabella] Hay mapa de precios (eOC) pero falta el File en memoria; no se puede enviar priceFile al servidor.'
        );
      }
      const discountForUpload =
        bulkPreviewMeta.profileId === FALABELLA_CHILE_PROFILE_ID || isBulkPeruWholesaleContextActive
          ? discountAmountFromSap
          : null;
      const isMultimarcaUpload = bulkPreviewMeta.profileId === MULTIMARCA_PROFILE_ID;
      const partnerCardName = String(bulkSapPartnerForConfirm?.CardName ?? '').trim();
      /**
       * Perú con catálogo de cliente (Falabella PE / Oechsle / Ripley PE): el Excel trae el Substitute,
       * no el PC13. Se manda el mapa `Substitute → ItemCode` que ya resolvió la vista previa para que
       * el backend persista el ItemCode como SKU IAC (es lo que después viaja a SAP).
       */
      const catalogItemCodesForUpload = usesCustomerCatalogSubstitute(bulkPreviewMeta.profileId)
        ? buildCatalogItemCodeMap(bulkCatalogResolution)
        : null;
      const responseData = await uploadSalesOrdersWholesaleFile(bulkUploadFile, {
        rut: bulkParams.customerRut.trim(),
        canalvta: bulkParams.salesChannel,
        marca: bulkParams.brand,
        tipovta: bulkParams.orderType,
        warehouse,
        userId,
        discountAmount: discountForUpload,
        shippingMethod: bulkParams.shippingMethod,
        /** eOC: backend matchea SKU → COSTO_UNI (solo aplica si el pedido es Falabella Chile). */
        priceFile: priceFileForUpload,
        /** MULTIMARCA: razón social ya resuelta en SAP (evita lookup duplicado en backend). */
        customerName: isMultimarcaUpload && partnerCardName ? partnerCardName : undefined,
        catalogItemCodes: catalogItemCodesForUpload
      });

      // ── Job asíncrono (202 Accepted) ─────────────────────────────────────────
      let finalResponseData = responseData;
      if (responseData?.status === 'PENDING' || responseData?.status === 'PROCESSING') {
        const jobId = responseData?.jobId;
        if (!jobId) throw new Error('El servidor aceptó la importación pero no devolvió un jobId.');
        setWholesaleImportPollingMsg(
          autoT('El archivo fue recibido. Creando órdenes de venta…')
        );
        finalResponseData = await pollWholesaleImportJob(jobId, {
          intervalMs: 4000,
          maxAttempts: 75,
          onStatusUpdate: (status) => {
            if (status === 'PROCESSING') {
              setWholesaleImportPollingMsg(
                autoT('Procesando en el servidor. Esto puede tardar varios minutos para importaciones grandes…')
              );
            }
          },
        });
        setWholesaleImportPollingMsg('');
      }
      // ── Fin job asíncrono ────────────────────────────────────────────────────

      const created = extractWholesaleImportCreatedCount(finalResponseData);
      const importDetail = extractWholesaleImportSuccessDetail(finalResponseData);
      const branchCount = bulkPreviewGroupedByClient.length;
      const isMultimarcaImport =
        bulkPreviewMeta.profileId === MULTIMARCA_PROFILE_ID ||
        String(importDetail.retailer || '').toUpperCase() === 'MULTIMARCA';
      let successText;
      if (isMultimarcaImport && importDetail.firstOrderNumber) {
        successText = autoT('OV creada: {{orderNumber}}', {
          orderNumber: importDetail.firstOrderNumber
        });
      } else if (isMultimarcaImport && created != null && created <= 1) {
        successText = autoT('Carga exitosa, se creó {{created}} OV', { created });
      } else if (
        created != null &&
        branchCount > 0 &&
        created > branchCount &&
        !showsPeruCentralWarehousePreview(bulkPreviewMeta.profileId)
      ) {
        successText = autoT(
          'Carga exitosa: se crearon {{created}} órdenes de venta. En la vista previa había {{branches}} sucursales distintas; el total es mayor porque una o más sucursales generaron más de una OV.',
          { created, branches: branchCount }
        );
      } else if (created != null) {
        successText = autoT('Carga exitosa, se crearon: {{created}} OV', { created });
      } else {
        successText = autoT('Carga exitosa. El servidor no indicó cuántas OV se crearon.');
      }

      setIsBulkPreviewOpen(false);
      setIsBulkDialogOpen(false);
      resetBulkMayoristaState();
      setWholesaleImportBanner(successText);
      await loadSalesOrders();
    } catch (err) {
      setWholesaleImportPollingMsg('');
      const data = err?.response?.data;
      let msg =
        (typeof data === 'string' && data) ||
        data?.message ||
        (Array.isArray(data?.errors) && data.errors.join(', ')) ||
        err?.message ||
        '';
      if (msg && typeof msg === 'object') {
        try {
          msg = JSON.stringify(msg);
        } catch {
          msg = '';
        }
      }
      setBulkUploadError(String(msg || 'Error al subir el archivo.'));
    } finally {
      setBulkUploading(false);
    }
  }, [
    autoT,
    bulkParams,
    bulkPreviewGroupedByClient.length,
    bulkUploadFile,
    loadSalesOrders,
    resetBulkMayoristaState,
    bulkPriceFile,
    bulkPriceMap,
    bulkPreviewMeta.profileId,
    bulkSapPartnerForConfirm,
    bulkCatalogResolution,
    isBulkPeruWholesaleContextActive,
    setWholesaleImportPollingMsg
  ]);

  useEffect(() => {
    if (isBulkPeruWholesaleContextActive) return undefined;
    setBulkParams((p) => (p.dockersGender ? { ...p, dockersGender: '' } : p));
    return undefined;
  }, [isBulkPeruWholesaleContextActive]);

  /**
   * Envío mayorista: el descuento sale **exclusivamente** de `DiscountBrand` del socio SAP
   * según la marca elegida en la cabecera. `DiscountPercent` ya no se consulta.
   */
  const startBulkUpload = useCallback(async () => {
    if (!canStartBulkUpload || !bulkUploadFile) return;
    setBulkUploadError('');
    setBulkUploadSuccessMessage('');
    const discountAmount = resolveBulkBrandDiscount(
      bulkSapPartnerForConfirm,
      bulkParams.brand,
      bulkBrandDiscountResolveOptions
    );
    const sendDiscountOnImport =
      bulkPreviewMeta.profileId === FALABELLA_CHILE_PROFILE_ID || isBulkPeruWholesaleContextActive;
    void performWholesaleFileUpload(sendDiscountOnImport ? discountAmount : null);
  }, [
    bulkSapPartnerForConfirm,
    bulkParams.brand,
    bulkBrandDiscountResolveOptions,
    bulkPreviewMeta.profileId,
    isBulkPeruWholesaleContextActive,
    bulkUploadFile,
    canStartBulkUpload,
    performWholesaleFileUpload
  ]);

  /**
   * Error de API visible **dentro** del Dialog (encima de los botones), porque capas modal de UI5 tapaban toasts globales.
   * Se invoca dos veces (dos instancias JSX) para no reutilizar el mismo elemento React en ambos modales.
   */
  const renderBulkUploadErrorAboveActions = () => {
    if (!bulkUploadError) return null;
    // El backend rechaza el archivo (400) listando las sucursales del Excel que no
    // existen como distritos activos. Detectamos ese caso para ofrecer una acción directa.
    const isMissingDistrictsError = /no est[áa]n registradas como distritos/i.test(bulkUploadError);
    const handleCopyError = () => {
      try {
        navigator.clipboard?.writeText(String(bulkUploadError));
      } catch {
        /* clipboard no disponible: el texto sigue visible para copiar manualmente */
      }
    };
    const goToDistricts = () => {
      setBulkUploadError('');
      setIsBulkPreviewOpen(false);
      setIsBulkDialogOpen(false);
      navigate('/districts');
    };
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          marginTop: '1rem',
          marginBottom: '0.75rem',
          padding: '0.85rem 1rem',
          background: '#ffebee',
          border: '2px solid #c62828',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(198, 40, 40, 0.15)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem' }}>
          <Icon name="message-error" style={{ color: '#c62828', flexShrink: 0, width: '1.35rem', height: '1.35rem' }} />
          <Text
            style={{
              flex: 1,
              minWidth: 0,
              color: '#b71c1c',
              fontWeight: 700,
              fontSize: '0.95rem',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              userSelect: 'text'
            }}
          >
            {bulkUploadError}
          </Text>
          <Button
            design="Transparent"
            icon="decline"
            aria-label={autoT('Cerrar aviso')}
            onClick={() => setBulkUploadError('')}
            style={{ minWidth: '2rem', flexShrink: 0, margin: '-0.35rem' }}
          />
        </div>
        {isMissingDistrictsError && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem', paddingLeft: '2rem' }}>
            <Button design="Emphasized" icon="navigation-right-arrow" iconEnd onClick={goToDistricts}>
              {autoT('Ir a Gestión de Distritos')}
            </Button>
            <Button design="Transparent" icon="copy" onClick={handleCopyError}>
              {autoT('Copiar mensaje')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Header + acciones: carga mayorista + procesar OV seleccionadas (confirmación SAP). */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.5rem' }}>
        <div style={{ height: '2.25rem' }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button
            onClick={openBulkDialog}
            style={{
              backgroundColor: '#2F5A88',
              color: '#F2F2F2',
              border: 'none',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px'
            }}
          >
            <Icon name="excel-attachment" style={{ marginRight: '0.5rem', color: '#F2F2F2' }} />
            {autoT('Carga Mayorista')}
          </Button>

          <Button
            disabled={
              selectedOrderIds.length === 0 ||
              sapChileProcessSelectionLoading ||
              !canProcessSapByVisibleRows
            }
            onClick={() => void openProcessSapDialog()}
            style={{
              backgroundColor:
                selectedOrderIds.length === 0 ||
                sapChileProcessSelectionLoading ||
                !canProcessSapByVisibleRows
                  ? '#ccc'
                  : '#2F5A88',
              color: '#F2F2F2',
              border: 'none',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px'
            }}
          >
            <Icon name="process" style={{ marginRight: '0.5rem', color: '#F2F2F2' }} />
            {sapChileProcessSelectionLoading ? autoT('Preparando…') : autoT('Procesar seleccionados', {})} (
            {selectedOrderIds.length})
          </Button>

          <Button
            disabled={
              selectedOrderIds.length === 0 ||
              !canCancelByVisibleRows
            }
            onClick={openCancelOrderDialog}
            style={{
              backgroundColor:
                selectedOrderIds.length === 0 || !canCancelByVisibleRows ? '#ccc' : '#c62828',
              color: '#fff',
              border: 'none',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px'
            }}
          >
            <Icon name="decline" style={{ marginRight: '0.5rem', color: '#fff' }} />
            {autoT('Cancelar Orden')}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx, .xls"
            style={{ display: 'none' }}
            onChange={() => {}}
          />
        </div>
      </div>

      {/* Vista previa validada antes de enviar a SAP (todas las OV; envío bloqueado si falta stock). */}
      <Dialog
        open={isSapChileProcessPreviewOpen}
        headerText=""
        style={{ width: 'min(1280px, 98vw)', maxHeight: '92vh', zIndex: 11000 }}
        onAfterClose={() => {
          if (!sapProcessLoading) closeSapChileProcessPreview();
        }}
        footer={
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              padding: '0.65rem 1rem',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              borderTop: '1px solid #e0e0e0',
              background: '#fafafa'
            }}
          >
            <Button design="Transparent" onClick={closeSapChileProcessPreview} disabled={sapProcessLoading}>
              {autoT('Cancelar')}
            </Button>
            <Button
              design="Emphasized"
              onClick={() => void handleSendSalesOrdersToSap()}
              disabled={!canSubmitSapChileProcessPreview || sapProcessLoading}
              style={{
                backgroundColor:
                  canSubmitSapChileProcessPreview && !sapProcessLoading ? '#0070AD' : '#ccc',
                color: '#fff'
              }}
            >
              {sapProcessLoading ? autoT('Enviando...') : autoT('Enviar a SAP')}
            </Button>
          </div>
        }
      >
        <div style={{ padding: '0.75rem 1rem 1rem', maxHeight: '85vh', overflow: 'auto' }}>
          {sapChilePreviewSapItemsLoading ? (
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 6,
                marginBottom: '1rem',
                padding: '1rem 1.25rem',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                border: '2px solid #f57c00',
                borderRadius: 12,
                boxShadow: '0 4px 18px rgba(245, 124, 0, 0.28)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                <BusyIndicator
                  active
                  size="M"
                  text={autoT('Cargando listas de precio y stock desde SAP…')}
                  style={{ flexShrink: 0 }}
                />
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <Title level="H5" style={{ margin: 0, color: '#e65100', lineHeight: 1.3 }}>
                    {autoT('Consultando listas de precio y stock en SAP')}
                  </Title>
                  <Text
                    style={{
                      display: 'block',
                      marginTop: '0.5rem',
                      color: '#4e342e',
                      fontSize: '0.95rem',
                      lineHeight: 1.45
                    }}
                  >
                    {autoT(
                      'Se están pidiendo a SAP el precio de la lista configurada y el stock por cada código de ítem (PC13). Con muchas líneas puede tardar; no cierre esta ventana hasta que termine.'
                    )}
                  </Text>
                  {sapChilePreviewSapItemsProgress.total > 0 ? (
                    <Text
                      style={{
                        display: 'block',
                        marginTop: '0.5rem',
                        color: '#bf360c',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                    >
                      {autoT('{{done}} / {{total}} códigos de ítem', {
                        done: sapChilePreviewSapItemsProgress.done,
                        total: sapChilePreviewSapItemsProgress.total
                      })}
                    </Text>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          {sapChilePreviewSapItemsError ? (
            <MessageStrip design="Negative" style={{ marginBottom: '0.75rem' }}>
              {sapChilePreviewSapItemsError}
            </MessageStrip>
          ) : null}
          {!sapChileSessionWhs ? (
            <MessageStrip design="Negative" style={{ marginBottom: '0.75rem' }}>
              {autoT(
                'No hay código de bodega (whsCode) en su sesión. El stock SAP no se puede validar. Contacte al administrador o vuelva a iniciar sesión.'
              )}
            </MessageStrip>
          ) : null}

          <div
            style={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              borderRadius: 10,
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}
          >
            <Icon name="process" style={{ color: '#1565c0', fontSize: '1.5rem' }} />
            <div>
              <Title level="H4" style={{ margin: 0, color: '#0d47a1' }}>
                {autoT('Vista previa antes de enviar a SAP')}
              </Title>
              <Text style={{ color: '#37474f' }}>
                {autoT(
                  'Validación de precio lista y stock SAP. El envío está bloqueado si falta stock, el ítem no existe en SAP o el precio acordado no coincide con el precio lista SAP (con el descuento de cabecera de la orden aplicado cuando corresponde).'
                )}
              </Text>
            </div>
          </div>

          <Text style={{ color: '#1565c0', marginBottom: '0.75rem', display: 'block', fontWeight: 600, fontSize: '0.95rem' }}>
            {autoT('Moneda')}: {autoT(sapChilePreviewCurrencyLabel.labelKey)}
          </Text>

          {sapProcessDockersPeruActive ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                flexWrap: 'wrap',
                marginBottom: '1rem',
                padding: '0.75rem',
                border: '1px solid #cfd8dc',
                borderRadius: '0.5rem',
                background: '#fafafa'
              }}
            >
              <div style={{ minWidth: 220 }}>
                <Label style={{ lineHeight: 1.25 }}>{autoT('Línea DOCKERS')}*</Label>
                <Select
                  style={{ width: '100%' }}
                  value={sapProcessDockersGender}
                  disabled={sapProcessPartnerLoading}
                  onChange={(e) =>
                    void applySapProcessDockersGender(e.detail.selectedOption.value)
                  }
                >
                  <Option value="">{autoT('Seleccione línea...')}</Option>
                  <Option value="men">{autoT('Hombre')}</Option>
                  <Option value="women">{autoT('Mujer')}</Option>
                </Select>
                {sapProcessPartnerLoading ? (
                  <BusyIndicator active size="Small" style={{ marginTop: '0.35rem' }} />
                ) : null}
              </div>
              <div style={{ flex: '1 1 280px', minWidth: 220 }}>
                {sapProcessPartnerError ? (
                  <MessageStrip design="Negative" hideCloseButton style={{ width: '100%' }}>
                    {sapProcessPartnerError}
                  </MessageStrip>
                ) : sapProcessDockersGender === 'men' || sapProcessDockersGender === 'women' ? (
                  <MessageStrip design="Positive" hideCloseButton style={{ width: '100%' }}>
                    {autoT('Descuento DOCKERS aplicado a la validación de precio.')}
                  </MessageStrip>
                ) : (
                  <MessageStrip design="Information" hideCloseButton style={{ width: '100%' }}>
                    {autoT('Seleccione la línea (Hombre/Mujer) para aplicar el descuento DOCKERS y habilitar el envío a SAP.')}
                  </MessageStrip>
                )}
              </div>
            </div>
          ) : null}

          <Title level="H5" style={{ margin: '0 0 0.75rem' }}>
            {autoT('Vista previa validada')}
          </Title>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
              <div style={{ padding: '1rem', borderLeft: '4px solid #2e7d32' }}>
                <Text style={{ color: '#2e7d32', fontWeight: 600 }}>{autoT('Líneas OK')}</Text>
                <Title level="H2" style={{ margin: '0.25rem 0 0', color: '#1b5e20' }}>
                  {sapChileValidationCounts.ok}
                </Title>
              </div>
            </Card>
            <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
              <div style={{ padding: '1rem', borderLeft: '4px solid #c62828' }}>
                <Text style={{ color: '#c62828', fontWeight: 600 }}>{autoT('Errores (bloquean)')}</Text>
                <Title level="H2" style={{ margin: '0.25rem 0 0', color: '#b71c1c' }}>
                  {sapChileValidationCounts.error}
                </Title>
              </div>
            </Card>
            <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
              <div style={{ padding: '1rem', borderLeft: '4px solid #f9a825' }}>
                <Text style={{ color: '#f57f17', fontWeight: 600 }}>{autoT('Advertencias')}</Text>
                <Title level="H2" style={{ margin: '0.25rem 0 0', color: '#e65100' }}>
                  {sapChileValidationCounts.warning}
                </Title>
              </div>
            </Card>
          </div>

          <div
            style={{
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center'
            }}
          >
            <Button
              design="Negative"
              icon="download"
              disabled={
                !hasSapValidationExportRows ||
                sapChilePreviewSapItemsLoading ||
                sapChileProcessSelectionLoading
              }
              onClick={() => setSapExportDialogOpen(true)}
            >
              {autoT('Exportar validación')}
            </Button>
          </div>

          <div
            style={{
              overflow: 'auto',
              maxHeight: 'min(420px, 50vh)',
              border: '1px solid #e0e0e0',
              borderRadius: 8
            }}
          >
            <table
              style={{
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                fontSize: '0.85rem'
              }}
            >
              <colgroup>
                <col style={{ width: '2.75rem' }} />
                <col style={{ width: '3.25%' }} />
                <col style={{ width: '22.5%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '31%' }} />
                <col style={{ width: '9.5%' }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#f5f6f7', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th
                    scope="col"
                    aria-label={autoT('Quitar de envío')}
                    style={{
                      width: '2.75rem',
                      padding: '10px 4px',
                      borderBottom: '1px solid #e0e0e0',
                      fontWeight: 600,
                      textAlign: 'center',
                      verticalAlign: 'bottom'
                    }}
                  >{' '}</th>
                  {[
                    '#',
                    autoT('Sucursal destino'),
                    autoT('N° OC'),
                    isIxComercioProfileId(bulkPreviewMeta.profileId)
                      ? autoT('Fecha entrega')
                      : autoT('Fecha OC'),
                    autoT('Líneas de detalle'),
                    autoT('Validación del grupo'),
                    autoT('Detalle')
                  ].map((h, colIdx) => {
                    const headerWrap = colIdx === 4 || colIdx === 5;
                    return (
                      <th
                        key={colIdx}
                        style={{
                          textAlign: colIdx === 4 ? 'center' : colIdx === 6 ? 'center' : 'left',
                          padding: '10px 8px',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: 600,
                          whiteSpace: headerWrap ? 'normal' : 'nowrap',
                          lineHeight: headerWrap ? 1.25 : 1.2,
                          verticalAlign: 'bottom',
                          wordBreak: headerWrap ? 'break-word' : undefined,
                          overflowWrap: headerWrap ? 'break-word' : undefined
                        }}
                      >
                        {h}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sapChilePreviewGroupedPageSlice.map((grp, idx) => {
                  const bg =
                    grp.validationStatus === 'error'
                      ? '#fff5f5'
                      : grp.validationStatus === 'warning'
                        ? '#fffbf0'
                        : '#f6fff8';
                  const globalIdx =
                    (sapChilePreviewGroupPage - 1) * SAP_PROCESS_PREVIEW_GROUPS_PAGE_SIZE + idx + 1;
                  const clientDisplay =
                    grp.clientLabel && String(grp.clientLabel).trim()
                      ? grp.clientLabel
                      : autoT('Sin sucursal');
                  const isExpanded = expandedSapChileGroupId === grp.id;
                  const detailPanelId = `sap-chile-preview-detail-${String(grp.id).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                  const sortedDetails = [...(grp.detailRows || [])].sort(
                    (a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0)
                  );
                  return (
                    <Fragment key={grp.id}>
                      <tr style={{ background: bg }}>
                        <td
                          style={{
                            padding: '4px',
                            borderBottom: '1px solid #eee',
                            textAlign: 'center',
                            verticalAlign: 'middle'
                          }}
                        >
                          <Button
                            design="Transparent"
                            icon="decline"
                            onClick={() => removeSapChilePreviewGroup(grp)}
                            disabled={sapProcessLoading || sapChilePreviewSapItemsLoading}
                            tooltip={autoT('Quitar esta OV del envío')}
                            accessibleName={autoT('Quitar esta OV del envío')}
                            style={{ color: '#c62828', minWidth: '2rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{globalIdx}</td>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere'
                          }}
                        >
                          {clientDisplay}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{grp.nOc || '—'}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>
                          {grp.fechaOc || '—'}
                        </td>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            textAlign: 'center',
                            fontWeight: 700,
                            color: '#1565c0'
                          }}
                        >
                          {grp.detailCount}
                        </td>
                        <td
                          style={{
                            padding: '4px 6px',
                            borderBottom: '1px solid #eee',
                            whiteSpace: 'normal',
                            verticalAlign: 'middle',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere'
                          }}
                        >
                          {grp.validationStatus === 'ok' ? (
                            pill(grp.validationMessage, '#e8f5e9', '#2e7d32', true)
                          ) : grp.validationStatus === 'warning' ? (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                columnGap: 6,
                                rowGap: 2,
                                maxWidth: '100%'
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#e65100',
                                  lineHeight: 1.2,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {grp.warningLineCount} {autoT('de')} {grp.detailCount}{' '}
                                {autoT('líneas con advertencia')}
                              </span>
                              {pill(grp.validationMessage, '#fff8e1', '#f57f17', true)}
                            </div>
                          ) : (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                columnGap: 6,
                                rowGap: 2,
                                maxWidth: '100%'
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#c62828',
                                  lineHeight: 1.2,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {grp.errorLineCount} {autoT('de')} {grp.detailCount}{' '}
                                {autoT('líneas con error')}
                                {grp.warningLineCount > 0 ? (
                                  <>
                                    {' · '}
                                    {grp.warningLineCount} {autoT('con advertencia')}
                                  </>
                                ) : null}
                              </span>
                              {pill(grp.validationMessage, '#ffebee', '#c62828', true)}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '4px',
                            borderBottom: '1px solid #eee',
                            textAlign: 'center',
                            verticalAlign: 'middle'
                          }}
                        >
                          <Button
                            design="Transparent"
                            icon={isExpanded ? 'navigation-down-arrow' : 'navigation-right-arrow'}
                            onClick={() =>
                              setExpandedSapChileGroupId((id) => (id === grp.id ? null : grp.id))
                            }
                            aria-expanded={isExpanded}
                            aria-controls={detailPanelId}
                            tooltip={isExpanded ? autoT('Ocultar líneas') : autoT('Ver líneas')}
                            accessibleName={
                              isExpanded ? autoT('Ocultar líneas de detalle') : autoT('Ver líneas de detalle')
                            }
                          />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, borderBottom: '1px solid #eee', background: '#fafbfc' }}>
                            <div
                              id={detailPanelId}
                              role="region"
                              aria-label={autoT('Líneas de la OV')}
                              style={{
                                maxHeight: 'min(320px, 42vh)',
                                overflow: 'auto',
                                padding: '10px 12px'
                              }}
                            >
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                                <thead>
                                  <tr style={{ background: '#eceff1' }}>
                                    <th style={bulkPreviewNestedThStyle}>#</th>
                                    <th style={bulkPreviewNestedThStyle}>{autoT('pc13')}</th>
                                    <th style={bulkPreviewNestedThStyle}>{autoT('Descripción')}</th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Cant. OC')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Precio acordado')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Total línea')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Precio SAP')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Descuento')}
                                    </th>
                                    <th
                                      style={{
                                        ...bulkPreviewNestedThStyle,
                                        textAlign: 'right',
                                        lineHeight: 1.2
                                      }}
                                    >
                                      {autoT('Precio SAP')}
                                      <br />
                                      {autoT('con Descuento')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Stock SAP')}
                                    </th>
                                    <th style={bulkPreviewNestedThStyle}>{autoT('Validación')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortedDetails.map((r) => (
                                    <tr key={r.id || r.rowIndex} style={{ background: '#fff' }}>
                                      <td style={bulkPreviewNestedTdStyle}>{r.rowIndex}</td>
                                      <td style={bulkPreviewNestedTdStyle}>{r.skuIac || '—'}</td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, maxWidth: 200 }}>
                                        {r.descripcion || '—'}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {r.cantOc != null ? r.cantOc : '—'}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {formatSapChilePreviewMoney(r.precioAcordado)}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right', fontWeight: 600 }}>
                                        {formatSapChilePreviewMoney(r.totalLinea)}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {formatSapChilePreviewMoney(r.precioListaSap)}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {r.precioListaSap != null
                                          ? `${r.brandDiscountPercent ?? 0}%`
                                          : '—'}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right', fontWeight: 600 }}>
                                        {formatSapChilePreviewMoney(r.precioListaSapConDescuento)}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {r.stockDispSap != null ? r.stockDispSap : '—'}
                                      </td>
                                      <td
                                        style={{
                                          ...bulkPreviewNestedTdStyle,
                                          whiteSpace: 'normal',
                                          maxWidth: 220,
                                          fontSize: '0.72rem'
                                        }}
                                      >
                                        {r.validationStatus === 'ok'
                                          ? pill(r.validationMessage, '#e8f5e9', '#2e7d32', true)
                                          : r.validationStatus === 'warning'
                                            ? pill(r.validationMessage, '#fff8e1', '#f57f17', true)
                                            : pill(r.validationMessage, '#ffebee', '#c62828', true)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!sapChilePreviewSapItemsLoading &&
          !sapChileProcessSelectionLoading &&
          sapChilePreviewGrouped.length === 0 &&
          sapChilePreviewFlatRows.length === 0 ? (
            <MessageStrip design="Information" hideCloseButton style={{ marginTop: '0.75rem', marginBottom: '0.25rem' }}>
              {autoT(
                'No quedan órdenes en la vista previa. Cierre el diálogo o vuelva a procesar la selección desde el listado.'
              )}
            </MessageStrip>
          ) : null}

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginTop: '0.75rem',
              marginBottom: '0.25rem'
            }}
          >
            <Button
              design="Transparent"
              disabled={sapChilePreviewGroupPage <= 1}
              onClick={() => setSapChilePreviewGroupPage((p) => Math.max(1, p - 1))}
            >
              ← {autoT('Anterior')}
            </Button>
            <Text style={{ color: '#37474f', fontWeight: 600 }}>
              {autoT('Página')} {sapChilePreviewGroupPage} / {sapChilePreviewGroupTotalPages}
              <span style={{ fontWeight: 400, color: '#78909c', marginLeft: 8 }}>
                ({sapChilePreviewGrouped.length} {autoT('órdenes')})
              </span>
            </Text>
            <Button
              design="Transparent"
              disabled={sapChilePreviewGroupPage >= sapChilePreviewGroupTotalPages}
              onClick={() =>
                setSapChilePreviewGroupPage((p) => Math.min(sapChilePreviewGroupTotalPages, p + 1))
              }
            >
              {autoT('Siguiente')} →
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={isCancelOrderDialogOpen}
        headerText=""
        style={{ width: 'min(480px, 94vw)', zIndex: 11000 }}
        onAfterClose={() => !cancelOrderLoading && closeCancelOrderDialog()}
        footer={
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              padding: '0.65rem 1rem',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              borderTop: '1px solid #e0e0e0',
              background: '#fafafa'
            }}
          >
            <Button design="Transparent" onClick={closeCancelOrderDialog} disabled={cancelOrderLoading}>
              {autoT('No, cancelar', {})}
            </Button>
            <Button
              design="Emphasized"
              onClick={handleConfirmCancelSalesOrders}
              disabled={cancelOrderLoading}
              style={{ backgroundColor: '#c62828', color: '#fff' }}
            >
              {cancelOrderLoading ? autoT('Procesando...') : autoT('Sí, cancelar órdenes', {})}
            </Button>
          </div>
        }
      >
        <div style={{ padding: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.65rem',
              padding: '0.85rem 1rem',
              background: '#b71c1c',
              color: '#fff',
              margin: 0
            }}
          >
            <Icon name="message-warning" style={{ color: '#ffccbc', flexShrink: 0 }} />
            <span style={{ fontWeight: 700, fontSize: '1rem', flex: 1 }}>{autoT('Cancelar órdenes', {})}</span>
          </div>
          <div style={{ padding: '1.25rem 1.25rem 0.5rem', textAlign: 'center' }}>
            <Text style={{ display: 'block', fontSize: '1.05rem', fontWeight: 600, color: '#263238', marginBottom: '0.75rem' }}>
              {autoT(
                '¿Estás seguro de que deseas cancelar las órdenes seleccionadas? Esta acción no se puede deshacer.'
              )}
            </Text>
            <Text style={{ display: 'block', color: '#455a64', fontSize: '0.9rem', lineHeight: 1.5 }}>
              {autoT('Órdenes afectadas', {})}: <strong style={{ color: '#b71c1c' }}>{selectedOrderIds.length}</strong>
            </Text>
          </div>
        </div>
      </Dialog>

      {showCancelOrderResultDialog && cancelOrderResultSummary && (
        <Dialog
          open={showCancelOrderResultDialog}
          headerText={autoT('Resultado de la cancelación')}
          onAfterClose={() => {
            setShowCancelOrderResultDialog(false);
            setCancelOrderResultSummary(null);
          }}
          footer={
            <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                design="Emphasized"
                onClick={() => {
                  setShowCancelOrderResultDialog(false);
                  setCancelOrderResultSummary(null);
                }}
              >
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem', minWidth: '320px', maxHeight: '360px', overflowY: 'auto' }}>
            <Text style={{ display: 'block', marginBottom: '0.75rem', color: '#37474f' }}>
              {autoT('Exitosas', {})}:{' '}
              <strong style={{ color: '#2e7d32' }}>{cancelOrderResultSummary.successCount}</strong>
              {' · '}
              {autoT('Con error', {})}:{' '}
              <strong style={{ color: '#c62828' }}>{cancelOrderResultSummary.failures.length}</strong>
            </Text>
            {cancelOrderResultSummary.failures.map((f) => (
              <div
                key={f.id}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #eee',
                  fontSize: '0.85rem'
                }}
              >
                <Text style={{ fontWeight: 700 }}>{f.id}</Text>
                <Text style={{ display: 'block', color: '#c62828' }}>{f.errorMessage}</Text>
              </div>
            ))}
          </div>
        </Dialog>
      )}

      {showSapResultDialog && (
        <Dialog
          open={showSapResultDialog}
          headerText={autoT('Resultado del Envío a SAP B1')}
          onAfterClose={() => setShowSapResultDialog(false)}
          footer={
            <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button design="Emphasized" onClick={() => setShowSapResultDialog(false)}>
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem', minWidth: '360px', maxHeight: '400px', overflowY: 'auto' }}>
            {sapResult && (
              <>
                <div
                  style={{
                    display: 'flex',
                    gap: '1.5rem',
                    marginBottom: '1rem',
                    padding: '0.5rem',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px'
                  }}
                >
                  <Text style={{ color: '#2e7d32' }}>
                    ✅ {sapResult.successCount} {autoT('exitosas')}
                  </Text>
                  <Text style={{ color: '#c62828' }}>
                    ❌ {sapResult.failureCount} {autoT('con errores')}
                  </Text>
                </div>
                {sapResult.results &&
                  sapResult.results.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '0.6rem 0',
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.2rem'
                      }}
                    >
                      <Text style={{ fontWeight: 'bold' }}>{r.orderNumber}</Text>
                      {r.success ? (
                        <Text style={{ color: '#2e7d32', fontSize: '0.85rem' }}>
                          SAP DocNum: <strong>{r.docNum}</strong> (DocEntry: {r.docEntry})
                        </Text>
                      ) : (
                        <Text style={{ color: '#c62828', fontSize: '0.85rem' }}>{r.error}</Text>
                      )}
                    </div>
                  ))}
              </>
            )}
          </div>
        </Dialog>
      )}

      {showSapErrorDialog && (
        <Dialog
          open={showSapErrorDialog}
          headerText={autoT('Error')}
          onAfterClose={() => setShowSapErrorDialog(false)}
          footer={
            <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button design="Emphasized" onClick={() => setShowSapErrorDialog(false)}>
                {autoT('Cerrar')}
              </Button>
            </div>
          }
        >
          <div style={{ padding: '1rem' }}>
            <Text>{sapErrorMessage}</Text>
          </div>
        </Dialog>
      )}

      <Dialog
        open={bulkExportDialogOpen}
        headerText={autoT('Exportar validación')}
        onAfterClose={() => setBulkExportDialogOpen(false)}
        footer={
          <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <Button design="Transparent" onClick={() => setBulkExportDialogOpen(false)}>
              {autoT('Cerrar')}
            </Button>
          </div>
        }
      >
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem', maxWidth: 440 }}>
          <Text style={{ color: '#37474f' }}>{autoT('Elija el tipo de exportación:')}</Text>
          <Button
            design="Emphasized"
            icon="download"
            onClick={() => {
              exportProblemRowsCsv();
              setBulkExportDialogOpen(false);
            }}
          >
            {autoT('Solo filas con error o advertencia (CSV)')}
          </Button>
          <Text style={{ color: '#78909c', fontSize: '0.82rem', marginTop: '-0.35rem' }}>
            {autoT('Descarga únicamente las líneas con problema, con el detalle de validación.')}
          </Text>
          <Button
            design="Attention"
            icon="download"
            onClick={async () => {
              await exportFullMarkedXlsx();
              setBulkExportDialogOpen(false);
            }}
          >
            {showBulkFalabellaChileDualFileUi
              ? autoT('Excel completo con colores y columna de observación (eOD + eOC)')
              : autoT('Excel completo con colores y columna de observación')}
          </Button>
          <Text style={{ color: '#78909c', fontSize: '0.82rem', marginTop: '-0.35rem' }}>
            {showBulkFalabellaChileDualFileUi
              ? autoT(
                  'Se descargarán 2 archivos: el eOD con filas resaltadas y columna de observación, y el eOC con los SKUs problemáticos marcados.'
                )
              : autoT(
                  'Toda la hoja actual: filas con error o advertencia resaltadas y una columna nueva al final con el mensaje.'
                )}
          </Text>
          <Button
            design="Default"
            icon="download"
            onClick={async () => {
              await exportXlsxWithoutErrorRows();
              setBulkExportDialogOpen(false);
            }}
          >
            {showBulkFalabellaChileDualFileUi
              ? autoT('Excel solo líneas OK para reimportar (eOD + eOC)')
              : autoT('Excel solo líneas OK (reimportar)')}
          </Button>
          <Text style={{ color: '#78909c', fontSize: '0.82rem', marginTop: '-0.35rem' }}>
            {showBulkFalabellaChileDualFileUi
              ? autoT(
                  'Se descargarán 2 archivos: el eOD solo con las líneas validadas OK (verde) —las de stock insuficiente por pedido total se incluyen con la cantidad repartida según el stock disponible, en el orden del archivo— y el eOC original sin modificar (es diccionario de precios; el eOD manda qué líneas se importan).'
                )
              : autoT(
                  'Misma plantilla y columnas; solo las líneas validadas OK (verde). Se excluyen errores y advertencias (ítem SAP no encontrado, precio distinto, etc.). Las líneas con stock insuficiente por pedido total se incluyen con la cantidad repartida según el stock disponible, en el orden del archivo.'
                )}
          </Text>
        </div>
      </Dialog>

      <Dialog
        open={sapExportDialogOpen}
        headerText={autoT('Exportar validación')}
        onAfterClose={() => setSapExportDialogOpen(false)}
        footer={
          <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <Button design="Transparent" onClick={() => setSapExportDialogOpen(false)}>
              {autoT('Cerrar')}
            </Button>
          </div>
        }
      >
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem', maxWidth: 440 }}>
          <Text style={{ color: '#37474f' }}>{autoT('Elija el tipo de exportación:')}</Text>
          <Button
            design="Emphasized"
            icon="download"
            onClick={() => {
              exportSapProblemRowsCsv();
              setSapExportDialogOpen(false);
            }}
          >
            {autoT('Solo filas con error o advertencia (CSV)')}
          </Button>
          <Text style={{ color: '#78909c', fontSize: '0.82rem', marginTop: '-0.35rem' }}>
            {autoT('Descarga únicamente las líneas con problema, con el detalle de validación.')}
          </Text>
          <Button
            design="Attention"
            icon="download"
            onClick={async () => {
              await exportSapFullMarkedXlsx();
              setSapExportDialogOpen(false);
            }}
          >
            {autoT('Excel completo con colores y columna de observación')}
          </Button>
          <Text style={{ color: '#78909c', fontSize: '0.82rem', marginTop: '-0.35rem' }}>
            {autoT(
              'Todas las líneas de detalle con validación SAP: filas con error o advertencia resaltadas y columna final con el mensaje (datos desde el listado seleccionado).'
            )}
          </Text>
          <Button
            design="Default"
            icon="download"
            onClick={async () => {
              await exportSapXlsxWithoutErrorRows();
              setSapExportDialogOpen(false);
            }}
          >
            {autoT('Excel solo líneas OK')}
          </Button>
          <Text style={{ color: '#78909c', fontSize: '0.82rem', marginTop: '-0.35rem' }}>
            {autoT(
              'Solo las líneas validadas OK (verde). Se excluyen errores y advertencias (ítem SAP no encontrado, precio distinto, stock insuficiente, etc.).'
            )}
          </Text>
        </div>
      </Dialog>

      {/* Modal/Pantalla: Carga Mayorista */}
      <Dialog
        open={isBulkDialogOpen}
        headerText={autoT('Carga Mayorista')}
        style={{ width: 'min(1100px, 95vw)' }}
        onAfterClose={handleBulkDialogAfterClose}
      >
        <div style={{ padding: '1rem' }}>
          {bulkUploadSuccessMessage ? (
            <MessageStrip design="Positive" style={{ marginBottom: '1rem' }}>
              {bulkUploadSuccessMessage}
            </MessageStrip>
          ) : null}
          <Card style={{ marginBottom: '1rem' }}>
            <div style={{ padding: '1rem' }}>
              <FlexBox
                justifyContent={FlexBoxJustifyContent.Start}
                style={{ gap: '0.75rem', alignItems: 'center' }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: '#e8f5e9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Icon name="download" style={{ color: '#2e7d32' }} />
                </div>
                <div>
                  <Title level="H4" style={{ margin: 0 }}>{autoT('Carga Mayorista')}</Title>
                </div>
              </FlexBox>
            </div>
          </Card>

          <Card style={{ marginBottom: '1rem' }}>
            <div style={{ padding: '1rem' }}>
              <FlexBox direction={FlexBoxDirection.Column} style={{ gap: '0.25rem', marginBottom: '1rem' }}>
                <Title level="H4" style={{ margin: 0 }}>{autoT('Parámetros de cabecera')}</Title>
                <Text style={{ color: '#6c757d' }}>
                  {autoT('Completa los datos antes de cargar el archivo. Estos parámetros se asociarán a todas las filas del documento.')}
                </Text>
              </FlexBox>

              {!bulkSessionWarehouse.code ? (
                <MessageStrip design="Negative" style={{ marginBottom: '0.75rem' }}>
                  {autoT(
                    'Su usuario no tiene código de bodega (whsCode). Contacte al administrador o vuelva a iniciar sesión.'
                  )}
                </MessageStrip>
              ) : null}

              {!bulkSessionUserId ? (
                <MessageStrip design="Negative" style={{ marginBottom: '0.75rem' }}>
                  {autoT(
                    'No se identificó el usuario de sesión (id). Vuelva a iniciar sesión o contacte al administrador.'
                  )}
                </MessageStrip>
              ) : null}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {/* Resumen SAP: una sola línea, alineado a la izquierda (razón social e id. fiscal en línea, no en lista vertical) */}
                {bulkSapPartnerLoading || bulkSapPartnerLookupError || bulkSapPartnerForConfirm ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flexWrap: 'nowrap',
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '0.45rem 0.75rem',
                      background: bulkSapPartnerLookupError ? '#ffebee' : '#f5f9fc',
                      border: `1px solid ${bulkSapPartnerLookupError ? '#ef9a9a' : '#b3d9f2'}`,
                      borderRadius: 8,
                      minHeight: '2.25rem'
                    }}
                  >
                    {bulkSapPartnerLoading ? (
                      <>
                        <BusyIndicator active size="Small" style={{ flexShrink: 0 }} />
                        <Text style={{ color: '#1565c0', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                          {autoT('Consultando cliente en SAP…')}
                        </Text>
                      </>
                    ) : null}
                    {!bulkSapPartnerLoading && bulkSapPartnerLookupError ? (
                      <Text
                        style={{
                          color: '#b71c1c',
                          fontWeight: 600,
                          fontSize: '0.88rem',
                          textAlign: 'left',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: '1 1 auto'
                        }}
                        title={bulkSapPartnerLookupError}
                      >
                        {bulkSapPartnerLookupError}
                      </Text>
                    ) : null}
                    {!bulkSapPartnerLoading && !bulkSapPartnerLookupError && bulkSapPartnerForConfirm ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          gap: '0.5rem',
                          minWidth: 0,
                          /** Junta razón social e Id. fiscal a la izquierda; sin grow que empuje el fiscal al borde */
                          width: 'max-content',
                          maxWidth: '100%',
                          overflow: 'hidden'
                        }}
                        title={`${bulkSapPartnerForConfirm.CardName || ''} — ${bulkSapPartnerForConfirm.FederalTaxID || ''}`}
                      >
                        <Text
                          style={{
                            fontSize: '0.88rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minWidth: 0,
                            flexShrink: 1,
                            textAlign: 'left'
                          }}
                        >
                          <strong>{autoT('Razón social')}:</strong>{' '}
                          {bulkSapPartnerForConfirm.CardName || '—'}
                        </Text>
                        <span style={{ color: '#90a4ae', fontWeight: 400, flexShrink: 0 }}>|</span>
                        <Text
                          style={{
                            fontSize: '0.88rem',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            textAlign: 'left'
                          }}
                        >
                          <strong>{autoT('Id. fiscal')}:</strong>{' '}
                          {bulkSapPartnerForConfirm.FederalTaxID || '—'}
                        </Text>
                        <span style={{ color: '#90a4ae', fontWeight: 400, flexShrink: 0 }}>|</span>
                        <Text
                          style={{
                            fontSize: '0.88rem',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            textAlign: 'left',
                            color: bulkSapPriceListId == null ? '#b71c1c' : undefined
                          }}
                        >
                          <strong>{autoT('Lista de precios SAP')}:</strong>{' '}
                          {bulkSapPriceListId ?? autoT('sin asignar')}
                        </Text>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {/* Cliente resuelto en SAP sin lista de precios asignada: bloquea validación y carga. */}
                {bulkSapPriceListError && !bulkSapPartnerLoading ? (
                  <MessageStrip design="Negative" hideCloseButton style={{ marginBottom: '0.75rem' }}>
                    {bulkSapPriceListError}
                  </MessageStrip>
                ) : null}

                {/* Fila 1: identificación y OC — misma línea de base para los tres campos */}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'flex-end'
                  }}
                >
                  <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: '100%' }}>
                    <Label>{autoT('RUT Cliente')}*</Label>
                    <Input
                      value={bulkParams.customerRut}
                      placeholder={autoT('Ej: 99.222.000-3')}
                      onChange={(e) => setBulkParams((p) => ({ ...p, customerRut: e.target.value }))}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: '100%' }}>
                    <Label>{autoT('Canal de Venta')}*</Label>
                    {bulkSaleChannelsError ? (
                      <MessageStrip design="Negative" style={{ marginBottom: '0.5rem' }}>
                        {bulkSaleChannelsError}
                      </MessageStrip>
                    ) : null}
                    <Select
                      key={`bulk-salesChannel-${bulkSelectRemountKey}`}
                      value={bulkParams.salesChannel}
                      disabled={bulkSaleChannelsLoading}
                      onChange={(e) => setBulkParams((p) => ({ ...p, salesChannel: e.detail.selectedOption.value }))}
                      style={{ width: '100%' }}
                    >
                      <Option value="">{autoT('Seleccione canal...')}</Option>
                      {bulkSaleChannels.map((ch) => (
                        <Option key={ch.id} value={ch.name}>
                          {ch.name}
                        </Option>
                      ))}
                    </Select>
                    {bulkSaleChannelsLoading ? (
                      <BusyIndicator active size="Small" style={{ marginTop: '0.35rem' }} />
                    ) : null}
                  </div>

                  {isBulkMultimarcaExcelFormat ? (
                    <div style={{ flex: '1 1 280px', minWidth: 200, alignSelf: 'flex-start' }}>
                      <MessageStrip
                        design="Information"
                        hideCloseButton
                        style={{ width: '100%', boxSizing: 'border-box' }}
                      >
                        {autoT(
                          'Se detectó plantilla Multimarca en el archivo: no ingrese N° de orden de compra cliente; el sistema generará la orden (prefijo MM) y la carga sigue la estructura del Excel.'
                        )}
                      </MessageStrip>
                    </div>
                  ) : isBulkIxComercioExcelFormat ? (
                    <div style={{ flex: '1 1 280px', minWidth: 200, alignSelf: 'flex-start' }}>
                      <MessageStrip
                        design="Information"
                        hideCloseButton
                        style={{ width: '100%', boxSizing: 'border-box' }}
                      >
                        {autoT(
                          'Se detectó plantilla IX Comercio en el archivo: no ingrese N° de orden de compra cliente; con este formato ese dato se omite y la carga sigue la estructura del Excel.'
                        )}
                      </MessageStrip>
                    </div>
                  ) : (
                    <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: '100%' }}>
                      <Label>{autoT('N° Orden de Compra Cliente')}*</Label>
                      <Input
                        value={bulkParams.customerOrderNumber}
                        placeholder={autoT('Ej: OC-2026-483')}
                        onChange={(e) => setBulkParams((p) => ({ ...p, customerOrderNumber: e.target.value }))}
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                </div>

                {/* Fila 2: marca, tipo, descuento (estrecho), método de envío (compacto a la derecha del descuento).
                    Franja fija de etiquetas (minHeight + align flex-end) para que los inputs queden alineados aunque el texto del label ocupe 1 o 2 líneas. */}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'flex-start'
                  }}
                >
                  <div
                    style={{
                      flex: '1 1 168px',
                      minWidth: 148,
                      maxWidth: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        height: '3rem',
                        display: 'flex',
                        alignItems: 'flex-end',
                        marginBottom: '0.35rem',
                        boxSizing: 'border-box'
                      }}
                    >
                      <Label style={{ lineHeight: 1.25 }}>{autoT('Marca')}*</Label>
                    </div>
                    {bulkBrandingsError ? (
                      <MessageStrip design="Negative" style={{ marginBottom: '0.5rem' }}>
                        {bulkBrandingsError}
                      </MessageStrip>
                    ) : null}
                    <Select
                      key={`bulk-brand-${bulkSelectRemountKey}`}
                      value={bulkParams.brand}
                      disabled={bulkBrandingsLoading || bulkPeruDockersHeaderLocked}
                      onChange={(e) => {
                        const brand = e.detail.selectedOption.value;
                        setBulkParams((p) => ({
                          ...p,
                          brand,
                          dockersGender:
                            isBulkPeruWholesaleContextActive && isDockersBrandName(brand)
                              ? p.dockersGender
                              : ''
                        }));
                      }}
                      style={{ width: '100%' }}
                    >
                      <Option value="">{autoT('Seleccione marca...')}</Option>
                      {bulkBrandings.map((b) => (
                        <Option key={b.id} value={b.name}>
                          {b.name}
                        </Option>
                      ))}
                    </Select>
                    {bulkBrandingsLoading ? (
                      <BusyIndicator active size="Small" style={{ marginTop: '0.35rem' }} />
                    ) : null}
                  </div>

                  {showBulkDockersGenderSelect ? (
                    <div
                      style={{
                        flex: '1 1 140px',
                        minWidth: 128,
                        maxWidth: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <div
                        style={{
                          height: '3rem',
                          display: 'flex',
                          alignItems: 'flex-end',
                          marginBottom: '0.35rem',
                          boxSizing: 'border-box'
                        }}
                      >
                        <Label style={{ lineHeight: 1.25 }}>{autoT('Línea DOCKERS')}*</Label>
                      </div>
                      <Select
                        key={`bulk-dockersGender-${bulkSelectRemountKey}`}
                        value={bulkParams.dockersGender}
                        disabled={bulkPeruDockersHeaderLocked}
                        onChange={(e) =>
                          setBulkParams((p) => ({
                            ...p,
                            dockersGender: e.detail.selectedOption.value
                          }))
                        }
                        style={{ width: '100%' }}
                      >
                        <Option value="">{autoT('Seleccione línea...')}</Option>
                        <Option value="men">{autoT('Hombre')}</Option>
                        <Option value="women">{autoT('Mujer')}</Option>
                      </Select>
                      {bulkPeruDockersHeaderLocked ? (
                        <BusyIndicator active size="Small" style={{ marginTop: '0.35rem' }} />
                      ) : null}
                      {bulkSapPartnerForConfirm &&
                      showBulkDockersGenderSelect &&
                      bulkParams.dockersGender &&
                      bulkBrandDiscountPercent === 0 ? (
                        isBulkIxComercioPeru ? (
                          <Text style={{ color: '#546e7a', fontSize: '0.8rem', marginTop: '0.35rem' }}>
                            {autoT('IX Comercio no tiene descuento de negocio: se valida con el precio de lista.')}
                          </Text>
                        ) : (
                          <Text style={{ color: '#c62828', fontSize: '0.8rem', marginTop: '0.35rem' }}>
                            {autoT(
                              'No se encontró descuento DOCKERS-MEN/DOCKERS-WOMEN para este cliente en SAP.'
                            )}
                          </Text>
                        )
                      ) : null}
                    </div>
                  ) : null}

                  <div
                    style={{
                      flex: '1 1 152px',
                      minWidth: 128,
                      maxWidth: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        height: '3rem',
                        display: 'flex',
                        alignItems: 'flex-end',
                        marginBottom: '0.35rem',
                        boxSizing: 'border-box'
                      }}
                    >
                      <Label style={{ lineHeight: 1.25 }}>{autoT('Tipo de Venta*')}</Label>
                    </div>
                    <Select
                      key={`bulk-orderType-${bulkSelectRemountKey}`}
                      value={bulkParams.orderType}
                      onChange={(e) => setBulkParams((p) => ({ ...p, orderType: e.detail.selectedOption.value }))}
                      style={{ width: '100%' }}
                    >
                      <Option value="">{autoT('Seleccione tipo...')}</Option>
                      <Option value="Venta">{autoT('Venta')}</Option>
                      <Option value="Consignación">{autoT('Consignación')}</Option>
                      <Option value="Traslado">{autoT('Traslado')}</Option>
                    </Select>
                  </div>

                  {/* Cuadro de descuento de cabecera: SIEMPRE visible para cualquier retail
                      (IX Comercio, Ripley, Paris, Falabella, etc.). Muestra 0 cuando la marca/cliente
                      no tiene descuento por marca en SAP. */}
                  <div
                    style={{
                      flex: '0 0 118px',
                      width: 118,
                      minWidth: 118,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        height: '3rem',
                        display: 'flex',
                        alignItems: 'flex-end',
                        marginBottom: '0.35rem',
                        boxSizing: 'border-box'
                      }}
                    >
                      <Label style={{ lineHeight: 1.25 }}>{autoT('Desc. negocio (%)')}</Label>
                    </div>
                    <Input
                      value={bulkParams.businessDiscount || '0'}
                      readonly
                      disabled
                      title={autoT('Calculado automáticamente desde la marca seleccionada')}
                      style={{ width: '100%', boxSizing: 'border-box', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div
                    style={{
                      flex: '0 1 200px',
                      minWidth: 160,
                      maxWidth: 220,
                      width: 'clamp(160px, 18vw, 220px)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div
                      style={{
                        height: '3rem',
                        display: 'flex',
                        alignItems: 'flex-end',
                        marginBottom: '0.35rem',
                        boxSizing: 'border-box'
                      }}
                    >
                      <Label style={{ lineHeight: 1.25 }}>{autoT('Método de envío')}</Label>
                    </div>
                    {bulkShippingMethodsError ? (
                      <MessageStrip design="Negative" style={{ marginBottom: '0.5rem' }}>
                        {bulkShippingMethodsError}
                      </MessageStrip>
                    ) : null}
                    <Select
                      key={`bulk-shippingMethod-${bulkSelectRemountKey}`}
                      value={bulkParams.shippingMethod}
                      disabled={bulkShippingMethodsLoading}
                      onChange={(e) =>
                        setBulkParams((p) => ({ ...p, shippingMethod: e.detail.selectedOption.value }))
                      }
                      style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
                    >
                      <Option value="">{autoT('Seleccione método...')}</Option>
                      {bulkShippingMethods.map((m) => (
                        <Option key={m.shipCode} value={m.shipCode}>
                          {m.shipName}
                        </Option>
                      ))}
                    </Select>
                    {bulkShippingMethodsLoading ? (
                      <BusyIndicator active size="Small" style={{ marginTop: '0.35rem' }} />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {showBulkFalabellaChileDualFileUi && isBulkFalabellaChileDualIncomplete ? (
              <MessageStrip design="Information" hideCloseButton>
                {!hasFalabellaChileOrderGrid
                  ? autoT(
                      'Falabella Chile: falta el archivo de orden (eOD). Puede soltarlo en la columna izquierda o en la derecha.'
                    )
                  : autoT(
                      'Falabella Chile: falta el archivo de precios (eOC, columnas SKU y COSTO_UNI). Puede soltarlo en cualquiera de las dos columnas.'
                    )}
              </MessageStrip>
            ) : null}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                alignItems: 'stretch'
              }}
            >
              <Card
                style={
                  showBulkFalabellaChileDualFileUi
                    ? bulkFalabellaDualCardStyle
                    : { flex: '1 1 320px', minWidth: 260, boxSizing: 'border-box' }
                }
              >
                <div
                  style={
                    showBulkFalabellaChileDualFileUi
                      ? bulkFalabellaDualCardBodyStyle
                      : { padding: '1rem' }
                  }
                >
                  {showBulkFalabellaChileDualFileUi ? (
                    <div style={{ marginBottom: '0.65rem', fontWeight: 600, color: '#37474f' }}>
                      {autoT('Orden (eOD)')}
                    </div>
                  ) : null}
                  {bulkParseError ? (
                    <MessageStrip design="Negative" style={{ marginBottom: '0.75rem' }}>
                      {bulkParseError}
                    </MessageStrip>
                  ) : null}
                  {/* Drag & drop + click para Excel/CSV */}
                  <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') bulkFileInputRef.current?.click();
                    }}
                    onDragOver={handleBulkDragOver}
                    onDragLeave={handleBulkDragLeave}
                    onDrop={handleBulkDrop}
                    onClick={() => bulkFileInputRef.current?.click()}
                    style={
                      showBulkFalabellaChileDualFileUi
                        ? {
                            ...bulkFalabellaDualDropBase,
                            border: isBulkDragging ? '2px solid #2F5A88' : '2px dashed #8aa4c2',
                            backgroundColor: isBulkDragging ? '#e8f0fa' : '#f2f6fb'
                          }
                        : {
                            border: isBulkDragging ? '2px solid #2F5A88' : '2px dashed #8aa4c2',
                            borderRadius: 10,
                            backgroundColor: isBulkDragging ? '#e8f0fa' : '#f2f6fb',
                            padding: '2.25rem 1rem',
                            textAlign: 'center',
                            cursor: 'pointer'
                          }
                    }
                  >
                    <Icon name="upload" style={{ color: '#2F5A88' }} />
                    <div style={{ marginTop: '0.75rem', marginBottom: '0.25rem', fontWeight: 600 }}>
                      {showBulkFalabellaChileDualFileUi
                        ? autoT('Archivo de datos de la orden (eOD)')
                        : autoT('Arrastra tu archivo Excel aquí')}
                    </div>
                    <Text style={{ color: '#6c757d' }}>
                      {showBulkFalabellaChileDualFileUi
                        ? autoT('Líneas de OC · también puede usar la otra columna si prefiere')
                        : autoT('o haz clic para seleccionar un archivo')}
                    </Text>

                    <div style={{ marginTop: '1rem' }} onClick={(e) => e.stopPropagation()}>
                      <Button design="Transparent" onClick={() => bulkFileInputRef.current?.click()}>
                        {showBulkFalabellaChileDualFileUi
                          ? autoT('Seleccionar archivo de orden')
                          : autoT('Seleccionar Archivo')}
                      </Button>
                      <input
                        ref={bulkFileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                        style={{ display: 'none' }}
                        onChange={handleBulkFilePicked}
                      />
                    </div>

                    {bulkSelectedFileName ? (
                      <div style={{ marginTop: '0.75rem', color: '#2F5A88', fontWeight: 600 }}>
                        {bulkSelectedFileName}
                        {hasBulkGridData ? (
                          <span style={{ color: '#6c757d', fontWeight: 400, marginLeft: 8 }}>
                            (
                            {bulkSheetGrids[bulkActiveSheetIndex]?.rowCount ?? 0} {autoT('filas')}
                            {bulkSheetGrids.length > 1
                              ? ` · ${bulkSheetGrids.length} ${autoT('hojas')}`
                              : ''}
                            )
                          </span>
                        ) : null}
                        {hasBulkGridData && bulkSheetGrids[bulkActiveSheetIndex]?.name ? (
                          <Text
                            style={{
                              display: 'block',
                              marginTop: '0.45rem',
                              color: '#546e7a',
                              fontSize: '0.88rem',
                              fontWeight: 500
                            }}
                          >
                            {autoT('Hoja utilizada')}: {bulkSheetGrids[bulkActiveSheetIndex].name}
                          </Text>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Card>

              {showBulkFalabellaChileDualFileUi ? (
                <Card style={bulkFalabellaDualCardStyle}>
                  <div style={bulkFalabellaDualCardBodyStyle}>
                    <div style={{ marginBottom: '0.65rem', fontWeight: 600, color: '#37474f' }}>
                      {autoT('Precios (eOC)')}
                    </div>
                    {bulkPriceFileError ? (
                      <MessageStrip design="Negative" style={{ marginBottom: '0.75rem' }}>
                        {bulkPriceFileError}
                      </MessageStrip>
                    ) : null}

                    {bulkPriceFileNroOcMismatch ? (
                      <MessageStrip design="Warning" style={{ marginBottom: '0.75rem' }}>
                        {autoT(
                          'El N° OC del eOC ({{ocEoc}}) no coincide con el del eOD ({{ocEod}}). Verifique que ambos archivos correspondan a la misma orden.',
                          { ocEoc: bulkPriceFileNroOc, ocEod: bulkEodNroOc }
                        )}
                      </MessageStrip>
                    ) : null}

                    {!bulkEocDiscountConsistency.valid &&
                    bulkEocDiscountConsistency.mismatches.length > 0 ? (
                      <MessageStrip design="Warning" style={{ marginBottom: '0.75rem' }}>
                        <div style={{ marginBottom: '0.35rem' }}>
                          {autoT(
                            'El descuento del cliente/marca ({{pct}}%) no coincide con TOT_DESCUENT del eOC. Verifique que el RUT y la marca seleccionados sean correctos.',
                            { pct: bulkBrandDiscountPercent }
                          )}
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.85rem', lineHeight: 1.4 }}>
                          {bulkEocDiscountConsistency.mismatches.map((m) => (
                            <li key={m.sku}>
                              {autoT(
                                'SKU {{sku}}: esperado {{expected}} · calculado {{calculated}} (Δ {{diff}})',
                                {
                                  sku: m.sku,
                                  expected: formatBulkMoney(m.expected),
                                  calculated: formatBulkMoney(m.calculated),
                                  diff: formatBulkMoney(m.diff)
                                }
                              )}
                            </li>
                          ))}
                        </ul>
                      </MessageStrip>
                    ) : null}

                    <div
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') bulkPriceFileInputRef.current?.click();
                      }}
                      onDragOver={handleBulkPriceDragOver}
                      onDragLeave={handleBulkPriceDragLeave}
                      onDrop={handleBulkPriceDrop}
                      onClick={() => bulkPriceFileInputRef.current?.click()}
                      style={{
                        ...bulkFalabellaDualDropBase,
                        border: isBulkPriceDragging ? '2px solid #6a1b9a' : '2px dashed #b39ddb',
                        backgroundColor: isBulkPriceDragging ? '#ede7f6' : '#f5f0fb'
                      }}
                    >
                      <Icon name="upload" style={{ color: '#6a1b9a' }} />
                      <div style={{ marginTop: '0.75rem', marginBottom: '0.25rem', fontWeight: 600 }}>
                        {autoT('Maestro de precios (eOC)')}
                      </div>
                      <Text style={{ color: '#6c757d' }}>
                        {autoT('Columnas SKU y COSTO_UNI · si arrastra el eOD aquí, se usará como archivo de orden')}
                      </Text>

                      <div style={{ marginTop: '1rem' }} onClick={(e) => e.stopPropagation()}>
                        <Button
                          design="Transparent"
                          onClick={() => bulkPriceFileInputRef.current?.click()}
                        >
                          {autoT('Seleccionar archivo (precios u orden)')}
                        </Button>
                        <input
                          ref={bulkPriceFileInputRef}
                          type="file"
                          accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                          style={{ display: 'none' }}
                          onChange={handleBulkPriceFilePicked}
                        />
                      </div>

                      {bulkPriceFileParsing ? (
                        <div style={{ marginTop: '0.75rem' }}>
                          <BusyIndicator
                            active
                            size="Small"
                            text={autoT('Leyendo archivo…')}
                          />
                        </div>
                      ) : null}

                      {bulkPriceFileName && !bulkPriceFileParsing ? (
                        <div style={{ marginTop: '0.75rem', color: '#6a1b9a', fontWeight: 600 }}>
                          {bulkPriceFileName}
                          {bulkPriceMap?.size ? (
                            <span style={{ color: '#6c757d', fontWeight: 400, marginLeft: 8 }}>
                              ({bulkPriceMap.size} {autoT('SKUs con precio')})
                            </span>
                          ) : null}
                          {bulkPriceFileNroOc ? (
                            <Text
                              style={{
                                display: 'block',
                                marginTop: '0.35rem',
                                color: '#546e7a',
                                fontSize: '0.88rem',
                                fontWeight: 500
                              }}
                            >
                              {autoT('N° OC en eOC')}: {bulkPriceFileNroOc}
                            </Text>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Card>
              ) : null}
            </div>
          </div>

          {/* ==================================================
              NUEVO (2026-03-17)
              Pie informativo + acciones (solo visual)
             ================================================== */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <Card style={{ flex: '1 1 420px', minWidth: 320 }}>
              <div style={{ padding: '1rem', borderLeft: '4px solid #2e7d32' }}>
                <FlexBox style={{ gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Icon name="message-success" style={{ color: '#2e7d32' }} />
                  <Title level="H5" style={{ margin: 0 }}>{autoT('Formatos soportados')}</Title>
                </FlexBox>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#2c3e50' }}>
                  <li>{autoT('Excel')} (.xlsx, .xls)</li>
                  <li>{autoT('CSV separado por comas')}</li>
                  <li>{autoT('Tamaño máximo')}: 20 MB</li>
                  <li>{autoT('Codificación')}: UTF-8</li>
                </ul>
              </div>
            </Card>

            <Card style={{ flex: '1 1 420px', minWidth: 320 }}>
              <div style={{ padding: '1rem', borderLeft: '4px solid #f0ad4e' }}>
                <FlexBox style={{ gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Icon name="message-warning" style={{ color: '#f0ad4e' }} />
                  <Title level="H5" style={{ margin: 0 }}>{autoT('Importante')}</Title>
                </FlexBox>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#2c3e50' }}>
                  <li>{autoT('Use la plantilla oficial del mayorista')}</li>
                  {isBulkMultimarcaExcelFormat ? (
                    <li>
                      {autoT(
                        'Plantilla Multimarca detectada: el N° OC no va en el Excel; el sistema genera el folio con el RUT/RUC del cliente y un correlativo.'
                      )}
                    </li>
                  ) : isBulkIxComercioExcelFormat ? (
                    <li>
                      {autoT(
                        'Plantilla IX Comercio detectada: el N° OC no va en el Excel; el sistema genera la orden.'
                      )}
                    </li>
                  ) : (
                    <li>{autoT('El archivo debe incluir el N° OC')}</li>
                  )}
                  {showBulkFalabellaChileDualFileUi ? (
                    <li>
                      {autoT(
                        'Falabella Chile entrega 2 archivos: el eOD (datos) y el eOC (precios). Puede cargarlos en cualquier orden y en cualquiera de las dos columnas.'
                      )}
                    </li>
                  ) : null}
                  {bulkPreviewMeta.profileId === RIPLEY_PERU_PROFILE_ID ? (
                    <li>
                      {autoT(
                        'Ripley Perú: se agrupa por Suc Entrega (bodega central); el destino por tienda va en Suc Destino de cada línea.'
                      )}
                    </li>
                  ) : null}
                  {bulkPreviewMeta.profileId === OECHSLE_PERU_PROFILE_ID ? (
                    <li>
                      {autoT(
                        'Oechsle Perú: se agrupa por «Enviar a:» del preámbulo (bodega central); el destino por tienda va en la columna Sucursal de cada línea.'
                      )}
                    </li>
                  ) : null}
                  <li>{autoT('Revise los códigos SKU del cliente')}</li>
                  <li>
                    {bulkPeruCentralWarehousePreview
                      ? autoT('El destino por tienda se informa en cada línea del Excel (no en la cabecera de carga).')
                      : autoT('Incluya la tienda destino por línea')}
                  </li>
                </ul>
              </div>
            </Card>
          </div>

          {renderBulkUploadErrorAboveActions()}

          {isBulkFalabellaChileDualIncomplete ? (
            <MessageStrip design="Warning" hideCloseButton style={{ marginTop: '0.75rem' }}>
              {!hasFalabellaChileOrderGrid
                ? autoT(
                    'Falabella Chile: cargue el archivo de orden (eOD) para habilitar Vista Previa e Iniciar Carga.'
                  )
                : autoT(
                    'Falabella Chile: cargue el archivo de precios (eOC) para habilitar Vista Previa e Iniciar Carga.'
                  )}
            </MessageStrip>
          ) : null}

          <FlexBox
            justifyContent={FlexBoxJustifyContent.End}
            style={{ gap: '0.5rem', marginTop: '0.25rem', alignItems: 'center' }}
          >
            {bulkUploading || bulkSapPartnerLoading ? <BusyIndicator active size="Small" /> : null}
            <Button
              design="Transparent"
              onClick={() => {
                resetBulkMayoristaState();
                closeBulkDialog();
              }}
              disabled={bulkUploading || bulkSapPartnerLoading}
            >
              {autoT('Cancelar')}
            </Button>
            <Button
              design="Transparent"
              icon="show"
              disabled={
                !hasBulkGridData ||
                !!bulkParseError ||
                bulkUploading ||
                bulkSapPartnerLoading ||
                isBulkFalabellaChileDualIncomplete ||
                !bulkParams.salesChannel ||
                bulkSapPriceListId == null
              }
              onClick={openBulkPreview}
            >
              {autoT('Vista Previa')}
            </Button>
            <Button
              icon="upload"
              disabled={!canStartBulkUpload || bulkUploading || bulkSapPartnerLoading}
              onClick={startBulkUpload}
              style={{
                backgroundColor:
                  canStartBulkUpload && !bulkUploading && !bulkSapPartnerLoading ? '#2F5A88' : '#ccc',
                color: '#fff',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              {bulkSapPartnerLoading
                ? autoT('Consultando cliente…')
                : bulkUploading
                  ? autoT('Enviando...')
                  : autoT('Iniciar Carga')}
            </Button>
          </FlexBox>
        </div>
      </Dialog>

      {/* Vista previa: tabla resumida por cliente/tienda + paginación de grupos (ver `BULK_PREVIEW_GROUPS_PAGE_SIZE`). */}
      <Dialog
        open={isBulkPreviewOpen}
        headerText=""
        style={{ width: 'min(1280px, 98vw)', maxHeight: '92vh' }}
        onAfterClose={closeBulkPreview}
      >
        <div style={{ padding: '0.75rem 1rem 1rem', maxHeight: '85vh', overflow: 'auto' }}>
          {bulkFileParsing || bulkUploading || bulkSapPartnerLoading ? (
            <div
              role="status"
              aria-live="polite"
              aria-busy="true"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 8,
                marginBottom: '1rem',
                padding: '1rem 1.25rem',
                borderRadius: 12,
                boxShadow: '0 4px 18px rgba(0, 0, 0, 0.12)',
                border: '2px solid',
                ...(bulkUploading
                  ? {
                      background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                      borderColor: '#2e7d32'
                    }
                  : bulkSapPartnerLoading
                    ? {
                        background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
                        borderColor: '#f9a825'
                      }
                    : {
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        borderColor: '#1565c0'
                      })
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}
              >
                <BusyIndicator
                  active
                  size="M"
                  text={
                    bulkUploading
                      ? autoT('Subiendo archivo…')
                      : bulkSapPartnerLoading
                        ? autoT('Consultando cliente…')
                        : autoT('Leyendo Excel…')
                  }
                  style={{ flexShrink: 0 }}
                />
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <Title
                    level="H5"
                    style={{
                      margin: 0,
                      lineHeight: 1.3,
                      color: bulkUploading ? '#1b5e20' : bulkSapPartnerLoading ? '#e65100' : '#0d47a1'
                    }}
                  >
                    {bulkUploading && wholesaleImportPollingMsg
                      ? autoT('Creando órdenes de venta en el servidor')
                      : bulkUploading
                        ? autoT('Subiendo el Excel a la plataforma')
                        : bulkSapPartnerLoading
                          ? autoT('Consultando el cliente en SAP B1')
                          : autoT('Leyendo y procesando el Excel')}
                  </Title>
                  <Text
                    style={{
                      display: 'block',
                      marginTop: '0.5rem',
                      color: bulkUploading ? '#33691e' : bulkSapPartnerLoading ? '#4e342e' : '#37474f',
                      fontSize: '0.95rem',
                      lineHeight: 1.45
                    }}
                  >
                    {bulkUploading && wholesaleImportPollingMsg
                      ? wholesaleImportPollingMsg
                      : bulkUploading
                        ? autoT(
                            'El archivo se está enviando al servidor. No cierre esta ventana ni abandone la página hasta que termine.'
                          )
                        : bulkSapPartnerLoading
                          ? autoT('Comprobando el RUT del cliente en SAP B1. Espere un momento.')
                        : autoT(
                            'Se está analizando el archivo en su equipo. En hojas muy grandes puede tardar un poco.'
                          )}
                  </Text>
                </div>
              </div>
            </div>
          ) : null}
          {bulkUploadSuccessMessage ? (
            <MessageStrip design="Positive" style={{ marginBottom: '0.75rem' }}>
              {bulkUploadSuccessMessage}
            </MessageStrip>
          ) : null}
          {bulkSapItemsError ? (
            <MessageStrip design="Negative" style={{ marginBottom: '0.75rem' }}>
              {bulkSapItemsError}
            </MessageStrip>
          ) : null}
          {!bulkSessionWarehouse.code ? (
            <MessageStrip design="Negative" style={{ marginBottom: '0.75rem' }}>
              {autoT(
                'No hay código de bodega (whsCode) en su sesión. El stock SAP de la vista previa no se puede asociar a una bodega. Contacte al administrador o vuelva a iniciar sesión.'
              )}
            </MessageStrip>
          ) : null}
          {bulkSapItemsLoading ? (
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 6,
                marginBottom: '1rem',
                padding: '1rem 1.25rem',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                border: '2px solid #f57c00',
                borderRadius: 12,
                boxShadow: '0 4px 18px rgba(245, 124, 0, 0.28)'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}
              >
                <BusyIndicator
                  active
                  size="M"
                  text={autoT('Cargando listas de precio y stock desde SAP…')}
                  style={{ flexShrink: 0 }}
                />
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <Title level="H5" style={{ margin: 0, color: '#e65100', lineHeight: 1.3 }}>
                    {autoT('Consultando listas de precio y stock en SAP')}
                  </Title>
                  <Text
                    style={{
                      display: 'block',
                      marginTop: '0.5rem',
                      color: '#4e342e',
                      fontSize: '0.95rem',
                      lineHeight: 1.45
                    }}
                  >
                    {autoT(
                      'Se están pidiendo a SAP el precio de la lista configurada para el formato del archivo y el stock por cada código de ítem. Con muchos productos distintos puede tardar varios minutos; no cierre esta ventana hasta que termine.'
                    )}
                  </Text>
                  {bulkSapItemsProgress.total > 0 ? (
                    <Text
                      style={{
                        display: 'block',
                        marginTop: '0.5rem',
                        color: '#bf360c',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                    >
                      {autoT('{{done}} / {{total}} códigos de ítem', {
                        done: bulkSapItemsProgress.done,
                        total: bulkSapItemsProgress.total
                      })}
                    </Text>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          <Button design="Transparent" icon="nav-back" onClick={closeBulkPreview} style={{ marginBottom: '0.75rem' }}>
            {autoT('Volver a Carga Mayorista')}
          </Button>

          <div
            style={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              borderRadius: 10,
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}
          >
            <Icon name="show" style={{ color: '#1565c0', fontSize: '1.5rem' }} />
            <div>
              <Title level="H4" style={{ margin: 0, color: '#0d47a1' }}>
                {autoT('Vista previa del archivo')}
              </Title>
              <Text style={{ color: '#37474f' }}>
                {autoT('Revise los datos antes de crear las órdenes de ventas')}
              </Text>
            </div>
          </div>

          <Text style={{ color: '#6c757d', marginBottom: '0.35rem', display: 'block' }}>
            {bulkSelectedFileName}
          </Text>
          <Text style={{ color: '#37474f', marginBottom: '0.25rem', display: 'block', fontWeight: 600 }}>
            {autoT('Formato detectado')}:{` ${getBulkFormatLabelKey(bulkPreviewMeta.profileId)}`}
          </Text>
          <Text style={{ color: '#1565c0', marginBottom: '0.5rem', display: 'block', fontWeight: 600, fontSize: '0.95rem' }}>
            {autoT('Moneda')}: {autoT(bulkCurrencyInfo.labelKey)}
          </Text>
          {bulkSheetGrids[bulkActiveSheetIndex]?.name ? (
            <Text style={{ color: '#546e7a', marginBottom: '0.75rem', display: 'block', fontSize: '0.9rem' }}>
              {autoT('Hoja utilizada')}: {bulkSheetGrids[bulkActiveSheetIndex].name}
            </Text>
          ) : null}
          {bulkPeruCentralWarehousePreview ? (
            <MessageStrip
              design="Information"
              hideCloseButton
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: '1rem' }}
            >
              {autoT(
                'Se creará 1 orden de venta a la bodega central ({{agrupador}}). El destino por tienda se informa en cada línea ({{destinoLinea}}).',
                {
                  agrupador: getBulkGroupingColumnLabel(bulkPreviewMeta.profileId, autoT),
                  destinoLinea: bulkLineDestinationColumnLabel || autoT('destino por línea')
                }
              )}
            </MessageStrip>
          ) : null}

          <Title level="H5" style={{ margin: '0 0 0.75rem' }}>
            {autoT('Vista previa validada')}
          </Title>

          {!bulkSapValidationReady ? (
            <MessageStrip
              design="Information"
              hideCloseButton
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: '1rem' }}
            >
              {autoT(
                'Seleccione un canal de venta válido y espere la validación del cliente en SAP para comparar precios y stock.'
              )}
            </MessageStrip>
          ) : null}

          {/* Recuadros resumen (conteos según reglas de ejemplo) */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
              <div style={{ padding: '1rem', borderLeft: '4px solid #2e7d32' }}>
                <Text style={{ color: '#2e7d32', fontWeight: 600 }}>{autoT('Líneas OK')}</Text>
                <Title level="H2" style={{ margin: '0.25rem 0 0', color: '#1b5e20' }}>
                  {validationCounts.ok}
                </Title>
              </div>
            </Card>
            <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
              <div style={{ padding: '1rem', borderLeft: '4px solid #c62828' }}>
                <Text style={{ color: '#c62828', fontWeight: 600 }}>{autoT('Errores (bloquean)')}</Text>
                <Title level="H2" style={{ margin: '0.25rem 0 0', color: '#b71c1c' }}>
                  {validationCounts.error}
                </Title>
              </div>
            </Card>
            <Card style={{ flex: '1 1 200px', minWidth: 200 }}>
              <div style={{ padding: '1rem', borderLeft: '4px solid #f9a825' }}>
                <Text style={{ color: '#f57f17', fontWeight: 600 }}>{autoT('Advertencias')}</Text>
                <Title level="H2" style={{ margin: '0.25rem 0 0', color: '#e65100' }}>
                  {validationCounts.warning}
                </Title>
              </div>
            </Card>
          </div>

          {bulkStockAllocationNotice ? (
            <MessageStrip
              design="Critical"
              onClose={() => setBulkStockAllocationNotice('')}
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: '1rem' }}
            >
              {bulkStockAllocationNotice}
            </MessageStrip>
          ) : null}

          <div
            style={{
              overflow: 'auto',
              maxHeight: 'min(420px, 50vh)',
              border: '1px solid #e0e0e0',
              borderRadius: 8
            }}
          >
            <table
              style={{
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                fontSize: '0.85rem'
              }}
            >
              <colgroup>
                <col style={{ width: '3.5%' }} />
                <col style={{ width: isBulkMultimarcaExcelFormat ? '20%' : '24%' }} />
                {isBulkMultimarcaExcelFormat ? <col style={{ width: '12%' }} /> : null}
                <col style={{ width: isBulkMultimarcaExcelFormat ? '9%' : '11%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: isBulkMultimarcaExcelFormat ? '8%' : '10%' }} />
                <col style={{ width: isBulkMultimarcaExcelFormat ? '28%' : '32%' }} />
                <col style={{ width: '9.5%' }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#f5f6f7', position: 'sticky', top: 0, zIndex: 1 }}>
                  {(() => {
                    const bulkGroupHeaders = [
                      '#',
                      getBulkGroupingColumnLabel(bulkPreviewMeta.profileId, autoT),
                      ...(isBulkMultimarcaExcelFormat ? [autoT('RUT', {})] : []),
                      autoT('N° OC'),
                      isIxComercioProfileId(bulkPreviewMeta.profileId)
                        ? autoT('Fecha entrega')
                        : autoT('Fecha OC'),
                      autoT('Líneas de detalle'),
                      autoT('Validación del grupo'),
                      autoT('Detalle')
                    ];
                    const lineasIdx = bulkGroupHeaders.indexOf(autoT('Líneas de detalle'));
                    const validacionIdx = bulkGroupHeaders.indexOf(autoT('Validación del grupo'));
                    const detalleIdx = bulkGroupHeaders.length - 1;
                    return bulkGroupHeaders.map((h, colIdx) => {
                      const headerWrap = colIdx === lineasIdx || colIdx === validacionIdx;
                      return (
                        <th
                          key={colIdx}
                          style={{
                            textAlign:
                              colIdx === lineasIdx ? 'center' : colIdx === detalleIdx ? 'center' : 'left',
                            padding: '10px 8px',
                            borderBottom: '1px solid #e0e0e0',
                            fontWeight: 600,
                            whiteSpace: headerWrap ? 'normal' : 'nowrap',
                            lineHeight: headerWrap ? 1.25 : 1.2,
                            verticalAlign: 'bottom',
                            wordBreak: headerWrap ? 'break-word' : undefined,
                            overflowWrap: headerWrap ? 'break-word' : undefined
                          }}
                        >
                          {h}
                        </th>
                      );
                    });
                  })()}
                </tr>
              </thead>
              <tbody>
                {bulkPreviewGroupedPageSlice.map((grp, idx) => {
                  const bg =
                    grp.validationStatus === 'error'
                      ? '#fff5f5'
                      : grp.validationStatus === 'warning'
                        ? '#fffbf0'
                        : '#f6fff8';
                  const globalIdx = (bulkPreviewGroupPage - 1) * BULK_PREVIEW_GROUPS_PAGE_SIZE + idx + 1;
                  const clientDisplay =
                    grp.clientLabel && String(grp.clientLabel).trim()
                      ? grp.clientLabel
                      : autoT('Sin tienda destino (agrupado)');
                  const isExpanded = expandedBulkGroupId === grp.id;
                  const detailPanelId = `bulk-preview-detail-${String(grp.id).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                  const sortedDetails = [...(grp.detailRows || [])].sort(
                    (a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0)
                  );
                  return (
                    <Fragment key={grp.id}>
                      <tr style={{ background: bg }}>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{globalIdx}</td>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere'
                          }}
                        >
                          {clientDisplay}
                        </td>
                        {isBulkMultimarcaExcelFormat ? (
                          <td
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #eee',
                              whiteSpace: 'nowrap',
                              fontVariantNumeric: 'tabular-nums'
                            }}
                          >
                            {bulkMultimarcaExcelRut || '—'}
                          </td>
                        ) : null}
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{grp.nOc || '—'}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>
                          {grp.fechaOc || '—'}
                        </td>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            textAlign: 'center',
                            fontWeight: 700,
                            color: '#1565c0'
                          }}
                        >
                          {grp.detailCount}
                        </td>
                        <td
                          style={{
                            padding: '4px 6px',
                            borderBottom: '1px solid #eee',
                            whiteSpace: 'normal',
                            verticalAlign: 'middle',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere'
                          }}
                        >
                          {grp.validationStatus === 'ok' ? (
                            pill(grp.validationMessage, '#e8f5e9', '#2e7d32', true)
                          ) : grp.validationStatus === 'warning' ? (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                columnGap: 6,
                                rowGap: 2,
                                maxWidth: '100%'
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#e65100',
                                  lineHeight: 1.2,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {grp.warningLineCount} {autoT('de')} {grp.detailCount}{' '}
                                {autoT('líneas con advertencia')}
                              </span>
                              {pill(grp.validationMessage, '#fff8e1', '#f57f17', true)}
                            </div>
                          ) : (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                columnGap: 6,
                                rowGap: 2,
                                maxWidth: '100%'
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#c62828',
                                  lineHeight: 1.2,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {grp.errorLineCount} {autoT('de')} {grp.detailCount}{' '}
                                {autoT('líneas con error')}
                                {grp.warningLineCount > 0 ? (
                                  <>
                                    {' · '}
                                    {grp.warningLineCount} {autoT('con advertencia')}
                                  </>
                                ) : null}
                              </span>
                              {pill(grp.validationMessage, '#ffebee', '#c62828', true)}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '4px',
                            borderBottom: '1px solid #eee',
                            textAlign: 'center',
                            verticalAlign: 'middle'
                          }}
                        >
                          <Button
                            design="Transparent"
                            icon={isExpanded ? 'navigation-down-arrow' : 'navigation-right-arrow'}
                            onClick={() =>
                              setExpandedBulkGroupId((id) => (id === grp.id ? null : grp.id))
                            }
                            aria-expanded={isExpanded}
                            aria-controls={detailPanelId}
                            tooltip={isExpanded ? autoT('Ocultar líneas') : autoT('Ver líneas')}
                            accessibleName={
                              isExpanded ? autoT('Ocultar líneas de detalle') : autoT('Ver líneas de detalle')
                            }
                          />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={isBulkMultimarcaExcelFormat ? 8 : 7}
                            style={{ padding: 0, borderBottom: '1px solid #eee', background: '#fafbfc' }}
                          >
                            <div
                              id={detailPanelId}
                              role="region"
                              aria-label={autoT('Líneas del Excel para esta tienda')}
                              style={{
                                maxHeight: 'min(320px, 42vh)',
                                overflow: 'auto',
                                padding: '10px 12px'
                              }}
                            >
                              <table
                                style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}
                              >
                                <thead>
                                  <tr style={{ background: '#eceff1' }}>
                                    <th style={bulkPreviewNestedThStyle}>#</th>
                                    <th style={bulkPreviewNestedThStyle}>
                                      {isIxComercioProfileId(bulkPreviewMeta.profileId) ||
                                      bulkPreviewMeta.profileId === MULTIMARCA_PROFILE_ID
                                        ? autoT('Número de artículo')
                                        : autoT('pc13')}
                                    </th>
                                    <th style={bulkPreviewNestedThStyle}>{autoT('Descripción')}</th>
                                    {bulkLineDestinationColumnLabel ? (
                                      <th style={bulkPreviewNestedThStyle}>
                                        {bulkLineDestinationColumnLabel}
                                      </th>
                                    ) : null}
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Cant. OC')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Precio acordado')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Total línea')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Precio SAP')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Descuento')}
                                    </th>
                                    <th
                                      style={{
                                        ...bulkPreviewNestedThStyle,
                                        textAlign: 'right',
                                        lineHeight: 1.2
                                      }}
                                    >
                                      {autoT('Precio SAP')}
                                      <br />
                                      {autoT('con Descuento')}
                                    </th>
                                    <th style={{ ...bulkPreviewNestedThStyle, textAlign: 'right' }}>
                                      {autoT('Stock SAP')}
                                    </th>
                                    <th style={bulkPreviewNestedThStyle}>{autoT('Validación')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortedDetails.map((r) => (
                                    <tr key={r.id || r.rowIndex} style={{ background: '#fff' }}>
                                      <td style={bulkPreviewNestedTdStyle}>{r.rowIndex}</td>
                                      <td style={bulkPreviewNestedTdStyle}>{r.skuIac || '—'}</td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, maxWidth: 200 }}>
                                        {r.descripcion || '—'}
                                      </td>
                                      {bulkLineDestinationColumnLabel ? (
                                        <td style={{ ...bulkPreviewNestedTdStyle, maxWidth: 160 }}>
                                          {r.sucursalDestinoLinea || '—'}
                                        </td>
                                      ) : null}
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {r.cantOc != null ? r.cantOc : '—'}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {formatBulkMoney(r.precioAcordado)}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right', fontWeight: 600 }}>
                                        {formatBulkMoney(r.totalLinea)}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {formatBulkMoney(r.precioListaSap)}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {r.precioListaSap != null
                                          ? `${r.brandDiscountPercent ?? 0}%`
                                          : '—'}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right', fontWeight: 600 }}>
                                        {formatBulkMoney(r.precioListaSapConDescuento)}
                                      </td>
                                      <td style={{ ...bulkPreviewNestedTdStyle, textAlign: 'right' }}>
                                        {r.stockDispSap != null ? r.stockDispSap : '—'}
                                      </td>
                                      <td
                                        style={{
                                          ...bulkPreviewNestedTdStyle,
                                          whiteSpace: 'normal',
                                          maxWidth: 220,
                                          fontSize: '0.72rem'
                                        }}
                                      >
                                        {r.validationStatus === 'ok'
                                          ? pill(r.validationMessage, '#e8f5e9', '#2e7d32', true)
                                          : r.validationStatus === 'warning'
                                            ? pill(r.validationMessage, '#fff8e1', '#f57f17', true)
                                            : pill(r.validationMessage, '#ffebee', '#c62828', true)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginTop: '0.75rem',
              marginBottom: '0.25rem'
            }}
          >
            <Button
              design="Transparent"
              disabled={bulkPreviewGroupPage <= 1}
              onClick={() => setBulkPreviewGroupPage((p) => Math.max(1, p - 1))}
            >
              ← {autoT('Anterior')}
            </Button>
            <Text style={{ color: '#37474f', fontWeight: 600 }}>
              {autoT('Página')} {bulkPreviewGroupPage} / {bulkPreviewGroupTotalPages}
              <span style={{ fontWeight: 400, color: '#78909c', marginLeft: 8 }}>
                ({bulkPreviewGroupedByClient.length}{' '}
                {bulkPeruCentralWarehousePreview ? autoT('OV previstas') : autoT('Destinos')})
              </span>
            </Text>
            <Button
              design="Transparent"
              disabled={bulkPreviewGroupPage >= bulkPreviewGroupTotalPages}
              onClick={() => setBulkPreviewGroupPage((p) => Math.min(bulkPreviewGroupTotalPages, p + 1))}
            >
              {autoT('Siguiente')} →
            </Button>
          </div>

          {renderBulkUploadErrorAboveActions()}

          <FlexBox
            justifyContent={FlexBoxJustifyContent.SpaceBetween}
            style={{ gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <Button
              design="Negative"
              icon="decline"
              onClick={cancelBulkFlow}
              disabled={bulkUploading || bulkSapPartnerLoading || bulkSapItemsLoading}
            >
              {autoT('Cancelar Carga')}
            </Button>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                design="Negative"
                icon="download"
                disabled={!hasBulkValidationExportRows || bulkSapItemsLoading}
                onClick={() => setBulkExportDialogOpen(true)}
              >
                {autoT('Exportar validación')}
              </Button>
              <Button
                design="Transparent"
                onClick={closeBulkPreview}
                disabled={bulkUploading || bulkSapPartnerLoading}
              >
                {autoT('Cerrar Vista Previa')}
              </Button>
              <Button
                icon="upload"
                disabled={!canStartBulkUpload || bulkUploading || bulkSapPartnerLoading || bulkSapItemsLoading}
                onClick={() => startBulkUpload()}
                title={
                  !canStartBulkUpload && !bulkUploading && !bulkSapPartnerLoading && !bulkSapItemsLoading
                    ? autoT(
                        'Complete parámetros de cabecera, asegure bodega en su usuario (sesión), cargue un archivo sin errores de línea y vuelva a intentar'
                      )
                    : undefined
                }
                style={{
                  backgroundColor:
                    canStartBulkUpload && !bulkUploading && !bulkSapPartnerLoading && !bulkSapItemsLoading
                      ? '#2e7d32'
                      : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px'
                }}
              >
                {bulkSapItemsLoading
                  ? autoT('Consultando SAP…')
                  : bulkSapPartnerLoading
                    ? autoT('Consultando cliente…')
                    : bulkUploading
                      ? autoT('Enviando...')
                      : `${autoT('Crear Pedidos')} (${bulkPreviewRows.length})`}
              </Button>
            </div>
          </FlexBox>
        </div>
      </Dialog>

      {/* Búsqueda: filtra la página actual en cliente; API de búsqueda pendiente. */}
      <div
        style={{
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <Title level="H4" style={{ marginBottom: '1rem' }}>{autoT('Búsqueda avanzada')}</Title>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: '1', minWidth: '220px' }}>
            <Label>{autoT('Número de orden')}</Label>
            <Input
              placeholder={autoT('Buscar por número')}
              value={filters.orderNumber}
              onChange={(e) => setFilters((prev) => ({ ...prev, orderNumber: e.target.value }))}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ flex: '1', minWidth: '220px' }}>
            <Label>{autoT('Estado')}</Label>
            <Select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.detail.selectedOption.value }))}
              style={{ width: '100%' }}
            >
              <Option value="">{autoT('Todos los estados')}</Option>
              <Option value="Pending">{autoT('Creada')}</Option>
              <Option value="Accepted">{autoT('Aceptada')}</Option>
              <Option value="Canceled">{autoT('Cancelada')}</Option>
              <Option value="Shipped">{autoT('Despachada')}</Option>
              <Option value="PartiallyShipped">{autoT('Despacho Parcial')}</Option>
              <Option value="Closed">{autoT('Cerrado')}</Option>
              <Option value="Returned">{autoT('Devuelta')}</Option>
            </Select>
          </div>

          <div style={{ flex: '1', minWidth: '220px' }}>
            <Label>{autoT('Canal')} (channel)</Label>
            <Select
              value={filters.channel}
              onChange={(e) => setFilters((prev) => ({ ...prev, channel: e.detail.selectedOption.value }))}
              disabled={filterSaleChannelsLoading}
              style={{ width: '100%' }}
            >
              <Option value="">{autoT('Todos')}</Option>
              {filterSaleChannels.length > 0
                ? filterSaleChannels.map((ch) => {
                    const name = String(ch.name || '').trim();
                    if (!name) return null;
                    return (
                      <Option key={String(ch.id ?? name)} value={name}>
                        {name}
                      </Option>
                    );
                  })
                : [
                    <Option key="mayorista" value="mayorista">
                      {autoT('Mayorista')}
                    </Option>,
                    <Option key="retail" value="retail">
                      {autoT('Retail')}
                    </Option>,
                    <Option key="multimarca" value="multimarca">
                      {autoT('Multimarca')}
                    </Option>,
                    <Option key="ixcomercio" value="ixcomercio">
                      IxComercio
                    </Option>,
                    <Option key="b2cdirecto" value="b2cdirecto">
                      B2C Directo
                    </Option>
                  ]}
            </Select>
          </div>

          <div style={{ flex: '1', minWidth: '220px' }}>
            <Label>{autoT('Periodo')}</Label>
            <Select
              value={filters.datePreset || 'all'}
              onChange={(e) => {
                const datePreset = e.detail.selectedOption.value;
                setFilters((prev) => ({
                  ...prev,
                  datePreset,
                  ...(datePreset !== 'custom' ? { createdAt: '', createdAtEnd: '' } : {})
                }));
              }}
              style={{ width: '100%' }}
            >
              <Option value="all">{autoT('Todas las fechas')}</Option>
              <Option value="last7">{autoT('Últimos 7 días')}</Option>
              <Option value="last15">{autoT('Últimos 15 días')}</Option>
              <Option value="last1m">{autoT('Último mes')}</Option>
              <Option value="last3m">{autoT('Últimos 3 meses')}</Option>
              <Option value="last6m">{autoT('Últimos 6 meses')}</Option>
              <Option value="custom">{autoT('Búsqueda avanzada')}</Option>
            </Select>
          </div>

          {filters.datePreset === 'custom' ? (
            <Fragment>
              <div style={{ flex: '1', minWidth: '220px' }}>
                <Label>{autoT('Fecha desde')}</Label>
                <DatePicker
                  value={filters.createdAt}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      createdAt: e.detail.value
                    }))
                  }
                  style={{ width: '100%' }}
                  placeholder="dd/mm/aaaa"
                />
              </div>

              <div style={{ flex: '1', minWidth: '220px' }}>
                <Label>{autoT('Fecha hasta')}</Label>
                <DatePicker
                  value={filters.createdAtEnd}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      createdAtEnd: e.detail.value
                    }))
                  }
                  style={{ width: '100%' }}
                  placeholder="dd/mm/aaaa"
                />
              </div>
            </Fragment>
          ) : null}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button design="Emphasized" icon="search" onClick={handleApplySearch}>
            {autoT('Buscar')}
          </Button>
          <Button design="Transparent" onClick={handleClearListFilters}>
            {autoT('Limpiar filtros')}
          </Button>
        </div>
      </div>

      {searchModeActive ? (
        <div
          style={{
            backgroundColor: '#e6f7ff',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}
        >
          <Text>
            <Icon name="filter" style={{ marginRight: '0.5rem' }} />
            {autoT('Resultados de búsqueda')} ({orders.length} {autoT('en esta página')})
          </Text>
          <Button design="Transparent" onClick={handleClearListFilters} style={{ padding: '0.25rem' }}>
            <Icon name="decline" />
          </Button>
        </div>
      ) : null}

      {listError ? (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          {listError}
        </MessageStrip>
      ) : null}

      {/* Éxito de import mayorista tras cerrar el modal (mensaje con N° OV creadas si el API lo permite). */}
      {wholesaleImportBanner ? (
        <div
          style={{
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}
        >
          <MessageStrip design="Positive" style={{ flex: '1 1 280px', minWidth: 0 }}>
            {wholesaleImportBanner}
          </MessageStrip>
          <Button design="Transparent" onClick={() => setWholesaleImportBanner('')}>
            {autoT('Cerrar')}
          </Button>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.65rem',
          marginBottom: '0.75rem'
        }}
      >
        <Button
          design="Emphasized"
          icon="download"
          onClick={handleExportSalesOrdersExcel}
          disabled={!searchModeActive || listLoading}
        >
          {autoT('Exportar Excel')}
        </Button>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.65rem',
            flex: '1 1 auto',
            minWidth: 0
          }}
        >
          <Text style={{ fontSize: '0.8125rem', color: '#6c757d' }}>
            {autoT('Mostrando')}{' '}
            <strong style={{ color: '#37474f' }}>
              {salesListRange.start === 0 && salesListRange.end === 0
                ? '0'
                : `${salesListRange.start}–${salesListRange.end}`}
            </strong>
            {salesListRange.total != null && Number.isFinite(salesListRange.total) ? (
              <>
                {' '}
                {autoT('de')} {salesListRange.total}
              </>
            ) : null}
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
            <Label style={{ margin: 0 }}>{autoT('Registros por página')}</Label>
            <Select
              value={String(pageSize)}
              onChange={handleSalesListPageSizeChange}
              style={{ minWidth: '5.5rem' }}
              disabled={listLoading}
            >
              {SALES_LIST_PAGE_SIZES.map((n) => (
                <Option key={n} value={String(n)}>
                  {String(n)}
                </Option>
              ))}
            </Select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Button
              design="Transparent"
              disabled={currentPage <= 1 || listLoading}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              {autoT('Anterior')}
            </Button>
            <Text style={{ fontSize: '0.8125rem', color: '#37474f', minWidth: '7rem', textAlign: 'center' }}>
              {autoT('Página')} {currentPage}
              {salesListTotalPagesLabel != null ? ` / ${salesListTotalPagesLabel}` : ''}
            </Text>
            <Button design="Transparent" disabled={isNextPageDisabled} onClick={() => setCurrentPage((p) => p + 1)}>
              {autoT('Siguiente')}
            </Button>
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          marginBottom: '0.5rem',
          minHeight: listLoading ? 200 : undefined,
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto'
        }}
      >
        {listLoading ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.65)'
            }}
          >
            <BusyIndicator active size="M" text={autoT('Cargando órdenes...')} />
          </div>
        ) : null}
        <table
          style={{
            width: '100%',
            maxWidth: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
            fontSize: '0.875rem',
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: 4,
            minWidth: 0
          }}
        >
          <colgroup>
            <col style={{ width: '2.5%' }} />
            <col style={{ width: '7%' }} />
            {/* DocNum estrecha; col explícita — antes había un col menos que columnas y Estado se comprimía */}
            <col style={{ width: '3.5%', maxWidth: '4.25rem' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '9.5%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7.5%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '4.5%' }} />
            <col style={{ width: '12.5%' }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f5f6f7' }}>
              <th
                style={{
                  padding: '8px 4px',
                  borderBottom: '1px solid #e5e5e5',
                  fontWeight: 600,
                  textAlign: 'center',
                  width: '2.5%',
                  verticalAlign: 'bottom'
                }}
              >
                <input
                  ref={salesListSelectAllHeaderRef}
                  type="checkbox"
                  checked={allVisibleOrdersSelected}
                  onChange={toggleSelectAllVisibleOrders}
                  disabled={!pageOrderKeysEligible.length}
                  aria-label={autoT('Seleccionar todas las órdenes visibles en esta página', {})}
                  title={
                    pageOrderKeysEligible.length
                      ? autoT(
                          'Seleccionar en esta página órdenes creadas (PENDING) o enviadas a SAP (ACCEPTED); canceladas no',
                          {}
                        )
                      : autoT('No hay órdenes creadas ni enviadas a SAP seleccionables en esta página', {})
                  }
                  style={{
                    margin: 0,
                    cursor: pageOrderKeysEligible.length ? 'pointer' : 'not-allowed'
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
              {[
                autoT('N° Orden'),
                autoT('DocNum'),
                autoT('Fecha OC', {}),
                autoT('Canal'),
                autoT('Marketplace'),
                autoT('Sucursal Destino'),
                autoT('RUT', {}),
                autoT('Marca'),
                autoT('Total de líneas', {}),
                autoT('Cant. Unidades', {}),
                autoT('Total'),
                autoT('Moneda'),
                autoT('Estado')
              ].map((label, i) => {
                /** Sucursal a la izquierda; Total a la derecha; el resto centrado. */
                const align =
                  i === 5 ? 'left' : i === 10 ? 'right' : 'center';
                const thPad =
                  i === 1 ? '6px 3px' : i === 6 ? '8px 2px' : '8px 4px';
                const isDocNumCol = i === 1;
                return (
                  <th
                    key={`${label}-${i}`}
                    style={{
                      padding: thPad,
                      borderBottom: '1px solid #e5e5e5',
                      fontWeight: 600,
                      textAlign: align,
                      whiteSpace: isDocNumCol ? 'nowrap' : 'normal',
                      wordBreak: isDocNumCol ? 'normal' : 'break-word',
                      lineHeight: 1.2,
                      fontSize: isDocNumCol ? '0.68rem' : '0.72rem',
                      verticalAlign: 'bottom',
                      minWidth: 0,
                      maxWidth: isDocNumCol ? '4.25rem' : undefined,
                      width: isDocNumCol ? '3.5%' : undefined,
                      boxSizing: 'border-box'
                    }}
                  >
                    {label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td
                  colSpan={14}
                  style={{
                    padding: '2rem 1rem',
                    textAlign: 'center',
                    color: '#6c757d',
                    borderBottom: '1px solid #e5e5e5'
                  }}
                >
                  <Text>{autoT('Sin órdenes en esta página.')}</Text>
                </td>
              </tr>
            ) : (
              orders.map((row, idx) => {
                const rowKey = String(row.id ?? row.orderNumber ?? '').trim() || `ov-${idx}`;
                const isRowSelected = rowKey && selectedOrderIds.includes(rowKey);
                const canSelectRow =
                  rowKey &&
                  !rowKey.startsWith('ov-') &&
                  isSalesOrderStatusSelectableInList(row.status);
                return (
                  <tr
                    key={row.id != null ? String(row.id) : `ov-${idx}-${row.orderNumber ?? ''}`}
                    onClick={() => navigateToSalesOrderDetail(row)}
                    style={{
                      cursor: 'pointer',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa'
                    }}
                  >
                    <td
                      style={{
                        padding: '8px 4px',
                        borderBottom: '1px solid #e5e5e5',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        width: '2.5%'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={!!isRowSelected}
                        onChange={() => toggleOrderRowSelected(row)}
                        disabled={!canSelectRow}
                        aria-label={autoT('Seleccionar orden', {})}
                        title={
                          canSelectRow
                            ? autoT(
                                'Seleccionar para enviar a SAP (solo PENDING) o cancelar (PENDING o ACCEPTED)',
                                {}
                              )
                            : autoT(
                                'Solo órdenes creadas (PENDING) o enviadas a SAP (ACCEPTED) se pueden marcar; canceladas no',
                                {}
                              )
                        }
                        style={{
                          margin: 0,
                          cursor: canSelectRow ? 'pointer' : 'not-allowed'
                        }}
                      />
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}
                    >
                      <span
                        role="link"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToSalesOrderDetail(row);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            navigateToSalesOrderDetail(row);
                          }
                        }}
                        style={{
                          color: '#1a73e8',
                          fontWeight: 600,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                        title={row.orderNumber != null ? String(row.orderNumber) : undefined}
                      >
                        {row.orderNumber}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '8px 4px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        maxWidth: '4.25rem',
                        width: '3.5%',
                        fontSize: '0.8125rem',
                        fontVariantNumeric: 'tabular-nums',
                        boxSizing: 'border-box'
                      }}
                      title={
                        formatSalesOrderListDocNum(row) !== '-'
                          ? formatSalesOrderListDocNum(row)
                          : undefined
                      }
                    >
                      <Text>{formatSalesOrderListDocNum(row)}</Text>
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}
                    >
                      <Text>{formatSalesOrderListDate(row.purchaseOrderDate)}</Text>
                    </td>
                    <td
                      style={{
                        padding: '8px 18px 8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        minWidth: 0,
                        borderRight: '1px solid #eceff1'
                      }}
                    >
                      <span style={{ display: 'inline-flex', justifyContent: 'center' }}>
                        {pill(row.channel, '#e8f5e9', '#2e7d32')}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '8px 6px 8px 18px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        overflow: 'hidden',
                        minWidth: 0
                      }}
                    >
                      <span style={{ display: 'inline-flex', justifyContent: 'center' }}>
                        {pill(row.marketplaceLabel, '#eceff1', '#37474f')}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}
                      title={(() => {
                        const dest = formatSalesOrderListDestination(row);
                        return dest !== '—' ? dest : undefined;
                      })()}
                    >
                      <Text>{formatSalesOrderListDestination(row)}</Text>
                    </td>
                    <td
                      style={{
                        padding: '6px 4px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        minWidth: 0,
                        lineHeight: 1.25,
                        fontSize: '0.8125rem',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      {row.clientRut}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}
                      title={row.brand != null && String(row.brand).trim() ? String(row.brand) : undefined}
                    >
                      <Text>{row.brand != null && String(row.brand).trim() ? String(row.brand) : '—'}</Text>
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 0
                      }}
                    >
                      {formatSalesOrderListCount(row.productLineCount)}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 0
                      }}
                    >
                      {formatSalesOrderListCount(row.productUnitsTotal)}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        textAlign: 'right',
                        verticalAlign: 'middle',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}
                    >
                      {formatSalesOrderListMoney(row.total, row.currency)}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {row.currency}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        borderBottom: '1px solid #e5e5e5',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        overflow: 'hidden',
                        minWidth: 0
                      }}
                    >
                      <span style={{ display: 'inline-flex', justifyContent: 'center', width: '100%' }}>
                        {statusPill(row)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pantallas de carga bloqueantes: Dialog UI5 para quedar encima de los modales abiertos. */}
      <BlockingLoadingDialog
        open={bulkUploading || bulkFileParsing}
        title={
          bulkUploading && wholesaleImportPollingMsg
            ? autoT('Creando órdenes de venta en el servidor…')
            : bulkUploading
              ? autoT('Procesando y subiendo el archivo…')
              : autoT('Leyendo y procesando el Excel…')
        }
        message={
          bulkUploading && wholesaleImportPollingMsg
            ? wholesaleImportPollingMsg
            : bulkUploading
              ? autoT('No cierre esta ventana ni abandone la página hasta que finalice el envío.')
              : autoT('Espere mientras se analiza el archivo. No cierre esta ventana.')
        }
      />
      {/* «Preparando…» solo hasta que abre la vista previa SAP (ahí la carga de ítems tiene su propio aviso). */}
      <BlockingLoadingDialog
        open={sapProcessLoading || (sapChileProcessSelectionLoading && !isSapChileProcessPreviewOpen)}
        title={
          sapProcessLoading
            ? autoT('Enviando órdenes de venta a SAP…')
            : autoT('Preparando la vista previa para SAP…')
        }
        message={
          sapProcessLoading
            ? sapSendProgressMsg ||
              autoT('No cierre esta ventana ni abandone la página hasta que finalice el envío.')
            : autoT('Consultando las órdenes seleccionadas. Espere un momento.')
        }
      />
    </>
  );
};

export default SalesOrderList;
